import {
  Fragment, useCallback, useEffect, useRef, useState,
  type FormEvent, type KeyboardEvent, type ReactNode,
} from 'react'
import {
  api, ApiError, can, cardDelivery, prettyPhone,
  type Certification, type Member, type MemberCertification,
  type MemberCertificationRecord, type MemberDocument, type MemberGroup,
  type MemberPhone, type MemberRecord,
} from '../lib/api'
import {
  ColumnMenu, calendarDate, calendarDay, dash, day, useColumns, useDialogGuard,
  useInertWhile, useReturnFocus, type Column,
} from '../lib/sheet'
import { readValue, writeValue } from '../lib/prefs'
import {
  AddressFields, BLANK_ADDRESS, addressBody, addressFrom, type Address,
} from './AddressFields'
import { ImportMembers } from './ImportMembers'
import { PhoneInput } from './PhoneInput'
import { MemberGroupsDialog } from './MemberGroups'

/** The roster — `PLAN.html` section 04.
 *
 *  REGISTER UP FRONT, DO NOT WAIT FOR CALLS. The broadcast fires at whoever is
 *  registered, so an unregistered member is simply not reached. That is the
 *  reason this screen exists at all, and the reason its empty state says what
 *  it says.
 *
 *  THE UNIT NUMBER IS THE IDENTITY. The customer, on the roster he actually
 *  keeps: "a member is recognized by his unit number. All other info is just
 *  for the records inside the CRM." Half of his twenty-four rows are a unit
 *  with nobody filled in yet, and those are real members — the unit answers on
 *  the air whoever is holding it. So `display_name` is nullable, the server
 *  computes `label` (the name, or the unit when there is no name), and every
 *  cell here reads that. Nothing in this file renders `display_name` as a
 *  member's identity: it would be a blank row somebody has to click to
 *  identify.
 *
 *  CHANNELS ARE THE POINT OF THE ROSTER, so they are a column and they are on
 *  by default. Text, email, the card and the radio are not a fallback chain:
 *  every channel a member has fires at once. A member with none is registered
 *  and unreachable, which is why the server refuses it.
 *
 *  The phone book's sheet, on the phone book's shared machinery
 *  (`lib/sheet.tsx`) rather than a copy of it.
 */

/** The scalar fields both dialogs edit.
 *
 *  `notify_sms` IS DELIBERATELY ABSENT AND MUST STAY ABSENT. It is derived by
 *  `members.resync` on the server from the per-number ticks, and
 *  `MemberPatch` will not accept it. Putting it back here would give the
 *  roster a second writer for something that already has exactly one, and the
 *  two would contradict each other the first time Text was on and every number
 *  was unticked — a member who looks reachable on every screen and is in no
 *  broadcast. */
const BLANK = {
  display_name: '',
  callsign: '',
  email: '',
  radio_imei: '',
  vehicle_plate: '',
  has_radio: false,
  notify_email: false,
  notify_card: false,
}

/** UNIQUE PER SHEET, and this is the argument that matters most in this file.
 *  `prefs.writeSet` is unfiltered, so sharing the phone book's key would mean
 *  each screen reading the other's list, filtering it down to the handful of
 *  keys both catalogues happen to share, and then overwriting the other's
 *  choice on the first checkbox touched. */
const COLUMN_PREF = 'members.columns'

/** HOW MANY of this member's numbers could not be read as phone numbers.
 *
 *  THE ONE FILTER, and `hasBadNumber` below is built on it rather than beside
 *  it. The record's Broadcast tab wants the COUNT for its badge and
 *  everything else wants the YES OR NO, and writing the second one out again —
 *  `member.phones.some((p) => !p.readable)`, with the predicate in scope — is
 *  exactly the drift this file has already paid for once.
 *
 *  `readable` is `app/phones.py`'s own answer carried back per number — never
 *  re-derived in the browser, which is the phone-book bug arriving somewhere
 *  it would be even harder to see. */
function badNumbers(m: Member): number {
  return m.phones.filter((p) => !p.readable).length
}

/** Whether any number on this member could not be read as a phone number.
 *
 *  ONE PREDICATE, and everything that asks about an unreadable number asks it:
 *  the footer's count, `stranded` below, the record's Numbers block and the
 *  amber on its Broadcast tab. */
function hasBadNumber(m: Member): boolean {
  return badNumbers(m) > 0
}

/** The fields that CHANGED, and only those.
 *
 *  THE MEMBER PATCH IS FIELD-BY-FIELD AND THE CALLER PATCH IS NOT.
 *  `update_member` reads its body with `exclude_unset`, so an unsent field is
 *  left exactly as it was and a field sent empty CLEARS its column — where
 *  `PATCH /api/contacts/{id}` reads the presence of ANY address part as
 *  "rewrite the whole address". So nothing here sends a block wholesale:
 *  sending all eight address parts every time would rewrite eight columns
 *  because somebody fixed a callsign.
 *
 *  ONE DIFFER FOR BOTH BLOCKS. The address and the personal half are the same
 *  question asked twice — which of these boxes is not what arrived — and a
 *  second copy of this loop is where the two would come to disagree about
 *  whether a box somebody emptied counts as a change. */
function changed(
  now: Record<string, string | null>, before: Record<string, string | null>,
): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  for (const field of Object.keys(now)) {
    if (now[field] !== before[field]) out[field] = now[field]
  }
  return out
}

/** BUILT ON `addressBody` RATHER THAN BESIDE IT, so "trimmed, and empty means
 *  null" keeps one definition. A second trimmer here is how the two screens
 *  would come to disagree about whether a space is an address. */
function addressChanges(
  now: Address, before: Address,
): Record<string, string | null> {
  return changed(addressBody(now), addressBody(before))
}

/** WHEN HE WAS BORN AND WHO TO RING IF SOMETHING HAPPENS TO HIM — the half of
 *  the record that is about the person rather than about reaching him.
 *
 *  IT IS NOT ON THE ROSTER ROW AND MUST NOT BE. `GET /api/members` is
 *  deliberately ungated, so these four ride on `GET /api/members/{id}` behind
 *  `settings.members` along with the mailing address, for the reason the server
 *  gives at length: a date of birth beside a home address is identity-theft
 *  material in one payload, handed to every dispatcher's browser on every
 *  shift, drawn by nothing.
 *
 *  `emergency_contact_phone_e164` IS NOT HERE AND IS ON NO REQUEST MODEL. The
 *  server derives it from the raw beside it and a client that sends it is
 *  ignored — two columns that must always agree and two places writing them is
 *  the phone-book bug, and the failure here is a record showing one number and
 *  holding another. */
type Personal = {
  date_of_birth: string
  emergency_contact_name: string
  emergency_contact_relationship: string
  emergency_contact_phone: string
}

const NO_PERSONAL: Personal = {
  date_of_birth: '',
  emergency_contact_name: '',
  emergency_contact_relationship: '',
  emergency_contact_phone: '',
}

function personalFrom(source: MemberRecord | null): Personal {
  if (!source) return { ...NO_PERSONAL }
  return {
    date_of_birth: source.date_of_birth ?? '',
    emergency_contact_name: source.emergency_contact_name ?? '',
    emergency_contact_relationship: source.emergency_contact_relationship ?? '',
    emergency_contact_phone: source.emergency_contact_phone ?? '',
  }
}

/** Trimmed, and empty means null — `addressBody`'s rule, applied to the same
 *  kind of nullable free-text column. */
function personalBody(p: Personal): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  for (const [k, v] of Object.entries(p)) out[k] = v.trim() || null
  return out
}

function personalChanges(
  now: Personal, before: Personal,
): Record<string, string | null> {
  return changed(personalBody(now), personalBody(before))
}

/** A byte count as somebody reads it. Display only — `byte_size` is the
 *  server's own count of the plaintext and nothing here recomputes it. */
function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Whether this member LOOKS reachable by text and is not.
 *
 *  DELIBERATELY NARROWER THAN `hasBadNumber`, and a second function rather
 *  than a second spelling of the first, because the two answer different
 *  questions and the footer and the record used to differ silently on which
 *  one they were asking. `hasBadNumber` is "there is something to correct" —
 *  a typo on a second number is still a typo somebody has to see, so the
 *  footer counts it. This one is the alarm: a number written down, nothing
 *  ticked, so no broadcast reaches him that way at all. The Channels cell and
 *  the record's notice both ask THIS, so a member who opens with the notice
 *  cannot have rendered as a plain "no text" on the sheet. */
function stranded(m: Member): boolean {
  return !m.notify_sms && hasBadNumber(m)
}

/** Four booleans in one cell, as letters that are either there or struck out.
 *
 *  NOT COLOUR ALONE, and not a tick. A colour-coded dot says nothing to
 *  somebody who cannot tell the colours apart, and four ticks in a row cannot
 *  say WHICH four. T, E, R and C are the words the rest of the product uses —
 *  text, email, radio, card — and an unset one is an en dash, so the shape of
 *  the cell is readable before any of the letters are.
 *
 *  THE RADIO IS IN HERE BECAUSE IT IS A CHANNEL. Fifteen of the twenty-four
 *  rows on the customer's roster have a unit and a handset and no phone and no
 *  email; `_reachable` on the server counts the radio for exactly that reason.
 *  Left out, most of a real roster would render as three dashes — the shape
 *  this column uses to mean "registered and unreachable" — which is the
 *  opposite of the truth about the people you call on the air. R sits next to
 *  C rather than after it because the card is delivered to the handset: the
 *  server refuses C without R, and adjacent letters make that pair legible.
 *
 *  THE T IS THE SERVER'S DERIVED ANSWER AND IS NOT RECOMPUTED HERE.
 *  `notify_sms` is true exactly when some number is ticked, and the database
 *  refuses a tick on a number it could not read — so a true T means, by
 *  construction, that a readable ticked number exists. `phones` is read only
 *  to explain the amber.
 *
 *  THE AMBER STATE IS THE USEFUL ONE, and it has moved. It used to mean "Text
 *  is on and the number does not parse", which can no longer happen. What can
 *  happen now is worse to miss: a member with a number written down and NO
 *  text reaching him, because the only number on file could not be read. He
 *  looks contactable — there is a number right there in the Phone column — and
 *  he is not.
 */
function Channels({ m }: { m: Member }) {
  const amber = stranded(m)
  return (
    <span className="chan">
      <span
        className={m.notify_sms ? '' : amber ? 'bad' : 'off'}
        title={amber
          ? 'There is a number on this member that could not be read as a '
            + 'phone number, so nothing here is ticked for text and a '
            + 'broadcast cannot reach them that way.'
          : m.notify_sms ? 'A number is ticked for text' : 'No text'}
      >
        {m.notify_sms ? 'T' : '–'}
      </span>
      <span className={m.notify_email ? '' : 'off'} title={m.notify_email ? 'Email' : 'No email'}>
        {m.notify_email ? 'E' : '–'}
      </span>
      <span className={m.has_radio ? '' : 'off'} title={m.has_radio ? 'Carries a radio' : 'No radio'}>
        {m.has_radio ? 'R' : '–'}
      </span>
      <span
        className={m.notify_card && cardDelivery() ? '' : 'off'}
        title={!cardDelivery() ? 'The radio card is not available yet'
          : m.notify_card ? 'Radio card, delivered to the handset' : 'No card'}
      >
        {m.notify_card && cardDelivery() ? 'C' : '–'}
      </span>
    </span>
  )
}

/** The names of one kind of group on a member, joined for a cell: his
 *  category, or the areas he covers. The server already orders them. */
function kindNames(m: Member, kind: MemberGroup['kind']): string {
  return m.groups.filter((g) => g.kind === kind).map((g) => g.name).join(', ')
}

function catalogue(onDuty: (m: Member) => void, mayEdit: boolean): Column<Member>[] {
  return [
    {
      // THE UNIT LEADS, because that is what he is recognised by, and it is
      // fixed rather than an optional extra column for the same reason. In
      // mono, so a column of unit numbers lines up and can be scanned.
      //
      // ITS OWN COLUMN, WITH THE NAME BESIDE IT. The two were one cell until
      // the customer read his imported roster (17 Sep 2026): "one column
      // should be the member code, the next column should be his name...
      // it will be more clear for the eye." Both are fixed, and both headers
      // are buttons that sort the sheet -- see `sortedRoster`.
      key: 'unit', header: 'Unit', fixed: true, className: 'nm unit',
      sortKey: (m) => (m.callsign || '').trim() || null,
      cell: (m) => (
        m.callsign
          ? <span className={m.active ? 'mono' : 'mono gone'}>{m.callsign}</span>
          : dash(null)
      ),
    },
    {
      // The name is `label` and never `display_name` — the server decides what
      // to call somebody so that no screen invents its own fallback. The
      // equality test is not a second fallback: it only stops the unit being
      // printed twice on one row when `label` IS the unit, which is what it
      // resolves to for the half of the roster with nobody filled in. Those
      // rows show a dash here, as an empty phone does.
      key: 'name', header: 'Name', fixed: true, className: 'nm',
      // A unit with nobody filled in has no name to sort on: it goes to the
      // bottom, in unit order, under the A-to-Z of the people who do.
      sortKey: (m) => (m.label === m.callsign ? null : m.label),
      cell: (m) => {
        const named = m.label === m.callsign ? null : m.label
        return named
          ? <span className={m.active ? '' : 'gone'}>{named}</span>
          : dash(null)
      },
    },
    {
      // FIXED, AND NOT IN THE ACTIONS CELL. That cell is hidden until hover on
      // a pointer device, and the shift change is the reason somebody opens
      // this screen — it cannot be the thing they have to hunt for.
      key: 'duty', header: 'Duty', fixed: true, className: 'duty',
      sortKey: (m) => (m.on_duty && m.active ? 0 : 1),
      cell: (m) => (
        <button
          className={m.on_duty ? 'ghost tiny nogrow ok' : 'ghost tiny nogrow'}
          type="button"
          disabled={!mayEdit || !m.active}
          onClick={(e) => { e.stopPropagation(); onDuty(m) }}
          title={m.active ? 'Change duty' : 'Retired members are never on duty'}
        >
          {m.on_duty && m.active ? 'On' : 'Off'}
        </button>
      ),
    },
    {
      key: 'phone', header: 'Phone', className: 'num',
      // Everyone with a number first, in number order; nobody-has-one last.
      sortKey: (m) => (m.phone ? m.phone.replace(/\D/g, '') || m.phone : null),
      cell: (m) => {
        if (!m.phone) return <span className="muted">—</span>
        // `prettyPhone` is display only and decides nothing. What a number IS
        // is app/phones.py on the server, and `phone_readable` is that
        // module's own answer about the PRIMARY carried back rather than
        // re-derived here. The rest ride on it as `+N`, like the phone book's:
        // a row per number would repeat the member down the page, which is the
        // one thing a dispatcher is scanning for.
        return (
          <span className={m.phone_readable === false ? 'unreadable' : ''}
                title={m.phone_readable === false
                  ? 'This could not be read as a phone number.' : undefined}>
            {prettyPhone(m.phone) || m.phone}
            {m.phones.length > 1 && <span className="lbl"> +{m.phones.length - 1}</span>}
          </span>
        )
      },
    },
    {
      key: 'channels', header: 'Channels', className: 'chancell',
      cell: (m) => <Channels m={m} />,
      // MOST WAYS TO REACH HIM FIRST. Negated, so the first press shows the
      // best-connected members at the top; no way at all is empty and sits at
      // the bottom. The same four letters the cell draws.
      sortKey: (m) => {
        const ways = [m.notify_sms, m.notify_email, m.has_radio,
                      m.notify_card && cardDelivery()].filter(Boolean).length
        return ways ? -ways : null
      },
    },
    {
      key: 'email', header: 'Email', className: 'clip', cell: (m) => dash(m.email),
      sortKey: (m) => (m.email || '').trim().toLowerCase() || null,
    },
    {
      // A WORD, NOT A TICK, for the same reason the Channels cell uses letters:
      // a mark in a column is only readable if you already know what the
      // column asks. It is also the word the customer's own sheet uses — his
      // last column is "Has Radio?" and its values are YES and NO.
      key: 'radio', header: 'Radio',
      sortKey: (m) => (m.has_radio ? 0 : 1),
      cell: (m) => (m.has_radio
        ? <span>Yes</span>
        : <span className="muted">No</span>),
    },
    {
      // HIS CATEGORY AND HIS AREAS, IN TWO COLUMNS. They were one "Groups"
      // cell until the customer asked to sort on each (17 Sep 2026): "I click
      // on groups, I see who's the coordinator, who not. I click on areas, I
      // see who's in which area right away." A category is what he is, and he
      // holds at most one; areas are where he covers, as many as apply. Names
      // only: the `kind` is what makes the group list readable and it belongs
      // on that screen, not repeated on every row here.
      key: 'group', header: 'Group', className: 'clip',
      cell: (m) => dash(kindNames(m, 'category')),
      sortKey: (m) => kindNames(m, 'category') || null,
    },
    {
      key: 'areas', header: 'Areas', className: 'clip',
      cell: (m) => dash(kindNames(m, 'area')),
      sortKey: (m) => kindNames(m, 'area') || null,
    },
    {
      // WHAT HE IS TRAINED TO DO. It rides on the ungated roster listing on
      // purpose — who is qualified is exactly what the dispatcher's responder
      // picker is for — so this column costs no extra request.
      //
      // A LAPSED CARD LEADS, AS A WORD AND A COUNT, and the colour only
      // reinforces it. A coloured dot says nothing to somebody who cannot tell
      // the colours apart, and the whole reason to track training is that an
      // expired card which still reads as current is a liability rather than a
      // record. The same call the Channels cell makes with its letters.
      //
      // `expired` IS THE SERVER'S, never a comparison made here.
      // `EXPIRED_SQL` decides it against today in the ORGANIZATION's timezone;
      // a browser west of it comparing against its own clock would lapse
      // somebody from seven the evening before, and the record and the roster
      // would disagree about whether a card is valid.
      key: 'certifications', header: 'Certifications', className: 'clip',
      sortKey: (m) => m.certifications.map((c) => c.name).join(', ') || null,
      cell: (m) => {
        if (m.certifications.length === 0) return dash(null)
        const lapsed = m.certifications.filter((c) => c.expired).length
        return (
          <span title={m.certifications
            .map((c) => (c.expired ? `${c.name} — expired` : c.name))
            .join(', ')}>
            {lapsed > 0 && <span className="lapsed">{lapsed} expired · </span>}
            {m.certifications.map((c) => c.name).join(', ')}
          </span>
        )
      },
    },
    {
      key: 'vehicle_plate', header: 'Plate', className: 'num',
      cell: (m) => dash(m.vehicle_plate),
      sortKey: (m) => (m.vehicle_plate || '').trim() || null,
    },
    {
      // "Handset id", not "Radio". There are two radio facts on a member now —
      // whether he carries one, and which one — and two columns both called
      // Radio in the picker is a choice nobody can make.
      key: 'radio_imei', header: 'Handset id', className: 'num',
      sortKey: (m) => (m.radio_imei || '').trim() || null,
      cell: (m) => dash(m.radio_imei),
    },
    {
      // A bare DATE, so `calendarDay` and not `day`: `new Date("1985-03-04")`
      // is UTC midnight, which in Montreal renders as the day before.
      key: 'joined_on', header: 'Joined', className: 'when',
      // NEWEST FIRST on the first press, so negated. A bare date parses as
      // UTC midnight, which is fine for ordering: every row is read the same
      // way. A date the browser cannot read counts as none.
      sortKey: (m) => {
        const t = m.joined_on ? Date.parse(m.joined_on) : NaN
        return Number.isNaN(t) ? null : -t
      },
      cell: (m) => calendarDay(m.joined_on),
    },
    {
      key: 'created_at', header: 'Added', className: 'when',
      cell: (m) => day(m.created_at),
      sortKey: (m) => {
        const t = Date.parse(m.created_at)
        return Number.isNaN(t) ? null : -t
      },
    },
  ]
}

/** Seven: the five his own sheet keeps, the channels, and his areas now that
 *  they are their own column beside his category. The count is in the
 *  picker's reset label as well ("Back to the usual seven"), so the two are
 *  read together and a miscount here is one somebody acts on.
 *
 *  `callsign` used to be one of these and is gone from the catalogue entirely
 *  — the unit is the fixed identity column now — so a stored preference naming
 *  it is dropped by `prefs.readSet`, which costs nobody a column: it is on
 *  every row either way.
 *
 *  THIS LIST IS ALSO WHAT REACHES A BROWSER THAT ALREADY HAS A PREFERENCE.
 *  Radio, Groups and Plate arrived after the column picker did, and a stored
 *  list cannot name a column that did not exist — so `prefs.readSet` switches
 *  on a fallback column the preference was never offered, and only that.
 *  Anything added to the catalogue and NOT to this list stays off until it is
 *  ticked, which is the promise the picker makes. */
const DEFAULT_COLUMNS = [
  'phone', 'channels', 'email', 'radio', 'group', 'areas', 'vehicle_plate',
]

/** Which column the roster is sorted on, and which way. Remembered per
 *  browser like the column choice: a matter of taste, not of record.
 *
 *  EVERY HEADER IS A SORT BUTTON (the customer, 17 Sep 2026: "all the columns
 *  should be sortable... first I see everybody with a yes, and then I see
 *  everybody with a no"). Each column says what it sorts on through
 *  `Column.sortKey`, and the first press gives the order he described: yes
 *  before no, has-one before has-none, A to Z, newest first. A second press
 *  turns it round. THE UNIT IS THE DEFAULT ORDER: it is what a member is
 *  recognised by. */
const SORT_PREF = 'members.sort'
type SortDir = 'asc' | 'desc'

/** Strings as a person reads them -- case does not matter and MT-9 comes
 *  before MT-20 -- and numbers as numbers. Never mixed: a column's `sortKey`
 *  hands back one kind. */
function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/** Unit order, which every other sort falls back to among equals, so a column
 *  of "Yes" rows still reads down the roster in counting order. */
function byUnit(a: Member, b: Member): number {
  return compareValues(a.callsign || '', b.callsign || '')
    || compareValues(a.label, b.label)
}

/** The roster in the chosen order. Sorted here and not on the server: the
 *  rows arrive whole, and a sort route would be a second definition of "the
 *  roster" for a choice that lives in one browser.
 *
 *  A ROW WITH NOTHING IN THE COLUMN SITS AT THE BOTTOM, whichever way the
 *  column is turned: turning Phone round puts the numbers in the other order,
 *  it does not lift the people with no number to the top. */
function sortedRoster(
  rows: Member[], column: Column<Member> | undefined, dir: SortDir,
): Member[] {
  const key = column?.sortKey
  if (!key) return [...rows].sort(byUnit)
  const keyed = rows.map((m) => ({ m, value: key(m) }))
  const sign = dir === 'asc' ? 1 : -1
  const has = keyed.filter(
    (k): k is { m: Member; value: string | number } => k.value !== null)
  const none = keyed.filter((k) => k.value === null)
  has.sort((a, b) => sign * compareValues(a.value, b.value) || byUnit(a.m, b.m))
  none.sort((a, b) => byUnit(a.m, b.m))
  return [...has, ...none].map((k) => k.m)
}

export function Members() {
  const [members, setMembers] = useState<Member[]>([])
  const [groups, setGroups] = useState<MemberGroup[]>([])
  const [loaded, setLoaded] = useState(false)
  const [q, setQ] = useState('')
  const [section, setSection] = useState<'active' | 'retired'>('active')
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const [managingGroups, setManagingGroups] = useState(false)
  const [record, setRecord] = useState<Member | null>(null)
  //: WHICH TAB THE NEXT RECORD OPENS ON, when something asked for one. Cleared
  //: with the record, so opening the next member from a row gets the default
  //: rather than wherever the last caller wanted to go.
  const [recordOpensOn, setRecordOpensOn] = useState<TabKey | undefined>(undefined)
  /** Why the record below was opened, when it was opened by finishing a
   *  registration rather than by clicking a row. Cleared with the record it
   *  belongs to — see `closeRecord`. */
  const [arrival, setArrival] = useState<Arrival | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  //: WHO THE BANNER IS ABOUT, when it is about somebody. Only the registration
  //: sets it, and only so the banner can offer that member's record -- see the
  //: comment where it is drawn.
  const [noteMember, setNoteMember] = useState<Member | null>(null)

  const mayEdit = can('settings.members')

  /** EVERYBODY, ALWAYS, retired included. The two sections below each print
   *  their own count, and a count you can only see by switching to it is not a
   *  count — it is a second click before anybody can answer "how many have we
   *  got". The roster is tens to a few hundred people, so this costs one query
   *  and saves a reload every time the sections are swapped. */
  const load = useCallback(async () => {
    try {
      setMembers(await api.members(true))
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the roster.')
    } finally {
      setLoaded(true)
    }
  }, [])

  /** UNGATED TO READ, like the nature-of-call list. A dispatcher who cannot
   *  see which areas exist can only send to everybody. A failure here costs
   *  the group ticks and must not cost the roster, so it does not touch
   *  `error`. */
  const loadGroups = useCallback(async () => {
    try {
      setGroups(await api.memberGroups())
    } catch {
      setGroups([])
    }
  }, [])

  useEffect(() => { load(); loadGroups() }, [load, loadGroups])

  /** One member changed. Patched in place rather than reloading, so a row does
   *  not move out from under the pointer between the press and the release —
   *  which is what reloading after every duty toggle used to do.
   *
   *  It also feeds the open record: `setRecord` is what the dialog renders
   *  from, so a number added or removed inside it comes back through here and
   *  the list redraws from the whole member the server returned. */
  function absorb(updated: Member) {
    setRecord((open) => (open && open.id === updated.id ? updated : open))
    setMembers((prev) => prev.map((m) => (m.id === updated.id ? updated : m)))
  }

  /** ONE WAY OUT OF THE RECORD, so the sentence that explained why it opened
   *  cannot survive it. `RecordDialog` seeds its own state from `arrival`, and
   *  it is keyed by member id — so leaving this behind would put a message
   *  about a registration onto the next record that happened to be the same
   *  member. */
  function closeRecord() {
    setRecord(null)
    setRecordOpensOn(undefined)
    setArrival(null)
  }

  async function toggleDuty(m: Member) {
    try {
      absorb(await api.memberUpdate(m.id, { on_duty: !m.on_duty }))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not change duty.')
    }
  }

  const CATALOGUE = catalogue(toggleDuty, mayEdit)
  const { shown, visible, choose } = useColumns(
    COLUMN_PREF, CATALOGUE, DEFAULT_COLUMNS)

  const [sort, setSort] = useState<{ key: string; dir: SortDir }>(() => {
    // A stored choice naming a column that no longer exists -- "groups", now
    // Group and Areas -- falls back to the unit, as the column picker's does.
    const allowed = CATALOGUE.flatMap((c) => [`${c.key}:asc`, `${c.key}:desc`])
    const [key, dir] = readValue(SORT_PREF, allowed, 'unit:asc').split(':')
    return { key, dir: dir as SortDir }
  })
  /** Press the header the sheet is sorted on to turn it round; press any other
   *  to sort on it, in its first-press order. */
  function sortOn(key: string) {
    const dir: SortDir = sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc'
    setSort({ key, dir })
    writeValue(SORT_PREF, `${key}:${dir}`)
  }

  /** TWO SECTIONS, ACTIVE AND NOT — the customer's own words: "I should have
   *  two sections. One active and non active." It used to be a "Show retired"
   *  checkbox, which is a different thing: a tickbox says "and also show me
   *  these", so retired people appeared mixed in among the roster rather than
   *  filed apart, and how many there were of either could not be read at all.
   */
  const active = members.filter((m) => m.active)
  const retired = members.filter((m) => !m.active)
  const pool = section === 'active' ? active : retired

  /** FILTERED IN THE BROWSER, over rows already loaded. The roster is tens to
   *  a few hundred people and arrives whole, so a search route would be a
   *  second definition of "matching a member" beside `app/search.py` for no
   *  gain.
   *
   *  A FRAGMENT MATCHER, NEVER AN IDENTITY ONE. The digits comparison can say
   *  a number CONTAINS these digits; it can never say two numbers are the
   *  same, and it does not try — deciding that is `app/phones.py`, on the
   *  server, and the global Search is where a number is looked up properly.
   *
   *  EVERY NUMBER HE ANSWERS ON, not just the primary. Somebody searching for
   *  a member by the number that rang them has no idea which of his numbers is
   *  filed as the main one.
   */
  const term = q.trim().toLowerCase()
  const termDigits = term.replace(/\D/g, '')
  const matched = !term ? pool : pool.filter((m) => {
    if (m.label.toLowerCase().includes(term)) return true
    if ((m.callsign || '').toLowerCase().includes(term)) return true
    if ((m.email || '').toLowerCase().includes(term)) return true
    if ((m.vehicle_plate || '').toLowerCase().includes(term)) return true
    if (m.groups.some((g) => g.name.toLowerCase().includes(term))) return true
    if (termDigits.length >= 3 && m.phones.some(
      (p) => p.phone.replace(/\D/g, '').includes(termDigits))) return true
    return false
  })
  // ON A COLUMN THAT IS SHOWN. A choice naming a hidden column would order
  // the roster by something nobody can see; the unit order stands in until
  // that column is switched back on, and the stored choice survives.
  const sortColumn = shown.find((c) => c.key === sort.key)
  const rows = sortedRoster(matched, sortColumn, sortColumn ? sort.dir : 'asc')

  const onDuty = active.filter((m) => m.on_duty).length
  const bad = active.filter(hasBadNumber).length

  const dialogOpen = adding || importing || managingGroups || record !== null
  const main = useRef<HTMLDivElement>(null)
  useInertWhile(main, dialogOpen)

  return (
    <div className="sheetpage">
      {error && (
        <div className="notice" role="alert" onClick={() => setError(null)}>{error}</div>
      )}
      {note && (
        <div className="notice ok" role="status">
          <span onClick={() => { setNote(null); setNoteMember(null) }}>{note}</span>
          {/* THE CARD THAT WAS NOT IN THE ROOM, offered where the registration
              ended.

              THE FORM CAN FILE ONE NOW, so this is no longer the only way in:
              `NewDocuments` holds the chosen file in the browser and uploads it
              the moment the member exists, which is what the customer asked for
              — "it's missing the section of uploading files". What this button
              answers is the other case, the common one on a roster being typed
              up: the volunteer's identity card is handed over afterwards, and
              finding his row again among a hundred others is how it never gets
              filed.

              AN OFFER RATHER THAN A JUMP. Opening his record automatically was
              tried and was wrong in the other direction: for an administrator
              working down a roster it is a modal to dismiss on every single
              row. One click while the card is in his hand; nothing to dismiss
              when it is not. */}
          {noteMember && can('settings.member_documents') && (
            <button
              className="ghost tiny"
              type="button"
              onClick={() => {
                setRecord(noteMember)
                setRecordOpensOn('documents')
                setNote(null)
                setNoteMember(null)
              }}
            >
              File an identity document
            </button>
          )}
        </div>
      )}

      <div className="sheet-main" ref={main}>
        <div className="sheetbar">
          <h2>Members</h2>
          {/* Each carries its own count, so both are readable without
              switching to either — and NEITHER PRINTS ONE UNTIL THE ROSTER HAS
              ARRIVED. `members` starts empty, so both tabs read "· 0" over a
              table saying "Loading…", which is a confident answer to the one
              question this screen exists to answer, and the answer is "nobody
              is registered". An ellipsis says the number is coming; a zero
              does not. */}
          <button
            className={section === 'active' ? 'tab on' : 'tab'}
            type="button"
            aria-pressed={section === 'active'}
            onClick={() => setSection('active')}
          >
            Active · {loaded ? active.length : '…'}
          </button>
          <button
            className={section === 'retired' ? 'tab on' : 'tab'}
            type="button"
            aria-pressed={section === 'retired'}
            onClick={() => setSection('retired')}
          >
            Retired · {loaded ? retired.length : '…'}
          </button>
          <input
            className="search"
            value={q}
            placeholder="Unit, name, number, area…"
            aria-label="Filter the roster"
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="spacer" />
          <ColumnMenu
            catalogue={CATALOGUE}
            visible={visible}
            fallback={DEFAULT_COLUMNS}
            resetLabel="Back to the usual seven"
            onChange={choose}
          />
          {/* UNGATED, LIKE THE TAB ITSELF. `GET /api/member-groups` is
              deliberately open, for the same reason `GET /api/members` is: a
              dispatcher who cannot see which areas exist can only send a
              broadcast to everybody. `MemberGroupsDialog` decides its own
              controls from `settings.members` and opens read-only without it —
              so gating the button here made every one of those read-only paths
              unreachable, and left a role that may legitimately READ the list
              with no way in at all. Import and Add stay gated: every control on
              those two writes, and a button that answers 403 every time is
              worse than no button. */}
          <button
            className="ghost tiny nogrow"
            type="button"
            onClick={() => setManagingGroups(true)}
          >
            Groups
          </button>
          {mayEdit && (
            <button
              className="ghost tiny nogrow"
              type="button"
              onClick={() => setImporting(true)}
            >
              Import a file
            </button>
          )}
          {mayEdit && (
            <button className="primary compact" onClick={() => setAdding(true)}>
              + Add member
            </button>
          )}
        </div>

        <div className="sheetwrap">
          <table className="sheet">
            <thead>
              <tr>
                {shown.map((c) => {
                  // EVERY HEADER WITH A `sortKey` IS A BUTTON -- today that is
                  // all of them. A column without one is a plain label.
                  if (!c.sortKey) {
                    return <th key={c.key}>{c.header}</th>
                  }
                  const on = sort.key === c.key
                  return (
                    <th
                      key={c.key}
                      aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <button
                        type="button"
                        className={on ? 'sorth on' : 'sorth'}
                        title={on
                          ? `Sorted by ${c.header.toLowerCase()} — press to turn round`
                          : `Sort by ${c.header.toLowerCase()}`}
                        onClick={() => sortOn(c.key)}
                      >
                        {c.header}
                        <span className="arrow" aria-hidden="true">
                          {on ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                        </span>
                      </button>
                    </th>
                  )
                })}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={shown.length + 1} className="sheetempty">
                    {!loaded
                      ? 'Loading…'
                      : q
                        ? 'Nobody in this section matches that.'
                        : section === 'retired'
                          ? 'Nobody has been retired.'
                          : 'Nobody registered yet. A broadcast right now would reach no one.'}
                  </td>
                </tr>
              ) : (
                rows.map((m) => (
                  <Row
                    key={m.id}
                    member={m}
                    columns={shown}
                    onOpen={() => setRecord(m)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="sheetfoot">
          {/* The same rule as the tabs: three zeroes here while the roster is
              still on the wire is a sentence saying the organization has
              nobody. */}
          <span>
            {!loaded ? 'Reading the roster…' : (
              <>
                {active.length} active, {retired.length} retired, {onDuty} on duty
                {q && ` · ${rows.length} matching`}
              </>
            )}
          </span>
          <span className="spacer" />
          {/* THE ONE FIGURE WORTH INTERRUPTING FOR. A number that could not be
              read is a typo on somebody's spreadsheet, and it is kept rather
              than refused precisely so that it can be seen and corrected —
              which only happens if something says it is there. Otherwise it is
              found during an emergency, as a delivery marked failed. */}
          {bad > 0 && (
            <span className="unreadable">
              {bad} {bad === 1 ? 'member has' : 'members have'} a number that
              could not be read — no broadcast can text it
            </span>
          )}
        </div>
      </div>

      {importing && (
        <ImportMembers
          onClose={() => setImporting(false)}
          onImported={(message) => { setNote(message); load() }}
        />
      )}

      {managingGroups && (
        <MemberGroupsDialog
          onClose={() => setManagingGroups(false)}
          // A rename or a retirement changes what every member's row and every
          // set of ticks says, so both lists are read again rather than
          // patched from a guess about what the dialog did.
          onChanged={() => { loadGroups(); load() }}
        />
      )}

      {adding && (
        <AddDialog
          groups={groups}
          onClose={() => setAdding(false)}
          onAdded={(made, unattached) => {
            setAdding(false)
            setSection('active')
            setNote(`${made.label} registered. A broadcast reaches them from now on.`)
            setNoteMember(made)
            load()

            // WHAT DID NOT GO ON HIM, and it is the only reason to carry on
            // into the record rather than leaving him on the sheet. A card that
            // would not attach and a scan that would not upload are both
            // retried there, and the sentence naming what happened has to be
            // where the retry is.
            //
            // A REGISTRATION WITH NOTHING LEFT TO DO CLOSES AS IT ALWAYS DID.
            // A dialog that opens with nothing waiting in it is a dialog to
            // dismiss, and somebody registering a roster one row at a time
            // would dismiss one every time.
            const trouble: string[] = []
            for (const u of unattached) {
              trouble.push(`${u.name} was not recorded against him: ${u.why}`)
            }
            // THE SERVER'S OWN VERDICT, not a guess: `readable` is
            // `app/phones.py`'s answer per number, carried back on the member
            // it just created. A number it could not read went on unticked, so
            // he is registered and no broadcast can text it — and this is the
            // one moment somebody is looking and able to correct it.
            if (hasBadNumber(made)) {
              trouble.push(
                'A number on this record could not be read as a phone number, '
                + 'so no broadcast can text it. Press Edit on it and correct '
                + 'it, or give it its country code.')
            }
            // NOTHING WAITING MEANS NOTHING OPENS. The condition once read
            // `trouble.length === 0 && !filing`, where `filing` was only
            // `can('settings.member_documents')` with no per-member term --
            // so for the one person most likely to hold that key, the
            // administrator typing up the roster, EVERY registration opened a
            // modal about an identity document, for twenty-four members whose
            // documents are not in the room. That is precisely the outcome the
            // comment claims this prevents.
            //
            // Filing a document afterwards is offered on the SUCCESS BANNER
            // instead, as an action he can take or ignore.
            if (trouble.length === 0) return

            // A FILE THAT WOULD NOT UPLOAD IS RETRIED ON ITS OWN TAB, so the
            // record opens there. It is the one kind of trouble whose retry
            // lives somewhere the record does not open by itself, and the
            // upload bar is at the bottom of that panel -- landing on Personal
            // information with a sentence about a passport scan is an
            // instruction with a hunt attached. A certification that did not
            // attach is on that same tab, so `what` only has to separate it
            // from everything else.
            // ANYTHING THAT DID NOT ATTACH OPENS THAT TAB, not documents
            // alone. Certifications and documents SHARE tab four now -- the
            // customer merged them -- so a card that was refused was
            // reported by name, with the server's own sentence, and then
            // the record opened on Personal information, which draws no
            // certification control at all. The sentence is most worth
            // acting on while the dates that produced it are still in his
            // head, and it arrived with a hunt attached.
            if (unattached.length > 0) {
              setRecordOpensOn('documents')
            }
            setArrival({
              wrong: true,
              message: `${made.label} is registered. ${trouble.join(' ')}`,
            })
            setRecord(made)
          }}
        />
      )}

      {record && (
        <RecordDialog
          key={record.id}
          member={record}
          groups={groups}
          mayEdit={mayEdit}
          arrival={arrival}
          openOn={recordOpensOn}
          onClose={closeRecord}
          onSaved={(updated) => { absorb(updated); closeRecord() }}
          onMember={absorb}
          onRetired={(updated) => {
            absorb(updated)
            closeRecord()
            setNote(updated.active
              ? `${updated.label} is back on the roster.`
              : `${updated.label} is retired. No broadcast reaches them now.`)
            load()
          }}
        />
      )}
    </div>
  )
}

function Row({
  member, columns, onOpen,
}: {
  member: Member
  columns: Column<Member>[]
  onOpen: () => void
}) {
  return (
    <tr
      tabIndex={0}
      className={member.active ? '' : 'retiredrow'}
      onClick={() => { if (!window.getSelection()?.toString()) onOpen() }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        onOpen()
      }}
    >
      {columns.map((c) => (
        <td key={c.key} className={c.className}>{c.cell(member)}</td>
      ))}
      <td className="rowacts">
        <button
          className="ghost tiny"
          onClick={(e) => { e.stopPropagation(); onOpen() }}
        >
          Open
        </button>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// One member
// ---------------------------------------------------------------------------

/** The four tabs, in the order the customer asked for them, in his own words:
 *
 *    "I want to change the order of the tabs. 1 should be who he is. Maybe we
 *     can put a better name like personal information. Address should be part
 *     of personal information. His phone numbers should be part of personal
 *     information."
 *    "2 The next step should be emergency contact. I don't know if the name
 *     you gave it, like, next of kin. I don't know what that name is."
 *    "3 Should be how he should get his broadcast email carries radio on the
 *     radio card and the numbers that are saved on his personal information
 *     should be available here to take as a checkbox that the text message
 *     should be delivered to that number."
 *    "4 should be for certifications and documents. Could share the same tab."
 *
 *  There were six — Who he is, Reaching him, Address, Next of kin, Roster,
 *  Documents. The address and the numbers were their own tabs; the categories
 *  and the areas were one row of ticks on "Roster"; the text tick was on the
 *  same tab as the box the number is typed into. All four of those are what he
 *  was correcting.
 *
 *  ONE LIST FOR BOTH DIALOGS, AND NOW THE SAME FOUR IN BOTH. The registration
 *  used to draw five of six, because a scan is filed against a member id and
 *  there is none until the create returns. He was explicit that this is not
 *  good enough — "the registration page, and the editing page from existing
 *  member should be the same. Identical." — so the file is held in the browser
 *  and uploaded the moment the member exists. See `NewDocuments`.
 *
 *  `documents` IS STILL THE FOURTH TAB'S KEY though the tab is now called
 *  Certifications and documents. It is what the success banner's "File an
 *  identity document" asks for by name, and the upload it means is still
 *  behind it. Renaming the key would be a rename with no reader. */
type TabKey = 'personal' | 'emergency' | 'broadcast' | 'documents'

/** The three sub-tabs of Personal information.
 *
 *  THE CUSTOMER COUNTED THEM, having used the four tabs live: "Personal
 *  information has 3 sections. It should have 3 sub types inside. We should
 *  not need to scroll down." It was five blocks down one scrolling panel —
 *  identity, mailing address, phone numbers, category, areas — and the
 *  category and the areas are part of WHAT HE IS rather than a section of
 *  their own, so they ride on `identity` and the three he is counting are the
 *  three below.
 *
 *  A SECOND KEY TYPE AND NOT A WIDENED `TabKey`. The two strips are switched
 *  by two independent pieces of state and neither may hold the other's value:
 *  one `setTab('identity')` would leave the dialog with no main panel on
 *  screen at all. */
type PersonalKey = 'identity' | 'address' | 'numbers'

/** One tab in the strip.
 *
 *  A BADGE IS A COUNT OR AN ALARM AND NEVER DECORATION. The whole reason for
 *  putting anything on a tab is that the thing worth seeing should not need
 *  the tab opening — so a badge that says nothing (a colour on its own, a `0`
 *  on an empty section) is worse than none: it is one more mark to learn to
 *  ignore, which is the escalation rule this codebase keeps rediscovering.
 *
 *  `title` EXPLAINS THE NUMBER, IT DOES NOT CARRY THE WARNING. CLAUDE.md
 *  records what hover text costs — nothing on a touch screen, nothing
 *  announced on a non-interactive element — so it is used here only to break a
 *  count down ("2 groups, 3 certifications"), never to say the one thing
 *  somebody has to know. What has to be known is on screen: the amber, the
 *  footer sentence, and the notice inside the panel.
 *
 *  GENERIC OVER THE KEY so that the sub-strip inside Personal information is
 *  the SAME mechanism rather than a copy of it. A copy is where the roving
 *  `tabIndex`, the arrow keys and the `aria-controls` wiring drift apart — and
 *  a second tablist that answers Left and Right differently from the one above
 *  it is worse than no keyboard support at all. */
type TabSpec<K extends string = TabKey> = {
  key: K
  label: string
  /** What the badge says, or nothing at all. */
  badge?: string
  /** Amber rather than the quiet outline. */
  alarm?: boolean
  /** What the badge counts, for anybody who wants the breakdown. */
  badgeTitle?: string
}

/** Which tab a REGISTRATION opens on.
 *
 *  A member needs a unit number before anything else — it is his identity, and
 *  eleven of the customer's twenty-four rows are a unit with nobody filled in
 *  yet — so the form opens where that is typed.
 *
 *  THE CUSTOMER HAS NOT SEEN EITHER OF THESE LIVE. He approved tabs with free
 *  navigation ("I think we should have tabs. I should be freely to jump
 *  around") and will judge which tab each dialog lands on when he uses it. So
 *  both are one named constant apiece: changing his mind is a one-line
 *  change, not a hunt through the JSX. */
const ADD_OPENS_ON: TabKey = 'personal'

/** Which tab a RECORD opens on.
 *
 *  THE SAME ONE, NOW THAT THE NUMBERS ARE ON IT. It used to open on "Reaching
 *  him", because the numbers were there and a number is what an administrator
 *  opens an existing record to correct. They are on Personal information now,
 *  beside the identity and the address, so that is where a record opens.
 *  Broadcast is where the DECISION about a number is made, and it is one click
 *  away. Not seen live either — see `ADD_OPENS_ON`. */
const RECORD_OPENS_ON: TabKey = 'personal'

/** The strip itself — the four sections across the head of a dialog, and the
 *  three sub-sections inside Personal information.
 *
 *  LOCAL TO THIS FILE ON PURPOSE, and NOT in `lib/sheet.tsx`. That file holds
 *  the mechanisms shared across SHEETS — the column catalogue's ordering rule,
 *  the single dialog exit, `inert`, focus return — each of which is there
 *  because a COPY of it silently loses a bug that has already been paid for.
 *  This is one screen's furniture: no other dialog in the product is tabbed,
 *  and a generic tab component with a props list wide enough for the second
 *  caller is the unreadable thing everything depends on.
 *
 *  ONE COMPONENT FOR BOTH LAYERS, THOUGH. The sub-strip is the same mechanism
 *  with a different `tone`, because the alternative is a second copy of the
 *  roving `tabIndex`, the arrow keys and the `aria-controls` wiring — and the
 *  copy is where they drift, leaving one tablist that answers Home and End and
 *  one that does not.
 *
 *  ONE TAB STOP PER STRIP, not one per button. A roving `tabIndex` means Tab
 *  from the header lands on the selected tab and the next Tab goes into the
 *  panel — walking every button to reach the field you can already see is what
 *  makes a tabbed dialog worse on a keyboard than the scrolling column it
 *  replaced. With two layers that is two stops, not seven.
 *
 *  LEFT AND RIGHT MOVE, AND THE FOCUS GOES WITH THE SELECTION. The button is
 *  focused straight from the handler rather than after a render: every tab is
 *  already in the DOM, `focus()` works on a `tabIndex={-1}` element, and React
 *  updates the attribute on the same node — so the focus survives the
 *  re-render that moves the roving index onto it. That holds for a sub-strip
 *  too: only the sub-PANELS are hidden, never the buttons, so the one they
 *  belong to is on screen the whole time the arrows are being pressed.
 *
 *  AND THE TWO KEY HANDLERS CANNOT REACH EACH OTHER. `onKeyDown` here is on
 *  the strip's own element, and the main strip is a SIBLING of `.sheet-recbody`
 *  rather than an ancestor of it — the sub-strip lives inside a panel in the
 *  body. So Left inside the sub-strip has no path to the handler above it and
 *  cannot move both layers at once; no `stopPropagation` is needed, and one
 *  added here would be a guard against a thing the structure already prevents.
 *  Moving either strip inside the other is what would break that.
 */
function TabStrip<K extends string>({
  idPrefix, tabs, active, onPick, label, tone,
}: {
  /** `rec` or `add` for a main strip, `rec-personal` or `add-personal` for a
   *  sub-strip. Ids have to be unique in the document, and both dialogs draw
   *  the same keys. */
  idPrefix: string
  tabs: TabSpec<K>[]
  active: K
  onPick: (key: K) => void
  /** What this tablist is called, for anybody who cannot see that one of them
   *  is inside the other. There are two on screen at once and `Sections` twice
   *  says nothing about which is which. */
  label: string
  /** `sub` for the strip inside a panel: smaller, quieter, a different shape,
   *  and it does not claim the focus on open. See `.recsubs` in theme.css. */
  tone?: 'sub'
}) {
  const buttons = useRef<Partial<Record<K, HTMLButtonElement | null>>>({})

  // SOMETHING INSIDE THE DIALOG MUST HOLD FOCUS WHEN IT OPENS, and when the
  // tabs were first built nothing did. The unit-number input carried
  // `autoFocus` while the record opened on a different tab, so that input
  // mounted inside a `hidden` panel -- `display: none` -- and React's focus
  // call was a no-op. Meanwhile `useInertWhile` marks the sheet behind inert,
  // which blurs the row that was clicked. Focus ended up on `<body>`.
  //
  // BOTH DIALOGS NOW OPEN ON `personal`, whose unit-number box is visible, so
  // the common case is answered by `autoFocus` and this effect returns early.
  // It is still load-bearing for the two cases where nothing in the panel can
  // take focus: a record opened on a tab named by its caller (`openOn` -- the
  // success banner asks for Certifications and documents), and a record opened
  // by a role without `settings.members`, whose every field is inside a
  // `<fieldset disabled>` and cannot be focused at all.
  //
  // THE COST WAS NOT ONLY THE SCREEN READER SAYING NOTHING. From `<body>` the
  // next Tab does not enter the dialog at all: `useInertWhile` covers the
  // sheet and not the top bar, so the next tabbable things are Dispatch,
  // Overview, Call log and the rest of the chrome -- and Enter on any of them
  // switches view, unmounting the record. `useDialogGuard` is only consulted
  // by `leave()`, so a half-typed callsign, address or emergency contact went
  // with NO PROMPT AT ALL. That is the one exit the guard cannot cover,
  // reached by pressing Tab once.
  //
  // The selected TAB is what takes it, rather than the first field: it
  // announces the dialog and its tablist, the arrow keys work immediately, and
  // one Tab reaches the panel. It is also the only rule that holds for every
  // tab, including the ones whose panel has no focusable field at all because
  // the fieldset is disabled for a read-only role.
  //
  // AND ONLY THE MAIN STRIP MAY TAKE IT -- but NOT for the reason first
  // written here, which said a sub-strip's effect runs first because it is
  // mounted deeper. React queues passive effects in completion order, which
  // is depth-first LEFT TO RIGHT, and the main strip is an earlier SIBLING
  // of `.sheet-recbody` -- so the main strip's effect runs FIRST and would
  // win a race that never happens. The guard is still wanted: it makes which
  // strip owns the opening focus a decision, rather than something a third
  // strip or a moved one could silently change. Unguarded it would take the
  // focus this effect exists to place, and the dialog would open with the
  // keyboard on furniture two levels inside a panel rather than on the strip
  // that says what the dialog is about.
  useEffect(() => {
    if (tone === 'sub') return
    // ONLY IF NOTHING ELSE TOOK IT. Both dialogs open on `Personal
    // information`, whose unit-number input is visible and carries
    // `autoFocus` -- that works, and stealing it would cost a keystroke on the
    // form somebody is about to type into. React fires `autoFocus` in the
    // commit phase, before this passive effect, so by now the question is
    // already answered: an `activeElement` of `<body>` (or nothing) is exactly
    // the broken case and nothing else.
    const held = document.activeElement
    if (held && held !== document.body) return
    buttons.current[active]?.focus()
    // ON OPEN ONLY. Re-focusing on every tab change would drag focus back to
    // the strip while somebody is typing in a panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function move(e: KeyboardEvent<HTMLDivElement>) {
    const at = tabs.findIndex((t) => t.key === active)
    let next = at
    if (e.key === 'ArrowRight') next = (at + 1) % tabs.length
    else if (e.key === 'ArrowLeft') next = (at - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = tabs.length - 1
    else return
    e.preventDefault()
    const to = tabs[next]
    onPick(to.key)
    buttons.current[to.key]?.focus()
  }

  // The two layers differ by their class and by nothing else in here: same
  // roving index, same arrow keys, same aria wiring. See `.recsubs` in
  // theme.css for why the sub-strip has to LOOK unmistakably junior.
  const strip = tone === 'sub' ? 'recsubs' : 'rectabs'
  const button = tone === 'sub' ? 'recsubtab' : 'rectab'

  return (
    <div className={strip} role="tablist" aria-label={label} onKeyDown={move}>
      {tabs.map((t) => {
        const on = t.key === active
        return (
          <button
            key={t.key}
            id={`${idPrefix}-tab-${t.key}`}
            ref={(el) => { buttons.current[t.key] = el }}
            className={on ? `${button} on` : button}
            // THE DIALOG'S ROOT IS A FORM and a typeless button submits it, so
            // switching tabs would save the member. The same reason every
            // other button in these two dialogs says so.
            type="button"
            role="tab"
            aria-selected={on}
            aria-controls={`${idPrefix}-panel-${t.key}`}
            tabIndex={on ? 0 : -1}
            onClick={() => onPick(t.key)}
          >
            {t.label}
            {t.badge && (
              <span
                className={t.alarm ? 'rectab-badge alarm' : 'rectab-badge'}
                // NOT THE ACCESSIBLE NAME. `title` on a span with text of its
                // own is not read as part of the button's name, and produces
                // nothing at all on a touch screen -- which is the failure this
                // repository already paid for once, when a warning about
                // numbers saving immediately was moved onto a heading's
                // `title`. It stays as a hover hint for a mouse and the WORDS
                // are on the button below.
                title={t.badgeTitle}
                aria-hidden="true"
              >
                {t.badge}
              </span>
            )}
            {/* WHAT THE BADGE MEANS, IN WORDS. Two numbers on two tabs are
                structurally identical to anything that cannot see the colour:
                "Broadcast 2" is two numbers a broadcast cannot text, and
                "Certifications and documents 2" is two perfectly ordinary
                cards on file. The file's own
                rule is the roster sheet's -- letters, not colour, because a
                colour-coded mark says nothing to somebody who cannot tell the
                colours apart -- and the badge was breaking it. The name of the
                tab carries the meaning now. */}
            {t.badge && t.badgeTitle && (
              <span className="sronly">{` — ${t.badgeTitle}`}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/** One tab's contents.
 *
 *  MOUNTED EVEN WHEN IT IS NOT THE ONE ON SCREEN, and that is the whole
 *  argument for `hidden` rather than rendering only the active panel.
 *
 *  The dialog's own fields would survive either way — `form`, `joined`,
 *  `address`, `personal` and `chosen` all live in the dialog component, above
 *  these panels, so switching tabs cannot reach them. What would NOT survive
 *  is the state the panels' children hold for themselves: the half-typed row
 *  in `Numbers`' add bar, the correction open in its editor, the file picked
 *  in `Documents`, and the certification catalogue both pickers fetch on
 *  mount. Unmounting on every switch would clear the first three and re-fetch
 *  the last on every visit — somebody types a number, checks the address, and
 *  comes back to an empty box.
 *
 *  `hidden` IS `display: none`, so nothing inside a hidden panel is focusable
 *  and Tab cannot walk into the tab you are not looking at.
 *
 *  `tabIndex={0}` ON THE PANEL ITSELF is not decoration either. A record
 *  opened by a role without `settings.members` is one big `<fieldset
 *  disabled>`, and a disabled fieldset's contents are not focusable at all —
 *  so without this there is no keyboard route from the strip into what the
 *  panel says, and a screen reader driven by focus finds nothing behind the
 *  tab it just selected.
 *
 *  A SUB-PANEL IS NOT A TAB STOP, AND THAT IS THE SAME RULE READ THE OTHER
 *  WAY. The sub-panels sit INSIDE the Personal information panel, which is
 *  already focusable and already contains everything they show — so a second
 *  focusable container would take away no dead end and add a fourth stop
 *  between the dialog's header and the first box somebody came here to type
 *  in. The keyboard route the outer `tabIndex` exists to provide is unchanged.
 */
function TabPanel<K extends string>({
  idPrefix, tab, active, tone, children,
}: {
  idPrefix: string
  tab: K
  active: K
  /** `sub` for a panel behind the strip inside Personal information. */
  tone?: 'sub'
  children: ReactNode
}) {
  const sub = tone === 'sub'
  return (
    <div
      className={sub ? 'recsubpanel' : 'rectabpanel'}
      id={`${idPrefix}-panel-${tab}`}
      role="tabpanel"
      aria-labelledby={`${idPrefix}-tab-${tab}`}
      {...(sub ? {} : { tabIndex: 0 })}
      hidden={tab !== active}
    >
      {children}
    </div>
  )
}

/** WHO HE IS — the identity and the record-keeping beside it, and the first
 *  block behind Personal information's Identity sub-tab.
 *
 *  SPLIT FROM THE CHANNELS RATHER THAN COPIED. This and `ChannelFields` below
 *  were one `Fields` component drawing both halves down a single column; they
 *  are two tabs now and so they are two components, still shared by both
 *  dialogs, so the record and the registration cannot drift about what a
 *  member is. Nothing about what a field WRITES changed in the split: the same
 *  keys of the same `BLANK` object, through the same `onChange`.
 *
 *  AND IT NO LONGER DRAWS A HEADING, unlike `Numbers`, `CategoryChoice` and
 *  `AreaTicks`. Its heading said "Identity" and it is now the first thing
 *  behind a sub-tab that says IDENTITY directly above it, fourteen pixels up,
 *  in the same small tracked uppercase mono — the same word twice, which reads
 *  as a rendering fault rather than as a section.
 *
 *  THE HEADINGS THAT SURVIVED SAY SOMETHING THEIR TAB CANNOT. A MAILING
 *  address is not the dispatch place a member's home must never become, and
 *  the numbers are "saved as you go", which is a warning about a write that
 *  has already happened rather than a name for a block. `CategoryChoice` and
 *  `AreaTicks` keep theirs for a different reason again: they are the second
 *  and third blocks on this same sub-tab, so nothing else names them at all. */
function IdentityFields({
  value, onChange, idPrefix, joined, onJoined, birth, onBirth,
}: {
  value: typeof BLANK
  onChange: (next: typeof BLANK) => void
  idPrefix: string
  /** The joining date, held beside `BLANK` rather than in it, like the date of
   *  birth below. It is drawn HERE rather than appended by the dialog that
   *  wants it, which left it a full-width band across the bottom of the form
   *  with a half-empty grid row above it.
   *
   *  REQUIRED NOW, BECAUSE BOTH DIALOGS ASK FOR IT. It was optional while the
   *  registration form did not -- the customer, having tested the record:
   *  "I can edit all those information by adding a member, registering a new
   *  member. It also needs all the new updates." A field that can only be
   *  filled in by saving a member and then opening him again is a field that
   *  stays empty. */
  joined: string
  onJoined: (next: string) => void
  /** WHEN HE WAS BORN, beside Joined. STILL OPTIONAL, and the reason is the
   *  gate rather than the form: on the record it is drawn only for a role
   *  holding `settings.members`, because it arrives with
   *  `GET /api/members/{id}` and a role without that key has nothing to draw --
   *  an empty box there would read as "nothing on file" rather than as "you may
   *  not see this". The registration form always passes it: nothing has to be
   *  read back in order to type it, and only a role that may write is offered
   *  that form at all. */
  birth?: string
  onBirth?: (next: string) => void
}) {
  const set = (k: keyof typeof BLANK, v: string | boolean) =>
    onChange({ ...value, [k]: v })
  return (
    <>
      {/* THE UNIT FIRST AND FOCUSED, because it is the identity and it is what
          an administrator has in front of him. The name is the optional one —
          the other way round for as long as this screen existed, which is the
          model his roster disagreed with. */}
      <div className="grid3">
        <div className="field">
          <label htmlFor={`${idPrefix}-callsign`}>
            Unit number
          </label>
          <input
            id={`${idPrefix}-callsign`}
            value={value.callsign}
            autoFocus
            placeholder="MT-21"
            // WHERE THE PARAGRAPH UNDER THIS PAIR WENT. "One of the two is
            // enough, and the unit number is the one that matters" stood under
            // the whole form; the half that is a DEFINITION of these two boxes
            // is here, on the box. The half that is a RULE -- both blank is
            // refused -- is enforced twice already, by `members_identified` and
            // by the Save button, and the Save row names
            // whichever is missing, while it is missing. NOT by the server's
            // refusal: `identified` disables the button, so that sentence is
            // unreachable from this form. The label above says how he is
            // recognised, so this does not say it again.
            //
            // Concatenated rather than written across lines: a JSX string
            // attribute keeps its newlines and its indentation, and a tooltip
            // renders both.
            title={'A unit with nobody filled in yet is a real member of the '
              + 'roster, so this on its own is enough — a member needs this '
              + 'or a name.'}
            onChange={(e) => set('callsign', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-name`}>Name</label>
          <input
            id={`${idPrefix}-name`}
            value={value.display_name}
            title={'Optional. The unit number is the identity; the name is for '
              + 'the records inside the CRM.'}
            onChange={(e) => set('display_name', e.target.value)}
          />
        </div>

        {/* ONE GRID, THREE ACROSS, TWO ROWS. These were two stacked `grid2`
            blocks -- unit and name, then the dates and the plate -- which is
            three rows, and the third was the one that would not fit. The
            customer asked for a box that never resizes AND for Personal
            information not to scroll, and those two only hold together if the
            tallest sub-tab actually fits the box: Identity measured 538px
            against 485px of room on a 768px-tall laptop.

            THE DATES STILL SHARE A ROW as they did, because the eye reads them
            as the same kind of fact. Both are `date` inputs, so the browser's
            own picker and its own locale decide how they are typed -- and the
            value each carries is the ISO "1985-03-04" the server wants, which
            is why nothing here formats or parses one.

            THE EMAIL IS NOT IN THIS GRID AND MUST NOT COME BACK TO IT. It is a
            CHANNEL — how a broadcast reaches him — so it sits with the ticks
            on Broadcast, the tab the customer describes as "how he should get
            his broadcast". */}
        {onBirth && (
          <div className="field">
            <label htmlFor={`${idPrefix}-dob`}>Date of birth</label>
            <input
              id={`${idPrefix}-dob`}
              type="date"
              value={birth ?? ''}
              onChange={(e) => onBirth(e.target.value)}
            />
          </div>
        )}
        <div className="field">
          <label htmlFor={`${idPrefix}-joined`}>Joined</label>
          <input
            id={`${idPrefix}-joined`}
            type="date"
            value={joined}
            onChange={(e) => onJoined(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-plate`}>Vehicle plate</label>
          {/* BOUNDED AT THE SERVER'S OWN 40. Overshooting a `max_length` is not
              a sentence anybody can act on: FastAPI answers a validation
              failure with a 422 whose `detail` is an ARRAY of problems, and
              `request()` in lib/api.ts puts `body.detail` straight into the
              message — so a plate pasted twice by accident comes back as
              "[object Object]". The same bound is on the numbers and their
              labels on the next tab. */}
          <input
            id={`${idPrefix}-plate`}
            value={value.vehicle_plate}
            maxLength={40}
            placeholder="FJJ3885"
            onChange={(e) => set('vehicle_plate', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-imei`}>Handset id</label>
          {/* WHAT GOES IN HERE IS NOT SETTLED, AND THE FIELD SAYS SO RATHER
              THAN PRETENDING. `HANDOVER-CRM-DECISIONS.md` asks the fleet team
              "is the fleet device id the same thing as the IMEI, or a separate
              identifier?" and it is still unanswered -- it is in CLAUDE.md's
              open questions. Nothing in this product READS `radio_imei` today:
              it is written here and by the importer, indexed since migration
              004, and consumed by no feature. Migration 043 reads it once, to
              infer `has_radio` for members who already existed.

              So it is record-keeping until that answer arrives, and the
              placeholder asks for the thing on the label rather than naming a
              format we would have to correct later. A field that confidently
              says "IMEI" would have people typing one identifier while the
              fleet server matches on another, and the symptom is radio audio
              filed to the wrong member -- silently, which is why the handover
              asked.

              ITS FOOTNOTE IS NOW THIS `title`. The sentence is a definition of
              the box -- what goes in it and what reads it -- so it belongs on
              the box rather than standing under the form for everybody who is
              not filling that box in. */}
          <input
            id={`${idPrefix}-imei`}
            value={value.radio_imei}
            maxLength={40}
            placeholder="Whatever is on the handset"
            title={'For your records. Which identifier the fleet server '
              + 'matches on is still an open question with that team, so '
              + 'nothing reads this yet.'}
            onChange={(e) => set('radio_imei', e.target.value)}
          />
        </div>
      </div>
    </>
  )
}

/** BROADCAST — the channel ticks and the email address, with the text ticks
 *  drawn underneath by the dialog.
 *
 *  ONE TAB BECAUSE IT IS ONE QUESTION, and it is the customer's own: "how he
 *  should get his broadcast email carries radio on the radio card and the
 *  numbers that are saved on his personal information should be available here
 *  to take as a checkbox that the text message should be delivered to that
 *  number." Whether a broadcast reaches this member is answered by the ticks,
 *  the address and the per-number ticks together — a member with none of them
 *  is registered and unreachable, which is why the server refuses it.
 *
 *  THE NUMBERS THEMSELVES ARE EDITED ON PERSONAL INFORMATION AND ONLY TICKED
 *  HERE, which is the split he asked for: a number is a fact about the person,
 *  and which of his numbers takes a text is a decision about the broadcast.
 *  `TextTicks` on the record and `NewTextTicks` on the registration draw that
 *  list — two components, because one writes the moment it is pressed and the
 *  other goes into a create body. */
function ChannelFields({
  value, onChange, idPrefix,
}: {
  value: typeof BLANK
  onChange: (next: typeof BLANK) => void
  idPrefix: string
}) {
  const set = (k: keyof typeof BLANK, v: string | boolean) =>
    onChange({ ...value, [k]: v })
  return (
    <>
      <div className="field">
        {/* THE "TEXT IS TICKED ON EACH NUMBER BELOW" FOOTNOTE IS NOW THIS
            `title`. It explains an ABSENCE -- why there is no Text tick in a
            row of channel ticks -- so there is no control of its own to hang
            it on, and the label over the row is the thing it is about. */}
        <label
          title={'Text is ticked on each number below, not here — a member '
            + 'answers on more than one and only some of them take texts. '
            + 'Everything ticked fires at once, not one after another.'}
        >
          How a broadcast reaches them
        </label>
        <div className="checks">
          <label className="check">
            <input
              type="checkbox"
              checked={value.notify_email}
              onChange={(e) => set('notify_email', e.target.checked)}
            />
            Email
          </label>

          {/* THE CARD IS DELIVERED TO A HANDSET, so it hangs off the radio and
              is drawn under it rather than beside it. The server refuses the
              pair outright (`_reachable`), and a checkbox that can be ticked
              into a state the save will reject is a control that lies about
              what it does -- the refusal arrives after the whole record has
              been filled in.

              THE OTHER DIRECTION IS NOT TRUE AND MUST NOT BE ENFORCED: a
              member can carry a radio and take no card. The customer said so
              in as many words. So the radio never depends on the card. */}
          <label className="check">
            <input
              type="checkbox"
              checked={value.has_radio}
              onChange={(e) => {
                const on = e.target.checked
                // TAKING THE RADIO AWAY TAKES THE CARD WITH IT, IN ONE UPDATE.
                // The alternative is a combination the server refuses (a card
                // needs a handset), found on Save. It was two `set` calls, and
                // both spread the SAME `value` -- so the second put the radio
                // back: the box could never be unticked, and the card choice
                // was wiped behind a card box drawn unticked anyway while the
                // card does not exist. A radio kept keeps the card choice.
                onChange(on ? { ...value, has_radio: true }
                  : { ...value, has_radio: false, notify_card: false })
              }}
            />
            Carries a radio
          </label>

          {/* UNCLICKABLE WHILE THERE IS NO CARD TO DELIVER -- the customer's
              instruction, and `broadcasting.CARD_DELIVERY` on the server.
              Drawn unticked whatever is stored, because a disabled tick that
              shows as on reads as "he gets cards". The stored choice is left
              alone and comes back the day the card exists. */}
          <label
            className={value.has_radio && cardDelivery() ? 'check' : 'check off'}
            title={!cardDelivery() ? 'The radio card is not available yet.'
              : value.has_radio ? undefined
              : 'The card is delivered to the handset, so it needs a radio.'}
          >
            <input
              type="checkbox"
              checked={value.notify_card && cardDelivery()}
              disabled={!value.has_radio || !cardDelivery()}
              onChange={(e) => set('notify_card', e.target.checked)}
            />
            {cardDelivery() ? 'Radio card' : 'Radio card (not available yet)'}
          </label>
        </div>
        {!value.has_radio && cardDelivery() && (
          <p className="footnote">
            The radio card needs a radio to be delivered to. A member can carry
            one and take no card, but not the other way round.
          </p>
        )}
      </div>

      {/* THE ADDRESS UNDER THE TICK THAT USES IT. Email was in the identity
          grid, three fields away from the box that decides whether anything is
          ever sent to it — so a member could be ticked for email with no
          address, or given an address with the tick off, and neither reads as
          wrong until a broadcast goes out. They are adjacent now. */}
      <div className="field">
        <label htmlFor={`${idPrefix}-email`}>Email</label>
        <input
          id={`${idPrefix}-email`}
          type="email"
          value={value.email}
          onChange={(e) => set('email', e.target.value)}
        />
      </div>
      {/* THERE IS NO TEXT TICK HERE AND THERE MUST NOT BE ONE. The customer:
          "it should be optional on his contact card, which one should have a
          check next to it that it should get the text." So the tick lives on
          the NUMBER; `members.notify_sms` is derived from those ticks by
          `members.resync`, the PATCH body will not accept it, and a control
          here would be a second writer contradicting the first.

          The footnote that used to say so out loud is the `title` on the label
          above. The customer, looking at this record: "all those small letters
          you can please remove. It's too much instructions on the page." So
          the rule did not change and neither did where it is enforced -- only
          where it is READ. */}
    </>
  )
}

/** The catalogue, plus any group this member is already in that the catalogue
 *  does not list.
 *
 *  A RETIRED GROUP HE IS STILL IN IS STILL A GROUP HE IS IN. `api.memberGroups()`
 *  asks for the ACTIVE list, while `chosen` is seeded from the member's own
 *  rows — and `groups_of` returns those whether the group was retired or not.
 *  Drawn from the catalogue alone, a retired category would be invisible AND
 *  still in `chosen`: picking a different one would then send two categories,
 *  and `set_groups` would refuse the whole save with a sentence naming a group
 *  that is nowhere on screen. Drawn, it can be seen and replaced, and an area
 *  nobody can untick stops being a membership nothing admits to.
 *
 *  `active: false` ON THE EXTRAS IS NOT A GUESS. The catalogue is the active
 *  list, so absent from it is exactly what retired means here. `groups_of`
 *  returns id, name and kind and no flag of its own, which is why this is
 *  decided by which list the group came from rather than read off it. */
function withHisOwn(catalogue: MemberGroup[], his: MemberGroup[]): MemberGroup[] {
  const known = new Set(catalogue.map((g) => g.id))
  const extra = his.filter((g) => !known.has(g.id))
    .map((g) => ({ ...g, active: false }))
  return extra.length === 0 ? catalogue : [...catalogue, ...extra]
}

/** WHAT HE IS — one category, as radio buttons.
 *
 *  RADIO BUTTONS BECAUSE HE HOLDS ONE. Asked whether a member could be in more
 *  than one category, the customer answered "exactly one" — so picking one
 *  un-picks the other, in front of him, rather than being accepted here and
 *  refused by the server after the whole record has been filled in. The
 *  database holds the same rule as a partial unique index
 *  (`member_group_members_one_category`, migration 046) and `set_groups`
 *  refuses a second in a sentence naming both; this is the control that means
 *  neither of them ever has to fire.
 *
 *  AT MOST ONE, THOUGH, WHICH IS WHY THERE IS A "None". The server stores at
 *  most one and not exactly one, deliberately: half the customer's roster is a
 *  unit with nobody filled in yet, the importer files a member under no
 *  category when his Status names none, and an organization has no categories
 *  at all until it makes some. A radio set with no way back would be a
 *  one-way door — a mis-click nobody could undo from this screen — so the
 *  first option is no category, and choosing it sends the areas alone.
 *
 *  SET, NOT ADD, like the areas below: `set_groups` reads the whole list as
 *  the whole answer, so what this hands back is every group he is in and
 *  unticking is a real operation rather than a second endpoint. Both controls
 *  write ONE list for that reason — two lists would be two save paths for one
 *  member, which is two chances for one of them to be dropped. */
function CategoryChoice({
  groups, chosen, onChange, idPrefix,
}: {
  groups: MemberGroup[]
  chosen: string[]
  onChange: (next: string[]) => void
  /** `rec` or `add`. A radio group is bound together by its `name`, and both
   *  dialogs can be in the document at once — one name across the two would
   *  make picking a category on the record un-pick the registration's. */
  idPrefix: string
}) {
  const categories = groups.filter((g) => g.kind === 'category')
  const ids = new Set(categories.map((g) => g.id))
  // WHICHEVER OF HIS GROUPS IS A CATEGORY. There can only be one — the index
  // says so — so `find` is the whole answer rather than a first-of-several.
  const picked = chosen.find((id) => ids.has(id)) ?? ''
  const headingId = `${idPrefix}-category`

  /** Replace it. Every category comes off the list and at most one goes back
   *  on, which is what makes this a radio button rather than a tick that
   *  happens to look like one. The areas are untouched. */
  function choose(id: string) {
    onChange([...chosen.filter((x) => !ids.has(x)), ...(id ? [id] : [])])
  }

  return (
    <>
      <h4 className="sheet-recsub" id={headingId}>Category</h4>
      {/* WHERE THEY ARE MADE, AND NO PROMISE ABOUT WHAT THEY DO YET. The
          sentence this replaced said a broadcast could then be aimed at them,
          and nothing aims one at a group today — `app/incidents.py` has no
          group query. A control that describes an unbuilt feature is the thing
          section 19 forbids, wearing a different hat. */}
      {categories.length === 0 ? (
        <p className="dim">
          No categories yet. They are made from Groups on the roster toolbar.
        </p>
      ) : (
        <div className="checks" role="radiogroup" aria-labelledby={headingId}>
          <label className="check">
            <input
              type="radio"
              name={headingId}
              checked={picked === ''}
              onChange={() => choose('')}
            />
            No category
          </label>
          {categories.map((g) => (
            <label className="check" key={g.id}>
              <input
                type="radio"
                name={headingId}
                checked={picked === g.id}
                onChange={() => choose(g.id)}
              />
              {g.name}
              {/* Only ever on one he is already in — see `withHisOwn`. It is
                  drawn so it can be replaced, and marked so nobody puts
                  somebody new into a category the organization has stopped
                  using. */}
              {g.active === false && <span className="lbl">retired</span>}
            </label>
          ))}
        </div>
      )}
    </>
  )
}

/** WHERE HE COVERS — as many areas as apply, as ticks.
 *
 *  ITS OWN BLOCK WITH ITS OWN HEADING, and that is the correction rather than
 *  a layout preference. The customer, looking at one row of ticks holding
 *  both: "It's two separate things, categories and areas. I think you're
 *  looking at it as it's one big thing. It should be like separate." Drawn
 *  together, the only thing saying which was which was a small grey word after
 *  each name, and nothing on screen said one of them was a choice of one.
 *
 *  MANY, DELIBERATELY. "It should be available to choose more than one area."
 *  A coordinator is also somebody's neighbour, and being promoted must not
 *  stop him hearing about his own street. */
function AreaTicks({
  groups, chosen, onChange, idPrefix,
}: {
  groups: MemberGroup[]
  chosen: string[]
  onChange: (next: string[]) => void
  idPrefix: string
}) {
  const areas = groups.filter((g) => g.kind === 'area')
  const headingId = `${idPrefix}-areas`
  return (
    <>
      <h4 className="sheet-recsub" id={headingId}>Areas</h4>
      {/* Same rule as the categories above: where they are made, and nothing
          about aiming a broadcast, which is not built. */}
      {areas.length === 0 ? (
        <p className="dim">
          No areas yet. They are made from Groups on the roster toolbar.
        </p>
      ) : (
        <div className="checks" role="group" aria-labelledby={headingId}>
          {areas.map((g) => (
            <label className="check" key={g.id}>
              <input
                type="checkbox"
                checked={chosen.includes(g.id)}
                onChange={(e) => onChange(e.target.checked
                  ? [...chosen, g.id]
                  : chosen.filter((id) => id !== g.id))}
              />
              {g.name}
              {g.active === false && <span className="lbl">retired</span>}
            </label>
          ))}
        </div>
      )}
    </>
  )
}

/** Where a refusal belongs: the id of the number whose control was pressed, or
 *  `ADDING` for the bar at the bottom. */
type Failure = { at: string; message: string }

const ADDING = 'the add bar'

/** A number being corrected, and what has been typed over it so far. */
type Draft = { id: string; phone: string; label: string }

/** The numbers a member answers on — added, corrected, re-primaried and
 *  removed here, and TICKED FOR TEXT SOMEWHERE ELSE.
 *
 *  THE TICK IS ON BROADCAST AND THERE IS NOT A SECOND ONE HERE. The customer's
 *  own split: the numbers belong to his personal information and the decision
 *  about which of them a text goes to belongs to "how he should get his
 *  broadcast". `TextTicks` is the one control that writes `member_phones.notify`
 *  — it calls the same `PATCH .../phones/{id}` these buttons call, with the
 *  same one meaning of "gets the text". A tick here as well would be two
 *  controls for one fact, which is the shape this codebase names the
 *  phone-book bug.
 *
 *  EVERY ONE OF THESE FOUR ROUTES RETURNS THE WHOLE MEMBER, and that is
 *  absorbed rather than unpicked. Adding, correcting, removing or
 *  re-primarying a number changes `phone`, `phone_e164` and `notify_sms` on
 *  the member — so a screen that patched its own copy of the list would be a
 *  second definition of "his number" living in the browser. `remove_phone` on
 *  the phone book learned this the same way.
 *
 *  THEY WRITE IMMEDIATELY, so they are said to write immediately. Cancel puts
 *  none of it back, which is not what a dialog with a Save button implies.
 *
 *  A REFUSAL IS SHOWN AT THE CONTROL THAT WAS PRESSED, never handed up to the
 *  record's own notice. This block is the LAST thing in a body that scrolls
 *  and the notice is pinned to the TOP of it, so untick-flicks-back with a
 *  sentence somewhere off screen is the whole of what the operator sees. The
 *  server's refusals here are the ones most worth reading — "Make another
 *  number the main one instead", "That number could not be read" — and each
 *  one names a row, so it is drawn under that row.
 */
function Numbers({
  member, busy, setBusy, onNumbers,
}: {
  member: Member
  busy: boolean
  setBusy: (on: boolean) => void
  onNumbers: (m: Member) => void
}) {
  const [phone, setPhone] = useState('')
  const [label, setLabel] = useState('')
  const [editing, setEditing] = useState<Draft | null>(null)
  const [failed, setFailed] = useState<Failure | null>(null)

  function said(at: string, err: unknown, fallback: string) {
    // The server's own sentences — "That number could not be read as a phone
    // number", "Make another number the main one instead" — are better than
    // anything this screen could say, so they are shown verbatim.
    setFailed({ at, message: err instanceof ApiError ? err.message : fallback })
  }

  async function add() {
    const typed = phone.trim()
    if (!typed) return
    setBusy(true)
    setFailed(null)
    try {
      onNumbers(await api.memberPhoneAdd(member.id, {
        phone: typed,
        label: label.trim() || null,
        // `null` — NOT STATED, which is the only thing this bar can honestly
        // say. The tick moved to Broadcast, so nobody has decided yet whether
        // a text goes to this number, and the server reads "not stated" as
        // "text it if it can be read". An explicit `true` would be worse than
        // wrong: on a number it cannot read it is a REFUSAL, which would throw
        // away the number that was just typed. Keeping an unreadable number so
        // somebody can SEE and fix it is the entire reason this table exists,
        // so it lands visibly amber and visibly unticked, on the tab where the
        // tick lives.
        notify: null,
      }))
      // Cleared INSIDE the write, so a refusal leaves the words in the box.
      setPhone('')
      setLabel('')
    } catch (err) {
      said(ADDING, err, 'Could not add that number.')
    } finally {
      setBusy(false)
    }
  }

  /** Which number is the main one.
   *
   *  `notify` IS NOT IN THIS SIGNATURE AND MUST NOT BE ADDED TO IT. It is the
   *  same route, and that is the point — one route, but exactly one control
   *  that decides whether a broadcast texts a number, and it is `TextTicks` on
   *  Broadcast. Widening this back to `{ notify?: boolean }` is all it would
   *  take to have two. */
  async function makeMain(ph: MemberPhone) {
    setBusy(true)
    setFailed(null)
    try {
      onNumbers(await api.memberPhoneUpdate(member.id, ph.id, { is_primary: true }))
    } catch (err) {
      said(ph.id, err, 'Could not change that number.')
    } finally {
      setBusy(false)
    }
  }

  /** The correction itself. `PATCH .../phones/{id}` re-normalises `phone`
   *  through `app/phones.py` and clears the tick if it still cannot read it,
   *  so nothing here has to reason about what a number IS.
   *
   *  ONLY WHAT CHANGED IS SENT. A body with neither field is "nothing to
   *  change" and comes back 400, which is a refusal for pressing Save on a row
   *  nobody edited — so that case closes the editor instead. Sending the
   *  number back unchanged would also re-run the normaliser for nothing.
   */
  async function saveEdit() {
    if (!editing) return
    const row = member.phones.find((p) => p.id === editing.id)
    if (!row) { setEditing(null); return }
    const typed = editing.phone.trim()
    if (!typed) return
    const named = editing.label.trim()
    const changes: { phone?: string; label?: string | null } = {}
    if (typed !== row.phone) changes.phone = typed
    if (named !== (row.label ?? '')) changes.label = named || null
    if (!('phone' in changes) && !('label' in changes)) { setEditing(null); return }
    setBusy(true)
    setFailed(null)
    try {
      onNumbers(await api.memberPhoneUpdate(member.id, row.id, changes))
      // Closed INSIDE the write, like the add bar is cleared inside it: a
      // number the server still cannot read comes back with a sentence, and
      // the correction has to still be in the box to be corrected again.
      setEditing(null)
    } catch (err) {
      said(row.id, err, 'Could not change that number.')
    } finally {
      setBusy(false)
    }
  }

  async function remove(ph: MemberPhone) {
    const shown = prettyPhone(ph.phone) || ph.phone
    if (!confirm(`Remove ${shown} from ${member.label}? Texts already sent to `
      + 'it stay on the record of who was told.')) return
    setBusy(true)
    setFailed(null)
    try {
      onNumbers(await api.memberPhoneRemove(member.id, ph.id))
    } catch (err) {
      said(ph.id, err, 'Could not remove that number.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* THIS ONE GOES BACK ON SCREEN, AND IT IS THE ONE EXCEPTION TO THE
          instruction that took the other four off. It was briefly a `title` on
          this heading, which is the least reachable place it could have gone:
          a heading is not something anybody hovers, `title` produces NOTHING
          on a touch screen — CLAUDE.md already records that cost, for the
          phone book's hover-only row buttons, "they were not awkward on a
          tablet — they were unreachable" — and it is not announced on a
          non-interactive element.

          What makes it different from the four that went: those were
          DEFINITIONS of a box, and this is a warning about a write that has
          already happened. The dialog actively contradicts it — the phone rows
          are deliberately outside `snapshot`, so `useDialogGuard` offers to
          "discard your unsaved changes" over a number that is already stored.
          Somebody removes one, thinks better of it, presses Cancel, accepts
          the discard, and the number is gone from a roster whose whole purpose
          is reachability.

          Four words on the heading rather than a paragraph under it. The text
          ticks carry the same four on Broadcast, and for the same reason. */}
      <h4 className="sheet-recsub">
        Phone numbers <span className="chans">saved as you go</span>
      </h4>

      {member.phones.length === 0 ? (
        <div className="dim">
          No number on file. He is reached by radio, the card or email.
        </div>
      ) : member.phones.map((ph) => {
        // THIS row's draft, or none. Written out rather than asked as
        // `editing?.id === ph.id` so the branch below has a non-null `editing`
        // without a second assertion about it.
        const draft = editing && editing.id === ph.id ? editing : null
        return (
          <Fragment key={ph.id}>
            {draft ? (
              <div
                // `.callbar` for the input styling, which `.listrow` has none
                // of. Its bottom margin is for a bar sitting ABOVE a list; this
                // one IS a row in the list, and 14px underneath it opens a gap
                // that reads as the end of the numbers.
                className="callbar"
                style={{ marginBottom: 0, padding: '4px 0' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // These boxes sit inside the record's own <form>, so
                    // without this Enter submits it: the member is saved, the
                    // dialog closes, and the correction goes with it.
                    e.preventDefault()
                    saveEdit()
                    return
                  }
                  if (e.key !== 'Escape') return
                  // ESCAPE MUST NOT REACH THE WINDOW. `useDialogGuard` listens
                  // there, so the key that means "forget this correction"
                  // would close the whole record instead — and with no scalar
                  // field touched it would not even ask first. React attaches
                  // its listeners below `window`, so stopping it here stops it;
                  // the group rename row is guarded the same way.
                  e.stopPropagation()
                  setEditing(null)
                }}
              >
                <PhoneInput
                  wrapClass="grow"
                  value={draft.phone}
                  maxLength={40}
                  autoFocus
                  aria-label="Correct the number"
                  onChange={(e) => setEditing({ ...draft, phone: e.target.value })}
                />
                <input
                  value={draft.label}
                  maxLength={40}
                  placeholder="mobile, home, the shop"
                  aria-label="What to call it"
                  onChange={(e) => setEditing({ ...draft, label: e.target.value })}
                />
                <button
                  className="ghost tiny nogrow"
                  type="button"
                  disabled={busy || !draft.phone.trim()}
                  onClick={() => saveEdit()}
                >
                  Save number
                </button>
                <button
                  className="ghost tiny nogrow"
                  type="button"
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="listrow sub">
                <span className="mono">{prettyPhone(ph.phone) || ph.phone}</span>
                {ph.label && <span className="tag">{ph.label}</span>}
                {ph.is_primary && <span className="tag">main</span>}
                {!ph.readable && <span className="pill wait">could not be read</span>}
                {/* WHETHER A TEXT GOES TO IT IS ON BROADCAST, NOT HERE. It is
                    not repeated as a read-only mark either: a tick somebody
                    cannot press, on the tab where every other control writes,
                    is a control drawn and then refused. What this row says is
                    what this row can change. */}
                <span className="spacer" />
                {/* THE ONLY WAY TO CORRECT A TYPO. `PATCH .../phones/{id}`
                    takes a new `phone` and `label`, `update_number` re-runs the
                    whole normalise-and-retick path over them, and no control
                    anywhere sent either — so the amber "could not be read" and
                    the footnote below it named a fix nothing on screen could
                    perform. Removing and retyping the number is not that fix:
                    it loses the label and files the number last. The label
                    rides along because it is the same route and the same
                    write. */}
                <button
                  className="ghost tiny"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setFailed(null)
                    setEditing({ id: ph.id, phone: ph.phone, label: ph.label ?? '' })
                  }}
                >
                  Edit
                </button>
                {!ph.is_primary && (
                  <button
                    className="ghost tiny"
                    type="button"
                    disabled={busy}
                    onClick={() => makeMain(ph)}
                  >
                    Make main
                  </button>
                )}
                <button
                  className="ghost tiny"
                  type="button"
                  disabled={busy}
                  onClick={() => remove(ph)}
                >
                  Remove
                </button>
              </div>
            )}
            {failed?.at === ph.id && (
              <div className="notice" role="alert">{failed.message}</div>
            )}
          </Fragment>
        )
      })}

      {/* THE PARAGRAPH ABOUT AN UNREADABLE NUMBER IS ON BROADCAST NOW, beside
          the tick it is about — this is where the number is CORRECTED and that
          is where being unable to tick it costs something. Two copies would be
          two places to keep in step, and the pill and the Edit button beside
          it already say everything this tab can do about it. */}

      {/* Above the bar, not at the top of the dialog: the words that were
          refused are still in these boxes and this is where the eye is. */}
      {failed?.at === ADDING && (
        <div className="notice" role="alert">{failed.message}</div>
      )}

      {/* Enter in any of these adds the number. They sit inside the record's
          own <form>, so without this Enter submitted it — saving the member,
          closing the dialog, and leaving the number that had just been typed
          in a box that no longer exists. */}
      <div className="callbar" onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        add()
      }}>
        <PhoneInput
          wrapClass="grow"
          value={phone}
          maxLength={40}
          placeholder="Another number"
          aria-label="Another number"
          onChange={(e) => setPhone(e.target.value)}
        />
        <input
          value={label}
          maxLength={40}
          placeholder="mobile, home, the shop"
          aria-label="What to call it"
          onChange={(e) => setLabel(e.target.value)}
        />
        {/* NO TEXT TICK ON THIS BAR EITHER. A number arrives here as a fact
            about the person; whether a broadcast texts it is decided on
            Broadcast, where every one of his numbers is in one list and the
            decision can be made about all of them at once. `notify: null`
            above is what leaves that decision open. */}
        <button
          className="ghost tiny nogrow"
          type="button"
          disabled={busy || !phone.trim()}
          onClick={() => add()}
        >
          Add
        </button>
      </div>
    </>
  )
}

/** WHICH OF HIS NUMBERS TAKE THE TEXT. The customer, describing this tab:
 *  "the numbers that are saved on his personal information should be available
 *  here to take as a checkbox that the text message should be delivered to
 *  that number."
 *
 *  THE SAME COLUMN, THE SAME ROUTE, AND THE ONLY WRITER OF IT. This calls
 *  `PATCH /api/members/{id}/phones/{id}` with `notify`, which is the one route
 *  that has ever written `member_phones.notify`; `members.resync` derives the
 *  member's own `notify_sms` from those ticks and nothing else may write that
 *  either. What moved is where the control is drawn, and only that — a second
 *  tick left behind on Personal information would be two controls for one
 *  fact, and the first time they disagreed one screen would say a member is
 *  textable while the broadcast reached nobody.
 *
 *  THE NUMBERS ARE READ-ONLY HERE. Adding, correcting, re-primarying and
 *  removing are all on Personal information, and this list deliberately offers
 *  none of them: the tab is a decision about the broadcast, not a place to
 *  edit the person. That is also why a number the server could not read is
 *  drawn with the reason and a way back rather than with an Edit button of its
 *  own.
 *
 *  IT WRITES IMMEDIATELY, so it says so — like the numbers themselves and the
 *  certifications. These ticks are outside the dialog's `snapshot`, so Cancel
 *  puts none of them back, and somebody who unticked a number, thought better
 *  of it and pressed Cancel would otherwise be told his change was discarded
 *  when it was stored. */
function TextTicks({
  member, busy, setBusy, onNumbers, onFix,
}: {
  member: Member
  busy: boolean
  setBusy: (on: boolean) => void
  onNumbers: (m: Member) => void
  /** Go to Personal information, where a number is corrected. The dialog owns
   *  this because it owns which tab is showing, and it moves focus with the
   *  tab — a link that switches what is on screen and leaves the keyboard
   *  behind reads as a broken control. */
  onFix: () => void
}) {
  const [failed, setFailed] = useState<Failure | null>(null)

  async function change(ph: MemberPhone) {
    setBusy(true)
    setFailed(null)
    try {
      onNumbers(await api.memberPhoneUpdate(member.id, ph.id, { notify: !ph.notify }))
    } catch (err) {
      // The server's own sentence, verbatim — it is better than anything this
      // screen could say about a number it has not read.
      setFailed({
        at: ph.id,
        message: err instanceof ApiError
          ? err.message : 'Could not change that number.',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h4 className="sheet-recsub">
        Text messages <span className="chans">saved as you go</span>
      </h4>

      {member.phones.length === 0 ? (
        <div className="dim">
          No number on file, so no text can reach him. Add one on Personal
          information → Numbers; he can still be reached by radio, the
          card or email.
        </div>
      ) : member.phones.map((ph) => (
        <Fragment key={ph.id}>
          <div className="listrow sub">
            <span className="mono">{prettyPhone(ph.phone) || ph.phone}</span>
            {ph.label && <span className="tag">{ph.label}</span>}
            {ph.is_primary && <span className="tag">main</span>}
            <span className="spacer" />
            {/* NO `title` ON THIS ONE, unlike the channel ticks above it. The
                reason a box is closed is printed under the row, in words, and
                a tooltip repeating it is a second copy that produces nothing on
                a touch screen anyway. `.check.off` is the visual half — a
                disabled control that looks exactly like its neighbours reads as
                a broken screen rather than as a rule. */}
            <label className={ph.readable ? 'check' : 'check off'}>
              <input
                type="checkbox"
                checked={ph.notify}
                // A NUMBER NOBODY COULD READ CANNOT BE TICKED. The database
                // refuses the pair outright (`member_phones_textable`) and a
                // CHECK violation reaches a screen as an unreadable 500 — so
                // the control is closed here, and the reason and the fix are
                // printed under the row rather than left to hover text that
                // produces nothing on a touch screen.
                disabled={!ph.readable || busy}
                onChange={() => change(ph)}
              />
              Gets the text
            </label>
          </div>
          {!ph.readable && (
            <p className="footnote warn">
              This could not be read as a phone number, so a broadcast could
              never reach it and the tick is refused rather than left looking
              as though it worked. Correct it, or give it its country code, on{' '}
              {/* NAMED IN WORDS AS WELL AS LINKED. This whole panel is inside
                  the record's `<fieldset disabled>` while a write is in flight
                  and for a read-only role, and a disabled button cannot be
                  pressed — so the sentence has to name the tab, which the tab
                  strip above (outside that fieldset) can always reach. */}
              <button className="linky" type="button" onClick={onFix}>
                Personal information → Numbers
              </button>.
            </p>
          )}
          {failed?.at === ph.id && (
            <div className="notice" role="alert">{failed.message}</div>
          )}
        </Fragment>
      ))}
    </>
  )
}

/** The most numbers one registration can carry — `MemberIn.phones`'s own
 *  `max_length`, not a figure chosen here.
 *
 *  ENFORCED IN THE FORM BECAUSE THE SERVER'S REFUSAL IS UNREADABLE. A pydantic
 *  length failure is a 422 whose `detail` is an ARRAY of error objects, and
 *  `request()` in lib/api.ts puts `body.detail` straight into the message — so
 *  an eleventh number would come back as "[object Object]", naming nothing,
 *  after the whole registration had been filled in and pressed. `text_within`
 *  on the server exists for exactly that reason and does not cover a list
 *  length, so this one is the form's own job. */
const MAX_NEW_NUMBERS = 10

/** One number typed on the registration form, before there is a member to
 *  attach it to. */
type NewNumber = {
  phone: string
  label: string
  /** Whether a broadcast texts it. TICKED ON BROADCAST AND NOWHERE ELSE, like
   *  the record's — `NewTextTicks` is the only control that touches this
   *  field, and it lists exactly the rows this one holds. It starts true
   *  because that is what "not stated" means to the server, and it is sent as
   *  `null` rather than `true` — see the create body for why that difference
   *  is a whole registration. */
  notify: boolean
  /** Which one is the main number. `members.add_number` would decide on its
   *  own (the first number a member has becomes the primary when nothing else
   *  claims it), but then this form would be silent about a fact the record
   *  screen shows in a tag the moment it is saved. */
  primary: boolean
}

const BLANK_NUMBER: NewNumber = {
  phone: '', label: '', notify: true, primary: false,
}

/** The numbers a member answers on, typed before there is a member — and, like
 *  the record's list, TICKED FOR TEXT ON BROADCAST rather than here.
 *
 *  A LIST, NOT ONE BOX. The customer's model is that a member has as many
 *  numbers as he answers on, each with its own text tick — `member_phones` is
 *  built for it, the record screen sends a list, and `MemberIn.phones` takes
 *  up to ten in the create body. One box here was the old single-`phone` shape
 *  surviving in the one place a member is created, so every number after the
 *  first had to be added later, on a screen somebody had to know to go back
 *  to.
 *
 *  NOTHING HERE WRITES. These rows go into the create body, so — unlike the
 *  record's `Numbers` — there is no "saved as you go" beside the heading:
 *  Cancel really does discard them, and the dialog's guard counts them.
 *
 *  AND NOTHING HERE DECIDES WHAT A NUMBER IS. `app/phones.py` is the one
 *  normaliser and it runs on the server; a browser working out whether these
 *  digits can be texted would be the phone-book bug in the place it is hardest
 *  to see. So a row is drawn exactly as it was typed, there is no "could not
 *  be read" mark on this form, and the first thing that knows is the member
 *  the POST answers with — which is why a registration that comes back holding
 *  one opens his record.
 */
function NewNumbers({ rows, onRows, draft, onDraft }: {
  rows: NewNumber[]
  onRows: (next: NewNumber[]) => void
  draft: NewNumber
  onDraft: (next: NewNumber) => void
}) {
  const [failed, setFailed] = useState<string | null>(null)
  const full = rows.length >= MAX_NEW_NUMBERS

  function add() {
    const typed = draft.phone.trim()
    if (!typed || full) return
    // THE ONLY DUPLICATE THIS FORM CAN SEE IS AN IDENTICAL ONE, and it must not
    // try to see any other: whether `514-555-1234` and `5145551234` are the
    // same number is `app/phones.py`'s answer and nobody else's. This catches
    // the double-press. The server catches the rest with "That number is
    // already on this member" — which, raised inside the create body, rolls the
    // whole registration back over a number somebody could simply have dropped.
    if (rows.some((r) => r.phone === typed)) {
      setFailed('That number is already on the list.')
      return
    }
    setFailed(null)
    onRows([...rows, {
      ...draft,
      phone: typed,
      label: draft.label.trim(),
      // THE FIRST ONE IS THE MAIN ONE, which is what the server does anyway.
      primary: rows.length === 0,
    }])
    // Cleared only once the row is on the list, like the record's add bar: a
    // refusal has to leave the typing where it was.
    onDraft({ ...BLANK_NUMBER })
  }

  function remove(at: number) {
    const left = rows.filter((_, i) => i !== at)
    // TAKING THE MAIN NUMBER OFF MUST LEAVE A MAIN NUMBER. `add_number` makes
    // the first number a member has the primary when nothing claims it, so
    // without this the form would show no main number and the record would
    // show one the moment it was saved.
    if (left.length > 0 && !left.some((r) => r.primary)) {
      left[0] = { ...left[0], primary: true }
    }
    setFailed(null)
    onRows(left)
  }

  return (
    <>
      <h4 className="sheet-recsub">Phone numbers</h4>

      {rows.length === 0 ? (
        <div className="dim">
          No number yet. He can still be reached by radio, the card or email.
        </div>
      ) : rows.map((r, at) => (
        // KEYED BY POSITION, which is safe here and nowhere near the record's
        // list: these rows hold no input of their own — every value in them
        // lives in `rows` — so a remount on removal costs nothing. The record's
        // rows carry an editor and are keyed by the number's id.
        <div className="listrow sub" key={at}>
          {/* AS IT WAS TYPED, not through `prettyPhone`. Nothing has read this
              number yet, and formatting it here would be the browser making a
              claim about a number the server has not seen. */}
          <span className="mono">{r.phone}</span>
          {r.label && <span className="tag">{r.label}</span>}
          {r.primary && <span className="tag">main</span>}
          <span className="spacer" />
          {/* WHETHER A TEXT GOES TO IT IS ON BROADCAST, exactly as on the
              record — `NewTextTicks` lists these same rows. One control for
              one fact, and the same control in both dialogs. */}
          {!r.primary && (
            <button
              className="ghost tiny"
              type="button"
              onClick={() => onRows(rows.map((row, i) => (
                { ...row, primary: i === at })))}
            >
              Make main
            </button>
          )}
          <button
            className="ghost tiny"
            type="button"
            onClick={() => remove(at)}
          >
            Remove
          </button>
        </div>
      ))}

      {/* Above the bar, where the words that were refused still are. */}
      {failed && <div className="notice" role="alert">{failed}</div>}

      {full ? (
        <p className="footnote warn">
          Ten numbers is as many as one registration takes. Add the rest on his
          record once he is on the roster.
        </p>
      ) : (
        /* Enter adds the number rather than submitting the form: these boxes
           sit inside the registration's own <form>, so without this Enter
           would register the member and take the half-typed number with it. */
        <div className="callbar" onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          e.preventDefault()
          add()
        }}>
          <PhoneInput
            wrapClass="grow"
            value={draft.phone}
            maxLength={40}
            placeholder="Another number"
            aria-label="Another number"
            onChange={(e) => onDraft({ ...draft, phone: e.target.value })}
          />
          <input
            value={draft.label}
            maxLength={40}
            placeholder="mobile, home, the shop"
            aria-label="What to call it"
            onChange={(e) => onDraft({ ...draft, label: e.target.value })}
          />
          {/* NO TEXT TICK ON THIS BAR, like the record's. A row joins the list
              taking the text — `BLANK_NUMBER` is `notify: true`, which the
              create body sends as "not stated" — and Broadcast is where that
              is turned off. */}
          <button
            className="ghost tiny nogrow"
            type="button"
            disabled={!draft.phone.trim()}
            onClick={() => add()}
          >
            Add
          </button>
        </div>
      )}
    </>
  )
}

/** WHICH OF THE NUMBERS BEING TYPED TAKE THE TEXT — the registration's half of
 *  `TextTicks`, over the same rows `NewNumbers` holds.
 *
 *  TWO COMPONENTS BECAUSE THEY WRITE DIFFERENTLY, which is the same split
 *  `Numbers` and `NewNumbers` already have: one calls a route the moment it is
 *  pressed and the other edits a list that goes into the create body. What they
 *  must not differ about is what a tick MEANS, and they do not — both set
 *  `notify` on one number, and the create body walks these rows through
 *  `members.add_number`, which is the route the record's tick patches.
 *
 *  NO "could not be read" STATE HERE, AND NOT BY OMISSION. That is
 *  `app/phones.py`'s verdict on a number it has normalised, and nothing has
 *  read these yet — a browser deciding for itself would be the phone-book bug
 *  in the place it is hardest to see. A number this form cannot read goes on
 *  unticked whatever this list says, and his record is where that shows, which
 *  is why a registration that comes back holding one opens it. */
function NewTextTicks({ rows, onRows }: {
  rows: NewNumber[]
  onRows: (next: NewNumber[]) => void
}) {
  return (
    <>
      <h4 className="sheet-recsub">Text messages</h4>

      {rows.length === 0 ? (
        <div className="dim">
          No number yet, so no text can reach him. Add one on Personal
          information → Numbers; he can still be reached by radio, the
          card or email.
        </div>
      ) : rows.map((r, at) => (
        // KEYED BY POSITION, safe for the reason `NewNumbers` gives: these rows
        // hold no state of their own, so a remount costs nothing.
        <div className="listrow sub" key={at}>
          {/* AS IT WAS TYPED, not through `prettyPhone` — see `NewNumbers`. */}
          <span className="mono">{r.phone}</span>
          {r.label && <span className="tag">{r.label}</span>}
          {r.primary && <span className="tag">main</span>}
          <span className="spacer" />
          <label className="check">
            <input
              type="checkbox"
              checked={r.notify}
              onChange={() => onRows(rows.map((row, i) => (
                i === at ? { ...row, notify: !row.notify } : row)))}
            />
            Gets the text
          </label>
        </div>
      ))}
    </>
  )
}

/** How near an expiry has to be before the record says so.
 *
 *  A DISPLAY CHOICE AND NOT A RULE, which is the only reason a number may be
 *  written down here at all. Whether a card HAS lapsed is `expired`, decided in
 *  SQL against today in the organization's timezone, and the figure in the
 *  label is the server's own `days_left`. This decides nothing except whether a
 *  label is drawn — nothing is flagged, nobody is taken out of a broadcast, and
 *  changing it cannot make the record and the roster disagree.
 *
 *  `member_certifications` expects a screen to do this. It reads `expires_on`
 *  as the LAST valid day on purpose, so that nobody is marked lapsed on a day
 *  their own certificate says they are qualified — "there is nothing to be
 *  gained by lapsing somebody early" — and warning in the weeks before is what
 *  makes that safe rather than merely late. */
const EXPIRING_SOON_DAYS = 60

/** A holding being renewed or corrected, and what has been typed so far. */
type CertDraft = {
  id: string
  obtained_on: string
  expires_on: string
  note: string
}

/** What a member is trained to do, and whether it is still valid today.
 *
 *  THE EXPIRY DECISION IS NEVER MADE HERE. `expired` and `days_left` come out
 *  of the database beside the row, against today in the ORGANIZATION's
 *  timezone — a browser comparing dates against its own clock would lapse a
 *  Montreal card from seven the evening before, and the roster column and this
 *  list would then disagree about whether somebody is qualified. The escalation
 *  queue already paid for that once, when the front end recomputed a window and
 *  a coordinator who set five minutes got the hard-coded seven.
 *
 *  A LAPSED ONE READS AS LAPSED IN WORDS, not in colour. The colour is on top
 *  of the word rather than instead of it — the call the Channels cell makes
 *  with its letters, and for the same reason: a coloured mark says nothing to
 *  somebody who cannot tell the colours apart, and an expired card that still
 *  reads as current is the exact failure tracking training exists to prevent.
 *
 *  THE RECORD'S SHAPE IS WHAT CARRIES THE CONTROLS. `held` is the fuller list
 *  from `GET /api/members/{id}`, which only a role with `settings.members`
 *  fetches; without it this renders the roster row's own compact list — name,
 *  expiry, lapsed — which is a subset of the same shape, so a read-only role
 *  sees the qualifications and none of the controls. That is the correct
 *  outcome rather than a degraded one: the certificate number in `note` is
 *  deliberately off the ungated listing.
 *
 *  THEY WRITE IMMEDIATELY, like the numbers above, so they say so. Adding,
 *  renewing and removing each commit on their own and are outside the dialog's
 *  `snapshot` — Cancel puts none of it back.
 *
 *  A REFUSAL IS SHOWN AT THE CONTROL THAT WAS PRESSED. The server's sentences
 *  here are the ones most worth reading — "'EMT-B' has been retired, so it
 *  cannot be added to anybody new", "Edit the dates on the one he has — a
 *  renewal is a change to that record, not a second copy of it" — and each one
 *  is about a row or about the bar, so it is drawn there rather than at the top
 *  of a body that scrolls.
 */
function Certifications({
  member, held, mayEdit, busy, setBusy, onHoldings,
}: {
  member: Member
  held: MemberCertificationRecord[] | null
  mayEdit: boolean
  busy: boolean
  setBusy: (on: boolean) => void
  onHoldings: (rows: MemberCertificationRecord[]) => void
}) {
  /** What the organization issues, for the picker. `null` while it is on the
   *  wire, `[]` once we know there are none — never the same value, or a
   *  failure leaves a permanent "Loading…" that cannot be told from a broken
   *  screen. Named for what it holds rather than `catalogue`, which at the top
   *  of this file is the function that builds the sheet's columns. */
  const [issued, setIssued] = useState<Certification[] | null>(null)
  const [pick, setPick] = useState('')
  const [obtained, setObtained] = useState('')
  const [expires, setExpires] = useState('')
  const [note, setNote] = useState('')
  const [editing, setEditing] = useState<CertDraft | null>(null)
  const [failed, setFailed] = useState<Failure | null>(null)

  // ONLY WHEN THERE IS SOMETHING TO PICK FROM. The list is ungated to read, but
  // the picker is the only thing that reads it here, and it is drawn only for a
  // role that may write — so a read-only record makes no request at all.
  useEffect(() => {
    if (!mayEdit) return
    let live = true
    api.certifications()
      .then((rows) => { if (live) setIssued(rows) })
      .catch((e) => {
        if (!live) return
        setIssued([])
        setFailed({
          at: ADDING,
          message: e instanceof ApiError
            ? e.message : 'Could not read the list of certifications.',
        })
      })
    return () => { live = false }
  }, [mayEdit])

  function said(at: string, err: unknown, fallback: string) {
    setFailed({ at, message: err instanceof ApiError ? err.message : fallback })
  }

  async function attach() {
    if (!pick) return
    setBusy(true)
    setFailed(null)
    try {
      onHoldings(await api.memberCertificationAdd(member.id, {
        certification_id: pick,
        obtained_on: obtained || null,
        expires_on: expires || null,
        note: note.trim() || null,
      }))
      // Cleared INSIDE the write, so a refusal leaves the dates and the
      // certificate number in the boxes to be corrected.
      setPick('')
      setObtained('')
      setExpires('')
      setNote('')
    } catch (err) {
      said(ADDING, err, 'Could not add that certification.')
    } finally {
      setBusy(false)
    }
  }

  /** A renewal, or a date typed into the wrong box.
   *
   *  ONLY WHAT CHANGED IS SENT. A body with nothing in it comes back 400 —
   *  "There is nothing to change" — which is a refusal for pressing Save on a
   *  row nobody edited, so that case closes the editor instead. The same rule
   *  the number rows follow. */
  async function saveEdit(row: MemberCertificationRecord) {
    if (!editing) return
    const changes: {
      obtained_on?: string | null; expires_on?: string | null
      note?: string | null
    } = {}
    if (editing.obtained_on !== (row.obtained_on ?? '')) {
      changes.obtained_on = editing.obtained_on || null
    }
    if (editing.expires_on !== (row.expires_on ?? '')) {
      changes.expires_on = editing.expires_on || null
    }
    if (editing.note.trim() !== (row.note ?? '')) {
      changes.note = editing.note.trim() || null
    }
    if (Object.keys(changes).length === 0) { setEditing(null); return }
    setBusy(true)
    setFailed(null)
    try {
      onHoldings(await api.memberCertificationUpdate(member.id, row.id, changes))
      // Closed INSIDE the write: "that certification would expire before it was
      // obtained" is answered by correcting a date that has to still be there.
      setEditing(null)
    } catch (err) {
      said(row.id, err, 'Could not change that certification.')
    } finally {
      setBusy(false)
    }
  }

  async function detach(row: MemberCertification) {
    if (!confirm(`Take ${row.name} off ${member.label}? It is a statement `
      + 'about what he can do now, so removing it leaves nothing behind. Add '
      + 'it again if he holds it.')) return
    setBusy(true)
    setFailed(null)
    try {
      onHoldings(await api.memberCertificationRemove(member.id, row.id))
    } catch (err) {
      said(row.id, err, 'Could not remove that certification.')
    } finally {
      setBusy(false)
    }
  }

  // THE SERVER'S ORDER, KEPT — BUT THERE ARE TWO OF THEM, AND WHICH ONE THIS
  // IS DEPENDS ON WHERE THE ROWS CAME FROM.
  //
  // `of_member` — the gated record read and the three holding writes — orders
  // lapsed first, then soonest to expire, because that is the row that changes
  // who gets sent. `ROSTER_CERTIFICATIONS`, on the ungated listing, orders by
  // the catalogue's own `sort_order`, because a sheet whose cells re-sort per
  // row is unreadable.
  //
  // So `held` is lapsed-first and `member.certifications` is catalogue order,
  // and this `??` falls back to the second — which is what a role WITHOUT
  // `settings.members` sees for the whole life of the dialog, since the fetch
  // returns early for them. The expired pill is drawn either way, so the
  // warning is present in both and only its POSITION differs. Sorting again
  // here would be a THIRD ordering, to disagree with the other two.
  const shown: MemberCertification[] = held ?? member.certifications
  const alreadyHeld = new Set(shown.map((c) => c.certification_id))
  // A RETIRED ONE IS NOT OFFERED AND NEITHER IS ONE HE ALREADY HOLDS. The
  // server refuses both in a sentence; a picker that offered them would be a
  // control drawn and then refused for something the screen already knew.
  const offer = (issued ?? []).filter(
    (c) => c.active && !alreadyHeld.has(c.id))

  return (
    <>
      {/* The four words the numbers block carries, and for the identical
          reason: these rows are outside `snapshot`, so `useDialogGuard` would
          offer to discard "unsaved changes" over a card that is already
          stored. */}
      <h4 className="sheet-recsub">
        Certifications <span className="chans">saved as you go</span>
      </h4>

      {shown.length === 0 ? (
        <div className="dim">Nothing recorded against this member.</div>
      ) : shown.map((c) => {
        // The record's row for this holding, when the record has arrived. It
        // is what carries the dates and the note the editor writes, so its
        // absence is what makes this list read-only for a role that only has
        // the roster row.
        const row = held?.find((h) => h.id === c.id)
        const draft = editing && row && editing.id === c.id ? editing : null
        const soon = row && !c.expired && row.days_left !== null
          && row.days_left <= EXPIRING_SOON_DAYS
        return (
          <Fragment key={c.id}>
            {draft && row ? (
              <div
                // `.callbar` for the input styling; the margin is dropped
                // because this IS a row in the list rather than a bar above
                // one, and 14px under it reads as the end of the section.
                className="callbar"
                style={{ marginBottom: 0, padding: '4px 0' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // These boxes sit inside the record's own <form>, so
                    // without this Enter saves the member and closes the
                    // dialog with the correction still in the box.
                    e.preventDefault()
                    saveEdit(row)
                    return
                  }
                  if (e.key !== 'Escape') return
                  // ESCAPE MUST NOT REACH THE WINDOW, where `useDialogGuard`
                  // listens: the key that means "forget this correction" would
                  // close the whole record instead.
                  e.stopPropagation()
                  setEditing(null)
                }}
              >
                <span className="nm">{c.name}</span>
                <span className="lbl">obtained</span>
                <input
                  type="date"
                  value={draft.obtained_on}
                  aria-label={`When ${c.name} was obtained`}
                  onChange={(e) => setEditing({ ...draft, obtained_on: e.target.value })}
                />
                <span className="lbl">expires</span>
                <input
                  type="date"
                  value={draft.expires_on}
                  autoFocus
                  aria-label={`When ${c.name} expires`}
                  onChange={(e) => setEditing({ ...draft, expires_on: e.target.value })}
                />
                <input
                  className="grow"
                  value={draft.note}
                  maxLength={500}
                  placeholder="Certificate number, awarding body…"
                  aria-label="Certificate number or note"
                  onChange={(e) => setEditing({ ...draft, note: e.target.value })}
                />
                <button
                  className="ghost tiny nogrow"
                  type="button"
                  disabled={busy}
                  onClick={() => saveEdit(row)}
                >
                  Save
                </button>
                <button
                  className="ghost tiny nogrow"
                  type="button"
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="listrow sub">
                <span className="nm">{c.name}</span>
                {/* THE WORD FIRST. `expired` is the server's flag; the amber is
                    the same amber a number nothing can text gets, and for the
                    same shape of failure — he looks qualified and is not. */}
                {c.expired && <span className="pill wait">expired</span>}
                {soon && row && (
                  <span className="pill wait">
                    {row.days_left === 0 ? 'last day' : `${row.days_left} days left`}
                  </span>
                )}
                {/* He keeps it; the organization stopped issuing it. That
                    asymmetry is the whole point of retiring rather than
                    deleting a certification. */}
                {row && !row.certification_active && (
                  <span className="tag">no longer issued</span>
                )}
                {row?.note && <span className="tag">{row.note}</span>}
                <span className="spacer" />
                <span>
                  {c.expires_on
                    ? <>expires {calendarDate(c.expires_on)}</>
                    : <span className="muted">does not expire</span>}
                </span>
                {mayEdit && row && (
                  <button
                    className="ghost tiny"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setFailed(null)
                      setEditing({
                        id: row.id,
                        obtained_on: row.obtained_on ?? '',
                        expires_on: row.expires_on ?? '',
                        note: row.note ?? '',
                      })
                    }}
                  >
                    Edit
                  </button>
                )}
                {mayEdit && row && (
                  <button
                    className="ghost tiny"
                    type="button"
                    disabled={busy}
                    onClick={() => detach(c)}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
            {failed?.at === c.id && (
              <div className="notice" role="alert">{failed.message}</div>
            )}
          </Fragment>
        )
      })}

      {failed?.at === ADDING && (
        <div className="notice" role="alert">{failed.message}</div>
      )}

      {/* Drawn only for a role that may write, and only once the list has
          arrived: a picker with nothing in it while the request is in flight
          reads as an organization that trains nobody. */}
      {mayEdit && issued !== null && (
        issued.length === 0 ? (
          <p className="dim">
            No certifications on the list yet. Make them in Settings →
            Certifications, and they can then be recorded against a member.
          </p>
        ) : (
          <div className="callbar" onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            attach()
          }}>
            {/* `select.pick` and NOT wrapped in a label: `.modal label select`
                makes a select a full-width block, which is right for a stacked
                form and wrong for a bar. */}
            <select
              className="pick"
              value={pick}
              aria-label="Certification to record"
              onChange={(e) => setPick(e.target.value)}
            >
              <option value="">Add a certification…</option>
              {offer.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {/* A DATE INPUT CANNOT CARRY A PLACEHOLDER, so the caption is a
                real word beside the box rather than hover text — two unlabelled
                date boxes side by side is exactly the pair somebody fills in
                the wrong way round, and the server refuses that with a sentence
                after the fact. */}
            <span className="lbl">obtained</span>
            <input
              type="date"
              value={obtained}
              aria-label="Date obtained"
              onChange={(e) => setObtained(e.target.value)}
            />
            <span className="lbl">expires</span>
            <input
              type="date"
              value={expires}
              aria-label="Expiry date"
              onChange={(e) => setExpires(e.target.value)}
            />
            <input
              value={note}
              maxLength={500}
              placeholder="Certificate number, awarding body…"
              aria-label="Certificate number or note"
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              className="ghost tiny nogrow"
              type="button"
              disabled={busy || !pick}
              onClick={() => attach()}
            >
              Add
            </button>
          </div>
        )
      )}
    </>
  )
}

/** One qualification chosen while a member is being registered.
 *
 *  `name` RIDES ALONG WITH THE ID, and that is not decoration: an attach that
 *  fails has to be reported BY NAME, and by then this dialog and the catalogue
 *  it read are gone. */
type NewCert = {
  certification_id: string
  name: string
  obtained_on: string
  expires_on: string
  note: string
}

const BLANK_CERT: NewCert = {
  certification_id: '', name: '', obtained_on: '', expires_on: '', note: '',
}

/** What a member is trained to do, chosen while he is being registered.
 *
 *  THEY CANNOT GO IN THE CREATE BODY, AND THERE IS NO STAGING AREA FOR THEM.
 *  A holding hangs off a member id and `MemberIn` has no field for one, so
 *  these are held here and attached through
 *  `POST /api/members/{id}/certifications` — the real route — the moment the
 *  member exists. The registration succeeds or fails on its own; an attach
 *  that fails afterwards leaves a member who IS registered, and the screen
 *  says exactly which cards did not go on rather than showing a failure that
 *  reads as though nothing was created. That is `complete_run`'s lesson in
 *  CLAUDE.md: a summary that cannot tell what was already done from what was
 *  never done is confidently wrong at the moment somebody is deciding what to
 *  do next.
 *
 *  NOTHING ABOUT EXPIRY IS DECIDED HERE. `expired` and `days_left` are
 *  computed in SQL against today in the ORGANIZATION's timezone, and none of
 *  these rows exists on the server yet — so there is no lapsed pill on this
 *  list, and the record is where a card that is already out of date shows as
 *  one. A browser comparing dates against its own clock would be a second
 *  definition of "lapsed" beside the one the roster column reads.
 */
function NewCertifications({ rows, onRows, draft, onDraft }: {
  rows: NewCert[]
  onRows: (next: NewCert[]) => void
  draft: NewCert
  onDraft: (next: NewCert) => void
}) {
  /** What the organization issues. `null` while it is on the wire, `[]` once we
   *  know there are none — never the same value, or a failure leaves a
   *  permanent "Loading…" that cannot be told from a broken screen. */
  const [issued, setIssued] = useState<Certification[] | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  // UNGATED TO READ, like the nature-of-call list — and unlike the record's
  // version there is no `mayEdit` to check first: this dialog is only opened
  // by a role holding `settings.members`.
  useEffect(() => {
    let live = true
    api.certifications()
      .then((list) => { if (live) setIssued(list) })
      .catch((e) => {
        if (!live) return
        setIssued([])
        setFailed(e instanceof ApiError
          ? e.message : 'Could not read the list of certifications.')
      })
    return () => { live = false }
  }, [])

  function attach() {
    const picked = (issued ?? []).find((c) => c.id === draft.certification_id)
    if (!picked) return
    setFailed(null)
    onRows([...rows, { ...draft, name: picked.name, note: draft.note.trim() }])
    onDraft({ ...BLANK_CERT })
  }

  // A RETIRED ONE IS NOT OFFERED AND NEITHER IS ONE ALREADY ON THE LIST. The
  // server refuses both in a sentence, and a picker that offered them would be
  // a control drawn and then refused for something the screen already knew —
  // here it would be refused AFTER the member had been created, which is the
  // worst moment for it.
  const offer = (issued ?? []).filter(
    (c) => c.active && !rows.some((r) => r.certification_id === c.id))

  return (
    <>
      <h4 className="sheet-recsub">Certifications</h4>

      {rows.length === 0 ? (
        <div className="dim">Nothing recorded against him yet.</div>
      ) : rows.map((r) => (
        <div className="listrow sub" key={r.certification_id}>
          <span className="nm">{r.name}</span>
          {r.note && <span className="tag">{r.note}</span>}
          <span className="spacer" />
          <span>
            {r.expires_on
              ? <>expires {calendarDate(r.expires_on)}</>
              : <span className="muted">does not expire</span>}
          </span>
          <button
            className="ghost tiny"
            type="button"
            onClick={() => onRows(rows.filter(
              (x) => x.certification_id !== r.certification_id))}
          >
            Remove
          </button>
        </div>
      ))}

      {failed && <div className="notice" role="alert">{failed}</div>}

      {/* Only once the list has arrived: a picker with nothing in it while the
          request is in flight reads as an organization that trains nobody. */}
      {issued !== null && (
        issued.length === 0 ? (
          // AND ONLY WHEN THE LIST WAS ACTUALLY READ. A failed read sets `[]`
          // as well -- it has to, or the block sits on "Loading…" for ever --
          // so without this the sentence under the refusal would contradict it,
          // and "no certifications on the list yet" is a fact rather than a
          // failure. The refusal above is the whole of what can be said.
          failed ? null : (
            <p className="dim">
              No certifications on the list yet. Make them in Settings →
              Certifications, and they can then be recorded against a member.
            </p>
          )
        ) : (
          <div className="callbar" onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            attach()
          }}>
            {/* `select.pick` and NOT wrapped in a label: `.modal label select`
                makes a select a full-width block, which is right for a stacked
                form and wrong for a bar. */}
            <select
              className="pick"
              value={draft.certification_id}
              aria-label="Certification to record"
              onChange={(e) => onDraft(
                { ...draft, certification_id: e.target.value })}
            >
              <option value="">Add a certification…</option>
              {offer.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {/* A DATE INPUT CANNOT CARRY A PLACEHOLDER, so the caption is a
                real word beside the box — two unlabelled date boxes side by
                side is exactly the pair somebody fills in the wrong way round,
                and here the server's refusal would arrive after the member had
                already been created. */}
            <span className="lbl">obtained</span>
            <input
              type="date"
              value={draft.obtained_on}
              aria-label="Date obtained"
              onChange={(e) => onDraft({ ...draft, obtained_on: e.target.value })}
            />
            <span className="lbl">expires</span>
            <input
              type="date"
              value={draft.expires_on}
              aria-label="Expiry date"
              onChange={(e) => onDraft({ ...draft, expires_on: e.target.value })}
            />
            <input
              value={draft.note}
              maxLength={500}
              placeholder="Certificate number, awarding body…"
              aria-label="Certificate number or note"
              onChange={(e) => onDraft({ ...draft, note: e.target.value })}
            />
            <button
              className="ghost tiny nogrow"
              type="button"
              disabled={!draft.certification_id}
              onClick={() => attach()}
            >
              Add
            </button>
          </div>
        )
      )}
    </>
  )
}

/** What each `kind` is called on screen.
 *
 *  THE SERVER OWNS THE LIST, not this. `member_documents.KINDS` sits beside the
 *  CHECK that enforces it, and a value this screen offered and the constraint
 *  refused would reach somebody as a 400 for a choice the product gave them.
 *  A kind arriving from the server that is not here renders as itself rather
 *  than as a blank. */
const DOCUMENT_KINDS = [
  { key: 'identification', label: 'Identification' },
  { key: 'other', label: 'Other' },
] as const

function kindLabel(kind: string): string {
  return DOCUMENT_KINDS.find((k) => k.key === kind)?.label ?? kind
}

/** The scans held against a member — what the customer asked for: "we should
 *  be able to upload identification card that is required for legal purposes."
 *
 *  THE WHOLE SECTION IS BEHIND `settings.member_documents` AND IS NOT DRAWN
 *  WITHOUT IT — not a disabled control, not an empty heading. A role without
 *  the key must not learn that this member has three documents on file, which
 *  a greyed-out list would tell them; and a heading over nothing is a control
 *  drawn and then refused, which CLAUDE.md records as worse than no control.
 *
 *  IT SITS OUTSIDE THE RECORD'S `<fieldset disabled>`, DELIBERATELY. That
 *  fieldset is locked by `settings.members`, and these are two different keys
 *  on purpose: correcting a typo in a callsign and opening a photograph of
 *  somebody's passport are not the same privilege. Inside it, a role holding
 *  the documents key and not the roster key would get every control here drawn
 *  and inert, with nothing on screen to say why.
 *
 *  EVERY WRITE HAS A CATCH THAT PUTS THE SERVER'S SENTENCE INSIDE THIS BLOCK.
 *  The refusals here are the ones worth reading — the file is not a JPEG, a
 *  PNG or a PDF; it is already on this member; SECRET_KEY has changed and the
 *  content cannot be decrypted — and the record's own notice is pinned to the
 *  top of a body that scrolls, which is off screen from here.
 */
function Documents({ member, onCount }: {
  member: Member
  /** HOW MANY ARE ON FILE, FOR THE TAB ABOVE. The count is reported up rather
   *  than the list being lifted into the dialog: this component owns the fetch,
   *  the upload and the delete, and moving the list up so a badge could count
   *  it would put a second holder of "what is on file" in the dialog for every
   *  one of them to keep in step.
   *
   *  `null` MEANS NOT KNOWN YET AND IS NOT THE SAME AS NONE. A `0` on the tab
   *  while the request is in flight is a confident answer to the question the
   *  badge exists to answer, and the answer is wrong. */
  onCount: (n: number | null) => void
}) {
  /** `null` while it is still on the wire, `[]` once we know there are none.
   *
   *  THE TWO MUST NOT BE THE SAME VALUE. A catch that set this back to `null`
   *  would leave a permanent "Loading…" — which is exactly how a denied role
   *  used to read the caller-history panel: a spinner that never finished,
   *  indistinguishable from a broken screen. A failure sets `[]` AND the
   *  sentence beside it. */
  const [docs, setDocs] = useState<MemberDocument[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [chosen, setChosen] = useState<File | null>(null)
  const [kind, setKind] = useState<'identification' | 'other'>('identification')
  const [note, setNote] = useState('')
  const picker = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // The record can be swapped for another member under this component, so
    // the answer to a request for the previous one must not land in the new
    // one's list.
    let live = true
    api.memberDocuments(member.id)
      .then((rows) => { if (live) { setDocs(rows); setError(null) } })
      .catch((e) => {
        if (!live) return
        setDocs([])
        setError(e instanceof ApiError
          ? e.message : 'Could not read the documents on file.')
      })
    return () => { live = false }
  }, [member.id])

  // ONE PLACE THAT REPORTS THE COUNT, downstream of every write. `docs` is
  // already what the fetch, the upload and the delete all end at, so a badge
  // fed from here cannot go stale behind one of them — which it would if each
  // of the three called `onCount` itself and one of them was forgotten.
  useEffect(() => {
    onCount(docs === null ? null : docs.length)
  }, [docs, onCount])

  async function upload() {
    if (!chosen) return
    setBusy(true)
    setError(null)
    try {
      const filed = await api.memberDocumentUpload(
        member.id, chosen, kind, note.trim() || null)
      // Newest first is the server's order, so a new one goes on top.
      setDocs((prev) => [filed, ...(prev ?? [])])
      // Cleared INSIDE the write, like the add-a-number bar above: a refusal
      // has to leave the file picked and the note typed, because "that is not
      // a JPEG, a PNG or a PDF" is answered by picking a different file, and
      // "it is already on this member" by not picking one at all.
      setChosen(null)
      setNote('')
      // The DOM node holds the selection independently of React state, and a
      // stale value in it means picking the SAME file again fires no change
      // event at all — so a re-scan of a document that was just refused would
      // silently do nothing.
      if (picker.current) picker.current.value = ''
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not file that document.')
    } finally {
      setBusy(false)
    }
  }

  /** Fetch, then hand the bytes to the browser as a download.
   *
   *  THE NAME COMES FROM THE LISTING, not from `Content-Disposition`. It is the
   *  same string the server puts in that header, and parsing the header here
   *  would be a second answer in the browser to what a document is called —
   *  `member_documents._clean_filename` already decided that once, on the way
   *  in. */
  async function openDocument(doc: MemberDocument) {
    setBusy(true)
    setError(null)
    try {
      const blob = await api.memberDocumentOpen(doc.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.filename
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open that document.')
    } finally {
      setBusy(false)
    }
  }

  /** NAMED IN THE PROMPT, which says it cannot be undone and asks why.
   *
   *  There is no retire-instead-of-delete here, unlike everywhere else in this
   *  file: the row and the ciphertext both go. What survives is the audit
   *  entry, which keeps what was held and who removed it — so the record that
   *  the document existed outlives the document. Somebody about to destroy the
   *  only copy of a volunteer's identity card has to be told which one. */
  async function remove(doc: MemberDocument) {
    // ONE PROMPT, NOT A CONFIRM THEN A PROMPT. It names the file, says the
    // destruction is final, and collects the reason in the same breath —
    // Cancel returns null and nothing happens, so the question that stops the
    // deletion and the question that records it are the same keypress.
    //
    // `audit_log.reason` is the only part of this trail nobody can reconstruct
    // later: what was held, who removed it and when all survive on the row.
    const reason = prompt(
      `Delete ${doc.filename} from ${member.label}?\n\n`
      + 'The document itself is destroyed and cannot be recovered — only '
      + 'the record that it was held, and who removed it, survives.\n\n'
      + 'Why is it being deleted?')
    if (reason === null) return
    setBusy(true)
    setError(null)
    try {
      const gone = await api.memberDocumentDelete(doc.id, reason.trim() || null)
      setDocs((prev) => (prev ?? []).filter((d) => d.id !== gone.id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not delete that document.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* THE FOUR WORDS ITS THREE NEIGHBOURS CARRY, for the identical
          reason and with more at stake. `upload()` POSTs and `remove()`
          DELETEs on the button press, and neither is in `snapshotOf` -- so the
          dialog's own guard offers to "discard your unsaved changes" after a
          passport scan has already been encrypted, stored and audited, or
          already destroyed by a Delete the same screen says cannot be undone.
          Somebody who uploads one, thinks better of it and accepts the discard
          believes it went with the rest. It did not. */}
      <h4 className="sheet-recsub">
        Documents <span className="chans">saved as you go</span>
      </h4>
      {/* THE ONE SENTENCE ADDED WHILE OTHERS CAME OFF THIS SCREEN, and it earns
          its place: every open of a document writes an `audit_log` row against
          the name of whoever opened it, in the same transaction as the
          decryption. Being audited without being told is the part that would
          not be fair to the person doing the looking, and it is the one fact
          here that cannot be inferred from any control. */}
      <p className="footnote">
        Opening or deleting one is recorded against your name.
      </p>

      {error && <div className="notice" role="alert">{error}</div>}

      {docs === null ? (
        <div className="dim">Reading what is on file…</div>
      ) : docs.length === 0 ? (
        <div className="dim">Nothing on file for this member.</div>
      ) : docs.map((doc) => (
        <div className="listrow sub" key={doc.id}>
          {/* A FLEX ITEM WILL NOT SHRINK BELOW ITS CONTENT unless it is told
              to, and `.clip` is scoped to `table.sheet` and `.maptable` so
              there is nothing to borrow here. A filename runs to 200
              characters on the server, and one long unbroken one would push
              this row wider than the dialog rather than wrapping inside it. */}
          <span className="nm" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {doc.filename}
          </span>
          <span className="tag">{kindLabel(doc.kind)}</span>
          <span className="tag">{fileSize(doc.byte_size)}</span>
          {doc.note && <span className="tag">{doc.note}</span>}
          <span className="spacer" />
          {/* WHO AND WHEN, from `uploaded_by_label` and never from the id: the
              id is ON DELETE SET NULL, so a document filed by somebody whose
              login has since been removed would otherwise read as nobody's.
              `day` is right for a timestamptz and drops the year, so the exact
              instant is on the hover — for a document kept for legal reasons
              which YEAR it was filed is part of the record.

              `.tag` AND NOT `.lbl`. Both are dim, but `.lbl` is uppercase mono
              with wide letter-spacing, which is right for `+2` and `coord` and
              wrong for a person's name — it would render whoever filed this as
              a callsign. */}
          <span
            className="tag"
            title={new Date(doc.uploaded_at).toLocaleString()}
          >
            {doc.uploaded_by_label ?? 'unknown'} · {day(doc.uploaded_at)}
          </span>
          <button
            className="ghost tiny"
            type="button"
            disabled={busy}
            onClick={() => openDocument(doc)}
          >
            Open
          </button>
          <button
            className="ghost tiny"
            type="button"
            disabled={busy}
            onClick={() => remove(doc)}
          >
            Delete
          </button>
        </div>
      ))}

      {/* Enter must not reach the record's own <form>: these boxes sit inside
          it, so without this Enter saves the member and closes the dialog with
          a file picked and never uploaded. The same guard is on the two number
          bars above. */}
      <div className="callbar" onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        upload()
      }}>
        {/* `accept` IS A HINT TO THE FILE PICKER AND NOTHING MORE. What a file
            actually is gets decided on the server from its magic bytes, never
            from its extension and never from the browser's Content-Type — so
            this narrows the dialog somebody browses in and is not relied on. */}
        <input
          className="grow"
          type="file"
          ref={picker}
          accept="image/jpeg,image/png,application/pdf"
          aria-label="A scan to file"
          disabled={busy}
          onChange={(e) => setChosen(e.target.files?.[0] ?? null)}
        />
        {/* `select.pick` and NOT WRAPPED IN A LABEL, the same pair of decisions
            the group bar in `MemberGroups.tsx` makes and for the same two
            reasons: `.field` and `.callbar` style input and textarea and NOT
            select, so a bare one renders with the browser's own chrome — a
            white box on the dark theme — and `.modal label select` makes it a
            full-width block, which is right for a stacked form and wrong for a
            bar. */}
        <select
          className="pick"
          value={kind}
          aria-label="What this document is"
          disabled={busy}
          onChange={(e) => setKind(e.target.value as 'identification' | 'other')}
        >
          {DOCUMENT_KINDS.map((k) => (
            <option key={k.key} value={k.key}>{k.label}</option>
          ))}
        </select>
        <input
          value={note}
          maxLength={500}
          placeholder="Note (optional)"
          aria-label="Note"
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          className="ghost tiny nogrow"
          type="button"
          disabled={busy || !chosen}
          onClick={() => upload()}
        >
          {busy ? 'Working…' : 'Upload'}
        </button>
      </div>
    </>
  )
}

/** One scan chosen while a member is being registered — the FILE ITSELF, held
 *  in the browser until there is a member to file it against. */
type NewDocument = {
  file: File
  kind: 'identification' | 'other'
  note: string
}

/** The add bar's half of the same thing, before a file has been picked. */
type NewDocumentDraft = {
  file: File | null
  kind: 'identification' | 'other'
  note: string
}

const BLANK_DOCUMENT: NewDocumentDraft = {
  file: null, kind: 'identification', note: '',
}

/** The scans to file against a member who does not exist yet.
 *
 *  THIS TAB USED NOT TO EXIST ON THE REGISTRATION, AND THE CUSTOMER ASKED FOR
 *  IT IN AS MANY WORDS: "When I want to register a member, it's missing the
 *  section of uploading files, the registration page, and the editing page
 *  from existing member should be the same. Identical." The two dialogs are
 *  identical BY REQUEST, and this is the block that was missing.
 *
 *  THE FILE IS HELD IN THE BROWSER, NOT STAGED ON THE SERVER, and that is the
 *  decision this comment exists to explain. `member_documents.member_id` is
 *  NOT NULL and points at a member, so there is nothing to attach a scan to
 *  until the create returns — the alternatives were a staging table or an
 *  upload endpoint with no member, and both mean the encrypted contents of
 *  somebody's passport sitting on the server attached to nothing, kept alive
 *  by whatever sweeps up rows nobody claimed. A `File` in a component's state
 *  costs nothing, disappears when the dialog closes, and is uploaded through
 *  the REAL route the moment there is a member id. It is the same shape the
 *  certifications already use for the same reason.
 *
 *  NOTHING HERE WRITES, so nothing here can half-succeed. `AddDialog` uploads
 *  these after the create, one at a time, and a file that will not go up is
 *  named — with the server's own sentence — rather than being reported as a
 *  failed registration. He is on the roster and a broadcast reaches him; a
 *  scan that did not land is a retry, on the record, on this same tab. */
function NewDocuments({ rows, onRows, draft, onDraft }: {
  rows: NewDocument[]
  onRows: (next: NewDocument[]) => void
  draft: NewDocumentDraft
  onDraft: (next: NewDocumentDraft) => void
}) {
  const [failed, setFailed] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  function add() {
    const file = draft.file
    if (!file) return
    // THE ONLY DUPLICATE THIS FORM CAN SEE, and it must not try to see any
    // other: whether two files hold the same IMAGE is the server's answer,
    // from the sha256 it takes of the plaintext, and it refuses a second copy
    // with a sentence. This catches the double-press.
    if (rows.some((r) => r.file.name === file.name && r.file.size === file.size)) {
      setFailed('That file is already on the list.')
      return
    }
    setFailed(null)
    onRows([...rows, { file, kind: draft.kind, note: draft.note.trim() }])
    onDraft({ ...BLANK_DOCUMENT })
    // The DOM node holds the selection independently of React state, and a
    // stale value in it means picking the SAME file again fires no change
    // event at all — so a file removed from the list could not be put back.
    if (picker.current) picker.current.value = ''
  }

  return (
    <>
      {/* THE FOUR WORDS SAY THE OPPOSITE OF THE RECORD'S, which is the one
          thing about this block somebody has to know: nothing is uploaded
          until the member is created, so Cancel really does discard these and
          the dialog's guard counts them. */}
      <h4 className="sheet-recsub">
        Documents <span className="chans">filed when he is registered</span>
      </h4>

      {rows.length === 0 ? (
        <div className="dim">Nothing to file against him yet.</div>
      ) : rows.map((r, at) => (
        // KEYED BY POSITION: these rows hold no state of their own, so a
        // remount on removal costs nothing.
        <div className="listrow sub" key={at}>
          {/* A FLEX ITEM WILL NOT SHRINK BELOW ITS CONTENT unless it is told
              to, and a long unbroken filename would push this row wider than
              the dialog rather than wrapping inside it — the record's list
              learned this the same way. */}
          <span className="nm" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {r.file.name}
          </span>
          <span className="tag">{kindLabel(r.kind)}</span>
          <span className="tag">{fileSize(r.file.size)}</span>
          {r.note && <span className="tag">{r.note}</span>}
          <span className="spacer" />
          <button
            className="ghost tiny"
            type="button"
            onClick={() => { setFailed(null); onRows(rows.filter((_, i) => i !== at)) }}
          >
            Remove
          </button>
        </div>
      ))}

      {/* Above the bar, where the file that was refused is still picked. */}
      {failed && <div className="notice" role="alert">{failed}</div>}

      {/* Enter must not reach the registration's own <form>: these boxes sit
          inside it, so without this Enter registers the member and takes the
          picked file with it. The same guard is on every bar in both
          dialogs. */}
      <div className="callbar" onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        add()
      }}>
        {/* `accept` IS A HINT TO THE FILE PICKER AND NOTHING MORE. What a file
            actually is gets decided on the server from its magic bytes, never
            from its extension and never from the browser's Content-Type — so
            this narrows the dialog somebody browses in and is not relied on.
            Which also means a file refused for its contents is refused AFTER
            the member exists, by name, on the banner. */}
        <input
          className="grow"
          type="file"
          ref={picker}
          accept="image/jpeg,image/png,application/pdf"
          aria-label="A scan to file"
          onChange={(e) => onDraft({ ...draft, file: e.target.files?.[0] ?? null })}
        />
        {/* `select.pick` and NOT wrapped in a label — `.modal label select`
            makes a select a full-width block, which is right for a stacked
            form and wrong for a bar. */}
        <select
          className="pick"
          value={draft.kind}
          aria-label="What this document is"
          onChange={(e) => onDraft(
            { ...draft, kind: e.target.value as 'identification' | 'other' })}
        >
          {DOCUMENT_KINDS.map((k) => (
            <option key={k.key} value={k.key}>{k.label}</option>
          ))}
        </select>
        <input
          value={draft.note}
          maxLength={500}
          placeholder="Note (optional)"
          aria-label="Note"
          onChange={(e) => onDraft({ ...draft, note: e.target.value })}
        />
        <button
          className="ghost tiny nogrow"
          type="button"
          disabled={!draft.file}
          onClick={() => add()}
        >
          Add
        </button>
      </div>
    </>
  )
}

/** Something that could not be attached after the member was created, and
 *  WHICH KIND of thing it was.
 *
 *  `what` DECIDES WHICH TAB HIS RECORD OPENS ON. A certification that would not
 *  go on and a scan that would not upload are both retried in a different
 *  place, and landing on the wrong one is a hunt through four tabs with a
 *  sentence in hand. It is not cosmetic for the documents: that tab is the only
 *  place the upload exists at all. */
type Unattached = { what: 'certification' | 'document'; name: string; why: string }

/** Why a record opened straight out of the registration form, and what is
 *  waiting inside it.
 *
 *  IT IS NOT THE PAGE'S OWN `note`, and it cannot be: the record is a modal
 *  over the whole sheet, so a banner behind it is read only after the dialog
 *  it is explaining has been closed. This one is drawn in the record's own
 *  body, at the moment it applies. */
type Arrival = { message: string; wrong: boolean }

function RecordDialog({
  member, groups, mayEdit, arrival, openOn, onClose, onSaved, onMember,
  onRetired,
}: {
  member: Member
  groups: MemberGroup[]
  mayEdit: boolean
  /** WHICH TAB TO LAND ON, when the thing that opened this record knows.
   *
   *  Only the success banner's "File an identity document" sets it, and
   *  without it that button did exactly what its label says it does not: it
   *  opened the record on `RECORD_OPENS_ON`, the channels and numbers list,
   *  one unmarked click short of the upload -- unmarked because Documents
   *  draws no badge when nothing is filed, which is precisely the case there.
   *  Everything else leaves it unset and gets the default. */
  openOn?: TabKey
  /** Set only when this record was opened by finishing a registration — see
   *  `Arrival`. Absent when the dialog was opened from a row, which is every
   *  other way in. */
  arrival?: Arrival | null
  onClose: () => void
  onSaved: (m: Member) => void
  /** Something that writes on its own changed — a number, or a certification.
   *  The dialog stays open, because attaching either is a run of small writes
   *  and closing after each one would be unusable, so the row behind it is
   *  patched in place instead.
   *
   *  The numbers routes answer with the whole member. The certification routes
   *  answer with the member's holdings, which are a SUPERSET of what the roster
   *  row carries, so the row is patched with them exactly rather than being
   *  guessed at or re-fetched. */
  onMember: (m: Member) => void
  onRetired: (m: Member) => void
}) {
  const [form, setForm] = useState({
    display_name: member.display_name ?? '',
    callsign: member.callsign ?? '',
    email: member.email ?? '',
    radio_imei: member.radio_imei ?? '',
    vehicle_plate: member.vehicle_plate ?? '',
    has_radio: member.has_radio,
    notify_email: member.notify_email,
    notify_card: member.notify_card,
  })
  const [joined, setJoined] = useState(member.joined_on ?? '')
  const [chosen, setChosen] = useState<string[]>(member.groups.map((g) => g.id))
  /** WHERE A LETTER GOES. `addressFrom` reads the eight columns off the member
   *  — the same eight, with the same names and the same limits, that the caller
   *  record has — which is what lets `AddressFields` be reused here rather than
   *  copied. `stored` is kept beside it so the save can send only what changed;
   *  it is `useRef` and not state because it is what the member ARRIVED with
   *  and must not move when the boxes do. */
  const [address, setAddress] = useState<Address>(() => addressFrom(member))
  const stored = useRef(addressFrom(member))
  /** WHEN HE WAS BORN AND WHO TO RING. Blank until the full record arrives —
   *  it is not on the roster row, deliberately, so there is nothing to seed it
   *  from until then. `storedPersonal` is what the save diffs against, for the
   *  same reason `stored` is: the member PATCH is field-by-field and an
   *  untouched field must not be sent at all. */
  const [personal, setPersonal] = useState<Personal>(() => ({ ...NO_PERSONAL }))
  const storedPersonal = useRef<Personal>({ ...NO_PERSONAL })
  /** The full record, for the two things a roster row cannot answer: whether
   *  the emergency-contact number could be read, and the dates and certificate
   *  numbers behind his qualifications. */
  const [full, setFull] = useState<MemberRecord | null>(null)
  /** His qualifications in the record's own shape, replaced whole by every
   *  write — the three routes answer with the list rather than the row, because
   *  the order is a rule the browser does not hold. `null` until the record
   *  arrives, which is what makes the block read-only for a role that only ever
   *  had the roster row. */
  const [held, setHeld] = useState<MemberCertificationRecord[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Seeded from the prop and then owned here, so it can be dismissed like the
   *  page's own notices. `key={record.id}` remounts this dialog per member, so
   *  it cannot outlive the registration it belongs to. */
  const [arrived, setArrived] = useState<Arrival | null>(arrival ?? null)
  /** WHICH TAB IS ON SCREEN, AND NOTHING ELSE.
   *
   *  EVERY FIELD'S STATE LIVES ABOVE THIS LINE, which is what makes switching
   *  tabs free: `form`, `joined`, `address`, `personal`, `chosen`, `full` and
   *  `held` are all held by this component, so a panel going out of view
   *  cannot reach them. Type an address, open Broadcast, come back — the
   *  address is where it was, because nothing about it ever depended on being
   *  drawn. The children that hold state of their own are not unmounted
   *  either; see `TabPanel`.
   *
   *  AND THE DIRTY CHECK IS UNTOUCHED BY IT. `snapshotOf` compares the WHOLE
   *  form regardless of which tab is showing, so a change typed on a tab you
   *  are not looking at still lights "Unsaved changes" and still stops Escape
   *  throwing it away. Narrowing the snapshot to the visible tab would be a
   *  guard that only guards what you can see. */
  const [tab, setTab] = useState<TabKey>(openOn ?? RECORD_OPENS_ON)
  /** WHICH SUB-TAB OF PERSONAL INFORMATION IS ON SCREEN — the identity, the
   *  mailing address, or the numbers.
   *
   *  IT IS VIEW STATE LIKE `tab` AND IS NOT IN `snapshotOf` EITHER. The dirty
   *  check compares the whole form whichever sub-tab is showing, so an address
   *  typed and then left behind still lights "Unsaved changes" and still stops
   *  Escape throwing it away. Narrowing either check to what is visible would
   *  be a guard that only guards what you can see.
   *
   *  AND EVERY FIELD IT SWITCHES BETWEEN STILL LIVES ABOVE THIS LINE, so
   *  switching is as free as switching a main tab; the sub-panels stay mounted
   *  for the state their children hold themselves. */
  const [sub, setSub] = useState<PersonalKey>('identity')

  /** THE CONTROL A JUMP LANDS ON, focused after the render that put it on
   *  screen rather than inside the click that asked for it.
   *
   *  `focus()` DOES NOTHING TO A `display: none` ELEMENT, and that is what a
   *  hidden panel is. The sub-tab buttons live INSIDE the Personal information
   *  panel, so at the moment a handler calls `setTab('personal')` they are
   *  still hidden — React has not re-rendered — and focusing one there is a
   *  silent no-op that leaves the keyboard in the footer and tells a screen
   *  reader nothing. Which is the exact failure these links exist to end: the
   *  sentence names a box somebody cannot see, and the control that was meant
   *  to end the hunt reads as broken. */
  const [landOn, setLandOn] = useState<string | null>(null)
  useEffect(() => {
    if (!landOn) return
    document.getElementById(landOn)?.focus()
    setLandOn(null)
  }, [landOn])

  //: GO TO A TAB AND TAKE THE KEYBOARD WITH IT. Used by the footer's jump link
  //: and by Broadcast's "correct it" link. `within` names a sub-tab of Personal
  //: information -- without it the main tab's own button is what takes focus,
  //: which is right for a tab whose panel is one block.
  const jump = (key: TabKey, within?: PersonalKey) => {
    setTab(key)
    if (within) setSub(within)
    setLandOn(within ? `rec-personal-tab-${within}` : `rec-tab-${key}`)
  }

  /** How many identity documents are on file, reported up by `Documents`.
   *  `null` until its own fetch lands — see the prop. */
  const [documents, setDocuments] = useState<number | null>(null)
  /** The scrolling half of the dialog, so a tab switch can start at the top of
   *  the panel it just opened. */
  const body = useRef<HTMLDivElement>(null)

  useReturnFocus()

  // The parts of the form that never arrive asynchronously, as refs, so that
  // the effect below can build a snapshot from the CURRENT values rather than
  // from the ones its closure captured on the first render.
  const formRef = useRef(form)
  formRef.current = form
  const joinedRef = useRef(joined)
  joinedRef.current = joined
  const chosenRef = useRef(chosen)
  chosenRef.current = chosen

  /** Everything the Save button would send, as one comparable string.
   *
   *  ONE BUILDER, and that is the point of it being a function rather than two
   *  `JSON.stringify` calls: the baseline and the current value are compared as
   *  strings, so two spellings of the same object — a key in a different order,
   *  a block left out of one of them — compare unequal for ever, and the dialog
   *  would ask to discard changes nobody made until people learned to click
   *  through the guard.
   *
   *  SORTED BEFORE COMPARING. Group ticks land in whatever order the boxes were
   *  touched, so an unsorted comparison calls a member dirty for having been
   *  unticked and re-ticked.
   *
   *  THE ADDRESS AND THE PERSONAL HALF ARE IN HERE, or typing one and pressing
   *  Escape would throw it away without asking — the guard exists precisely for
   *  the fields somebody has just spent a minute on. The numbers and the
   *  certifications are NOT: those write the moment they are pressed, and the
   *  headings over them say so.
   */
  function snapshotOf(a: Address, p: Personal): string {
    return JSON.stringify({
      form: formRef.current,
      joined: joinedRef.current,
      address: a,
      personal: p,
      chosen: [...chosenRef.current].sort(),
    })
  }

  const snapshot = snapshotOf(address, personal)
  const initial = useRef(snapshot)
  const dirty = snapshot !== initial.current
  // Read by the effect below to ask whether anything has been touched. A ref
  // rather than the value, because the effect runs once and its closure would
  // otherwise be looking at the first render for ever.
  const latest = useRef(snapshot)
  latest.current = snapshot

  // THE ADDRESS, THE DATE OF BIRTH AND THE NEXT OF KIN ARE NOT ON THE ROW THIS
  // DIALOG OPENED FROM, and that is deliberate rather than an oversight to work
  // around. `GET /api/members` is ungated -- the dispatcher's responder picker
  // depends on it, and `Dispatch.tsx` calls it on load -- so those columns were
  // deliberately kept off it: a home address and a date of birth riding on that
  // list would sit in every dispatcher's browser for the length of every shift,
  // invisible, because no control on that screen draws them. CLAUDE.md's own
  // words: a read that hands over what a write is gated on IS the gate.
  //
  // So they are fetched here, once, from the route that carries
  // `settings.members`. Only when `mayEdit` -- a role reading the record
  // without that key would get a 403, and a 403 fired on open is a red notice
  // over a record somebody is only looking at.
  //
  // A failure is SILENT ON PURPOSE, and this is the one place in this file
  // where that is right: the boxes stay empty and the rest of the record works.
  // Announcing it would put an alarm on the screen for a field nobody may have
  // come here for. It also cannot destroy anything, because the baselines below
  // are only ever written beside the values they came with -- a save after a
  // failed read finds nothing changed and sends nothing.
  useEffect(() => {
    if (!mayEdit) return
    let live = true
    void (async () => {
      try {
        const record = await api.memberOne(member.id)
        if (!live) return
        setFull(record)
        // OUTSIDE THE UNTOUCHED CHECK BELOW, because the certifications are not
        // part of the form: they write the moment they are pressed and are not
        // in `snapshotOf`, so nothing anybody has typed can be overwritten by
        // filling them in.
        setHeld(record.certifications)

        // NOT OVER TYPING ALREADY IN PROGRESS, and it is all or none. The fetch
        // is fast and the dialog is usable immediately, so somebody can be
        // mid-postcode when it lands, and overwriting that would be the dialog
        // undoing them.
        //
        // THE BASELINE MOVES WITH THE VALUES OR NEITHER MOVES. Seeding a box
        // and leaving the baseline behind opened the record on "Unsaved
        // changes" for anybody who had an address on file, and offered to
        // discard work nobody had done; seeding the BASELINE and not the box
        // would be far worse -- the empty boxes would then read as fields
        // somebody had cleared, and Save would send eight nulls over an address
        // that was never on screen.
        if (latest.current !== initial.current) return
        const posted = addressFrom(record)
        const person = personalFrom(record)
        stored.current = posted
        storedPersonal.current = person
        setAddress(posted)
        setPersonal(person)
        initial.current = snapshotOf(posted, person)
      } catch {
        /* left blank; see above */
      }
    })()
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id, mayEdit])

  const identified = !!(form.display_name.trim() || form.callsign.trim())

  /** WHEN NOTHING IN THE BODY MAY BE TOUCHED — a role that may only read, or a
   *  write already in flight.
   *
   *  ONE EXPRESSION FOR THE FIELDSETS THAT READ IT. There were two of them the
   *  moment Personal information's sub-strip had to be lifted out from between
   *  its panels, and the same condition typed twice is the drift the single
   *  fieldset was written to avoid: the next clause added to one of them would
   *  leave the other half of the dialog editable during a save. */
  const locked = !mayEdit || busy

  /** WHETHER THIS ROLE MAY OPEN A SCAN AT ALL — its own key, and a different
   *  one from `mayEdit`. Correcting a typo in a callsign and looking at a
   *  photograph of somebody's passport are not the same privilege. */
  const mayFile = can('settings.member_documents')

  /** THE UNREADABLE NUMBERS, THROUGH THE ONE PREDICATE. Not
   *  `member.phones.filter(...)` written out here: that is the copy CLAUDE.md
   *  names by hand as the drift `hasBadNumber` exists to stop. */
  const unreadable = badNumbers(member)

  /** What is on the fourth tab. The certifications are `held ??
   *  member.certifications`, which is EXACTLY what the panel draws — a second
   *  expression for "his cards" is how the number on the tab and the rows
   *  behind it come to disagree. `documents` is `null` until that block's own
   *  fetch lands, and stays null for a role without `mayFile`, which draws no
   *  documents at all. */
  const cards = (held ?? member.certifications).length
  const papers = cards + (documents ?? 0)

  /** The groups he is in, as the two controls will draw them: the catalogue
   *  plus any group he is in that the catalogue no longer lists. Computed here
   *  rather than inside each control so the category and the areas cannot
   *  disagree about which groups exist — see `withHisOwn`. */
  const known = withHisOwn(groups, member.groups)

  /** The strip.
   *
   *  A TAB THAT IS NOT DRAWN IS NOT DRAWN EMPTY, which is rule for rule the
   *  reason the blocks behind them are conditional. The emergency contact
   *  arrives on the GATED record read, so a role without `settings.members` has
   *  nothing to put in it — and an empty box reads as "nothing on file", not as
   *  "you may not see this". The night a volunteer is hurt, that is a
   *  dispatcher concluding no emergency contact was ever recorded and stopping
   *  looking. The mailing address and the date of birth are the same read and
   *  are left out of Personal information for the same reason, and the
   *  documents have their own key again.
   *
   *  THE AMBER IS ON BROADCAST AND THE `!` IS ON PERSONAL INFORMATION, one mark
   *  each, and which tab gets which is decided by what the mark MEANS rather
   *  than by where it is fixed. A number the server could not read is a
   *  BROADCAST fact — the member looks contactable and no text can reach him —
   *  and the panel behind that badge is where the tick is refused, with the way
   *  back to the box that corrects it. The missing identity is what stops the
   *  Save button, and both boxes are on Personal information.
   *
   *  NO COUNT ON PERSONAL INFORMATION, deliberately. It holds three sub-tabs
   *  and five kinds of thing, so a single number over them would count nothing
   *  anybody asked about — a badge is a count or an alarm and never decoration.
   *
   *  A `0` DRAWS NOTHING, for the same reason: a badge that says the same as a
   *  blank is one more mark to learn to ignore. */
  const tabs: TabSpec[] = [
    {
      key: 'personal',
      label: 'Personal information',
      // WHICH TAB THE REFUSAL IS ON, and this is the only refusal on this
      // dialog whose tab the screen actually knows. The SERVER's sentences —
      // a callsign clash, an unreachable member — are not pattern-matched to
      // a tab: guessing wrong puts a red mark on the wrong one, which is
      // worse than none, so they stay in the footer.
      //
      // AND IT IS ON THE SUB-TAB TOO, below. A mark that says "something on
      // Personal information" and then leaves somebody to open three sub-tabs
      // looking for it is the hunt the badge exists to end.
      ...(identified ? {} : {
        badge: '!',
        alarm: true,
        badgeTitle: 'A member needs a unit number or a name.',
      }),
    },
    ...(mayEdit ? [{ key: 'emergency' as const, label: 'Emergency contact' }] : []),
    {
      key: 'broadcast',
      label: 'Broadcast',
      ...(unreadable > 0 ? {
        badge: String(unreadable),
        alarm: true,
        badgeTitle: unreadable === 1
          ? 'One number could not be read, so no broadcast can text it.'
          : `${unreadable} numbers could not be read, so no broadcast can text them.`,
      } : {}),
    },
    {
      // ONE TAB FOR THE TWO, which is what the customer asked for: "4 should be
      // for certifications and documents. Could share the same tab." The tab is
      // drawn for everybody because the certifications are — they ride on the
      // ungated roster row, so even a read-only record can say what he is
      // qualified for. Only the Documents block inside it needs the second key.
      key: 'documents',
      label: 'Certifications and documents',
      ...(papers > 0 ? {
        badge: String(papers),
        // `null` MEANS NOT KNOWN YET AND IS NOT NONE, which is why the
        // documents clause is dropped rather than printed as a zero. `0
        // documents` while that block's own fetch is still in flight is a
        // confident answer to the question the badge exists to answer, and it
        // is wrong — the same reason `Documents` keeps `null` and `[]` apart.
        // A role without the key never has them at all.
        badgeTitle: `${cards} certification${cards === 1 ? '' : 's'}`
          + (mayFile && documents !== null
            ? `, ${documents} document${documents === 1 ? '' : 's'}`
            : ''),
      } : {}),
    },
  ]

  /** THE OPENING TAB HAS TO BE ONE THAT EXISTS, and this is not a check that
   *  can never fire. `RECORD_OPENS_ON` is deliberately one line so it can be
   *  changed on the customer's word — and pointed at Emergency contact it would
   *  open a dispatcher's record on a tab that is not drawn for him, with
   *  nothing selected and no panel on screen. `openOn` can do the same: the
   *  success banner asks for Certifications and documents, which is drawn for
   *  everybody, but nothing stops the next caller naming a gated one. Falling
   *  back to the first tab is a smaller wrong answer than a dialog that renders
   *  blank. */
  const shown = tabs.some((t) => t.key === tab) ? tab : tabs[0].key

  /** THE THREE SUB-TABS OF PERSONAL INFORMATION, in the customer's own count:
   *  "Personal information has 3 sections."
   *
   *  ADDRESS IS DRAWN ONLY FOR A ROLE THAT MAY READ IT, exactly as the
   *  Emergency contact TAB is and for the same reason: the eight columns
   *  arrive on the gated record read, so for anybody else the boxes would be
   *  empty — and an empty box reads as "nothing on file", not as "you may not
   *  see this". A gated section is not drawn at all, never drawn empty, and a
   *  sub-tab over an empty panel is the emptiest version of that.
   *
   *  THE BADGE IS THE MAIN TAB'S, REPEATED WHERE THE BOXES ARE. `identified` is
   *  the one refusal this screen knows the place of; the unit number and the
   *  name are on Identity and nowhere else. */
  const subTabs: TabSpec<PersonalKey>[] = [
    {
      key: 'identity',
      label: 'Identity',
      ...(identified ? {} : {
        badge: '!',
        alarm: true,
        badgeTitle: 'A member needs a unit number or a name.',
      }),
    },
    ...(mayEdit ? [{ key: 'address' as const, label: 'Address' }] : []),
    { key: 'numbers', label: 'Numbers' },
  ]

  /** THE SUB-TAB HAS TO BE ONE THAT EXISTS, for the reason the tab above it
   *  does. The LIST is conditional — Address comes off it for a role that
   *  cannot read a mailing address — and the STATE is not: its opening value
   *  is a literal here, exactly as `RECORD_OPENS_ON` is a literal above, and
   *  `jump` writes it from two places. Pointed at Address on a read-only
   *  record, none of the three would be selected and the panel area would
   *  simply be blank. */
  const subShown = subTabs.some((t) => t.key === sub) ? sub : subTabs[0].key

  /** A NEW TAB STARTS AT ITS OWN TOP. `.sheet-recbody` is one scroll container
   *  for all of them, so leaving a long Personal information halfway down and
   *  pressing Broadcast opens it a screen further down than its first box —
   *  which reads as a tab with nothing on it. Only the scroll moves; nothing
   *  anybody typed is touched.
   *
   *  THE SUB-TABS ARE IN THE SAME SCROLL CONTAINER, so they are in the same
   *  rule: the numbers list is the one panel here that can outgrow the box,
   *  and coming back from the bottom of it to a two-line Address would open
   *  that on empty space. */
  useEffect(() => {
    if (body.current) body.current.scrollTop = 0
  }, [shown, subShown])

  const { leave, backdrop } = useDialogGuard(
    // THE SAME THREE LINES THE REGISTRATION ALREADY HAS, and the reason is
    // sharper here. `busy` is set by Save, by every number write, by a text
    // tick, by a certification write and by a document upload or delete --
    // and most of those leave `dirty` FALSE, because they are not part of
    // `snapshotOf`. So Escape or Cancel while one was in flight closed the
    // record with no prompt at all, or offered to discard changes at the
    // exact moment they were being stored.
    //
    // That is the contradiction the "saved as you go" wording exists to
    // prevent, and the customer asked for these two dialogs to be
    // identical.
    () => {
      if (busy) return 'Still saving. Wait a moment before closing this.'
      return dirty ? `Discard your unsaved changes to ${member.label}?` : null
    },
    () => { if (!busy) onClose() },
  )

  async function save(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onSaved(await api.memberUpdate(member.id, {
        // NULL, NOT AN EMPTY STRING, for both halves of the identity. Either
        // may be cleared now; clearing BOTH is refused by the server with a
        // sentence, which is shown below.
        display_name: form.display_name.trim() || null,
        callsign: form.callsign.trim() || null,
        email: form.email.trim() || null,
        radio_imei: form.radio_imei.trim() || null,
        vehicle_plate: form.vehicle_plate.trim() || null,
        has_radio: form.has_radio,
        notify_email: form.notify_email,
        notify_card: form.notify_card,
        group_ids: chosen,
        joined_on: joined || null,
        // ONLY THE PARTS THAT CHANGED — see `addressChanges`. The member PATCH
        // is per-part, so an untouched part must not be sent at all.
        ...addressChanges(address, stored.current),
        // AND THE SAME RULE FOR THE PERSONAL HALF, which matters more here
        // than for the address: these four are blank until the record arrives,
        // so sending them wholesale would clear a date of birth and a next of
        // kin every time a role that could not read them pressed Save.
        ...personalChanges(personal, storedPersonal.current),
      }))
    } catch (err) {
      // The server's own sentences -- "Callsign 'MT-9' is already in use.
      // Callsigns go out on the air", "A member needs a unit number or a
      // name", "The radio card is delivered to a handset" -- are better than
      // anything this screen could say, so they are shown verbatim.
      setError(err instanceof ApiError ? err.message : 'Could not save that change.')
      setBusy(false)
    }
  }

  /** A certification was added, renewed or removed.
   *
   *  THE ROW BEHIND THE DIALOG IS PATCHED WITH WHAT THE WRITE RETURNED, not
   *  re-fetched and not guessed at. The three routes answer with the member's
   *  whole list in the record's shape, which is a strict SUPERSET of the
   *  fields the roster listing carries, so the sheet's Certifications column
   *  cannot go stale behind an open record. Working out the compact rows from
   *  the full ones would be the listing's own aggregate written a second time
   *  in the browser.
   *
   *  THE FIELDS MATCH; THE ORDER DOES NOT, and that is fine here. `of_member`
   *  returns lapsed-first and the listing returns catalogue order —
   *  `member_certifications_test` section 5 asserts the two differ on purpose
   *  — so after a write this one member's cell is ordered lapsed-first while
   *  the rest of the sheet is in catalogue order, until a reload. The
   *  `expired` flags and the count are identical, and they are all that cell
   *  reports. Said plainly because the first version of this comment claimed
   *  the values were identical, which the suite one directory over
   *  disproves. */
  function absorbHoldings(rows: MemberCertificationRecord[]) {
    setHeld(rows)
    onMember({ ...member, certifications: rows })
  }

  /** RETIRE, NEVER DELETE. Nothing in this product deletes a member and
   *  nothing should: `incident_responders` points at them with ON DELETE
   *  RESTRICT and there is no frozen name anywhere, so removing somebody would
   *  take them off the incidents they attended rather than leaving a record
   *  that reads correctly. Retiring takes them out of every broadcast, which
   *  is the thing anybody actually wants. */
  async function retire() {
    const back = !member.active
    if (!confirm(back
      ? `Put ${member.label} back on the roster? Broadcasts will reach them again.`
      : `Retire ${member.label}? They stay on every incident they have `
        + 'attended, and no broadcast reaches them from now on.')) return
    setBusy(true)
    try {
      onRetired(await api.memberUpdate(member.id, { active: back }))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that.')
      setBusy(false)
    }
  }

  return (
    <div className="modalback" {...backdrop}>
      {/* `memrec` IS THE WIDTH AND NOTHING ELSE. `.sheet-rec` is shared with
          the phone book's record, Users, the member groups and both importers,
          so the extra 60px these four long tab labels want is scoped to the two
          dialogs that have them rather than applied to six screens. */}
      <form
        className="modal wide sheet-rec memrec"
        role="dialog"
        aria-modal="true"
        aria-labelledby="memname"
        onSubmit={save}
      >
        <header className="sheet-rechead">
          {/* `label`, never `display_name`: half the roster has no name and the
              title of the dialog would be empty. */}
          <h3 id="memname">{member.label}</h3>
          <span className={dirty ? 'sheet-recstate dirty' : 'sheet-recstate'}>
            {!mayEdit ? 'Read only' : dirty ? 'Unsaved changes' : 'Editing'}
          </span>
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>

        {/* BETWEEN THE HEAD AND THE BODY, so it never scrolls away. Only
            `.sheet-recbody` scrolls; a strip inside it would be gone the moment
            somebody read to the bottom of the numbers, and the way back to
            another tab would be to scroll up first. It is also OUTSIDE the
            fieldset below, so a save or a number write in flight cannot make
            the tabs unclickable for the length of the request. */}
        <TabStrip
          idPrefix="rec" label="Sections"
          tabs={tabs} active={shown} onPick={setTab} />

        <div className="sheet-recbody" ref={body}>
          {/* FIRST, BECAUSE IT IS WHY THIS DIALOG IS OPEN. A registration that
              finished with something still to do lands here rather than on the
              sheet, and this sentence is what says which — an identity
              document to file, a certification that did not attach, a number
              the server could not read.

              ABOVE THE PANELS AND NOT INSIDE ONE. Every notice here is about
              the whole member — the arrival, a server refusal, retired,
              stranded — and filing one behind a tab would hide it from
              somebody standing on a different one. */}
          {arrived && (
            <div
              className="notice"
              role="alert"
              onClick={() => setArrived(null)}
            >
              {arrived.message}
            </div>
          )}
          {!member.active && (
            <div className="notice wait" role="status">
              This member is retired. No broadcast reaches them.
            </div>
          )}
          {stranded(member) && (
            <div className="notice" role="alert">
              There is a number on this member that could not be read as a phone
              number, and nothing here is ticked for text — so no broadcast
              reaches them that way. Check it, and check the organization’s
              country is right for a number typed without a country code.
            </div>
          )}

          {/* PERSONAL INFORMATION — three sub-tabs, in the customer's own
              count: "Personal information has 3 sections. It should have 3 sub
              types inside. We should not need to scroll down." The order is
              still his: "1 should be who he is... Address should be part of
              personal information. His phone numbers should be part of personal
              information", and the category and the areas ride on Identity
              because they are part of WHAT HE IS rather than a fourth section.

              THE SUB-STRIP IS OUTSIDE THE FIELDSET BELOW, and that is not
              tidiness. A `<fieldset disabled>` disables every form control
              under it, buttons included — so a strip inside one would be
              unclickable for the whole length of a save, and PERMANENTLY dead
              for a read-only record, leaving a dispatcher able to see the
              identity and nothing else of a member he is allowed to read. The
              main strip is outside its fieldset for the same reason. */}
          <TabPanel idPrefix="rec" tab="personal" active={shown}>
            <TabStrip
              tone="sub"
              idPrefix="rec-personal"
              label="Personal information"
              tabs={subTabs}
              active={subShown}
              onPick={setSub}
            />

            {/* ONE EXPRESSION, TWO FIELDSETS. The strip above had to be lifted
                out from between them, which is what split the record's single
                fieldset in two — so `locked` is computed once and both read it,
                rather than the same condition being typed twice and drifting
                the first time one of them gains a clause. */}
            <fieldset className="bare" disabled={locked}>
              <TabPanel tone="sub" idPrefix="rec-personal" tab="identity" active={subShown}>
                <IdentityFields
                  value={form}
                  onChange={setForm}
                  idPrefix="rec"
                  joined={joined}
                  onJoined={setJoined}
                  {...(mayEdit ? {
                    birth: personal.date_of_birth,
                    onBirth: (v: string) =>
                      setPersonal({ ...personal, date_of_birth: v }),
                  } : {})}
                />

                {/* TWO BLOCKS AND NOT ONE ROW OF TICKS, which is the correction:
                    "It's two separate things, categories and areas. I think
                    you're looking at it as it's one big thing." One category, as
                    radio buttons; as many areas as apply, as ticks. Both write
                    the same `chosen` list, because `set_groups` takes the whole
                    answer in one call — two lists would be two save paths for one
                    member.

                    ON IDENTITY RATHER THAN ON A SUB-TAB OF THEIR OWN. The
                    customer counted three sections and these are not one of
                    them: which category a member is in and which streets he
                    covers are what he IS, which is what the rest of this panel
                    says. */}
                {/* SIDE BY SIDE, WHICH SAYS "TWO SEPARATE THINGS" BETTER
                    THAN STACKING THEM DID. The customer's correction was
                    exactly that -- "It's two separate things, categories and
                    areas. I think you're looking at it as it's one big thing"
                    -- and two columns make the split structural rather than a
                    matter of reading two headings in a row. It also costs one
                    row instead of two, which is what lets Identity fit the
                    fixed box without scrolling. */}
                <div className="sidebyside">
                  <div>
                    <CategoryChoice
                      groups={known} chosen={chosen} onChange={setChosen}
                      idPrefix="rec" />
                  </div>
                  <div>
                    <AreaTicks
                      groups={known} chosen={chosen} onChange={setChosen}
                      idPrefix="rec" />
                  </div>
                </div>
              </TabPanel>

              {/* DRAWN ONLY FOR A ROLE THAT CAN ACTUALLY READ IT, the way
                  `Documents` is, and for a sharper reason than tidiness.

                  These columns live on the GATED record read: the effect that
                  calls `api.memberOne` returns early without `settings.members`,
                  so for a dispatcher `address` stays blank. Left unconditional,
                  the boxes still render -- empty, and disabled by the fieldset
                  -- and AN EMPTY BOX READS AS "NOTHING ON FILE", not as "you
                  may not see this". A gate that manufactures a wrong fact is
                  worse than one that declines to answer. The date of birth on
                  Identity rides on the same read and is left out the same way.
                  The SUB-TAB goes with the panel: a sub-tab over an empty panel
                  is the emptiest form of a section drawn empty.

                  WHERE A LETTER GOES, and the customer's reason for asking:
                  "we should be able to send out letters to all our members and
                  so on, or we should be able to send a gift for the holidays."

                  IT IS NOT A `places` ROW AND MUST NOT BECOME ONE. A place is a
                  DISPATCH location — entry codes, cross streets, site notes,
                  deduplicated by address, and read by the dispatcher's address
                  search. A member's home filed there would show up in that
                  search, and would merge with an incident location the day
                  somebody is dispatched to his street.

                  AND IT IS NEVER GEOCODED. Nothing on this screen calls
                  `placeLocate`, and the server does not geocode these columns:
                  a home address has no map to appear on, so sending the whole
                  roster's home addresses to OpenStreetMap would be a privacy
                  cost with no feature behind it.

                  "Mailing address" AND NOT "Address", though the sub-tab that
                  opens it says Address: the whole reason these eight columns are
                  not a `places` row is that they are where a letter goes rather
                  than where anybody is sent, and this heading is the only place
                  on screen that can say so — which is exactly why it survived
                  when the Identity heading did not. */}
              {mayEdit && (
                <TabPanel tone="sub" idPrefix="rec-personal" tab="address" active={subShown}>
                  <h4 className="sheet-recsub">Mailing address</h4>
                  <AddressFields
                    showCountry value={address} onChange={setAddress} />
                </TabPanel>
              )}

              {/* HIS NUMBERS, WITHOUT THE TEXT TICK — added, corrected and
                  removed here, and ticked on Broadcast. It keeps its own
                  heading because the four words beside it say the numbers save
                  the moment a button is pressed, which the sub-tab cannot. */}
              <TabPanel tone="sub" idPrefix="rec-personal" tab="numbers" active={subShown}>
                <Numbers
                  member={member}
                  busy={busy}
                  setBusy={setBusy}
                  onNumbers={onMember}
                />
              </TabPanel>
            </fieldset>
          </TabPanel>

          {/* THE OTHER TWO PANELS THIS FIELDSET COVERS, `busy` included, and
              that is what serialises the text ticks. They write straight from
              onClick, and two quick presses on Gets the text would otherwise be
              two racing writes against the same member.

              IT WRAPS THE PANELS RATHER THAN SITTING INSIDE EACH ONE. Two
              copies of `locked` is two places for the next tab to be added
              without one.

              THE FOURTH TAB IS OUTSIDE IT, and that is not an oversight: it
              holds two blocks gated on two DIFFERENT keys, so it carries a
              fieldset of its own around the half this one would have covered.
              See below. */}
          <fieldset className="bare" disabled={locked}>
            {/* WHO TO RING IF SOMETHING HAPPENS TO HIM — its own tab, second,
                where the customer put it: "2 The next step should be emergency
                contact."

                CALLED WHAT HE CALLS IT. It was "Next of kin", and he said
                plainly that the phrase meant nothing to him: "I don't know if
                the name you gave it, like, next of kin. I don't know what that
                name is." The columns and the routes keep their names —
                `emergency_contact_*` was always what they were called — so only
                the words on screen changed.

                DRAWN ONLY FOR A ROLE THAT MAY READ IT, like the address above
                and for the same reason: it arrives on the gated record read, so
                the boxes would be empty rather than refused. The night a
                volunteer is hurt, a dispatcher opening his record, seeing an
                empty emergency contact and concluding none was ever recorded is
                the failure this prevents — while the name and the number sit on
                the row behind a permission he does not hold. The TAB goes with
                the panel: an empty tab is a heading over nothing.

                NOTHING HERE IS A CHANNEL AND NOTHING MAY SUGGEST IT IS. There
                is no tick beside this number, and there is not going to be one:
                a broadcast goes to the people on the roster, and a member's
                mother receiving an emergency text meant for a volunteer is the
                failure that arrangement would produce. `_reachable` on the server
                cannot be satisfied by anything in this section either — which
                is also why it is not part of Broadcast.

                THE RELATIONSHIP IS FREE TEXT, not the caller-relationship list.
                That list is the CALLER's relationship to an INCIDENT and one of
                its rows decides what the intake screen does with an address —
                pointing an emergency contact at it would put "Wife" among the
                dispatcher's intake buttons.

                NO HEADING OF ITS OWN. The tab is the heading, and this panel
                holds one block; repeating the two words underneath them is the
                small print the customer asked to be rid of. */}
            {mayEdit && (
              <TabPanel idPrefix="rec" tab="emergency" active={shown}>
                <div className="grid2">
                  <div className="field">
                    <label htmlFor="rec-ecname">Name</label>
                    <input
                      id="rec-ecname"
                      value={personal.emergency_contact_name}
                      maxLength={200}
                      onChange={(e) => setPersonal(
                        { ...personal, emergency_contact_name: e.target.value })}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="rec-ecrel">Relationship</label>
                    <input
                      id="rec-ecrel"
                      value={personal.emergency_contact_relationship}
                      maxLength={80}
                      placeholder="Wife, son, neighbour"
                      onChange={(e) => setPersonal({
                        ...personal,
                        emergency_contact_relationship: e.target.value,
                      })}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="rec-ecphone">Number</label>
                    {/* BOUNDED AT THE SERVER'S OWN 40, like the member's own
                        numbers: a number is a number whoever it belongs to. */}
                    <PhoneInput
                      id="rec-ecphone"
                      value={personal.emergency_contact_phone}
                      maxLength={40}
                      onChange={(e) => setPersonal(
                        { ...personal, emergency_contact_phone: e.target.value })}
                    />
                    {/* AN UNREADABLE ONE IS KEPT, NOT REFUSED —
                        `member_phones`'s rule rather than the phone book's,
                        because it is the only lead there is to a member's
                        family and nothing sends to it. So it costs nothing
                        tonight and everything on the night it is dialled, and
                        the only moment anybody can fix it is while they are
                        looking at this box.

                        AND IT IS NOT ON THE BROADCAST BADGE. That amber counts
                        numbers a BROADCAST cannot text; this one is never
                        texted by anything, so counting it there would raise the
                        alarm for a member who is perfectly reachable.

                        SAID ONLY WHILE IT IS TRUE OF WHAT IS STORED.
                        `emergency_contact_readable` describes the number the
                        server last read, so it is drawn only while the box
                        still holds that number — otherwise a correction being
                        typed would carry the old verdict, and the sentence
                        would be about a number that is no longer there. */}
                    {full && full.emergency_contact_phone
                      && !full.emergency_contact_readable
                      && personal.emergency_contact_phone
                        === full.emergency_contact_phone && (
                      <p className="footnote warn">
                        This could not be read as a phone number.
                      </p>
                    )}
                  </div>
                </div>
              </TabPanel>
            )}

            {/* BROADCAST — the ticks, the email address, and which of his
                numbers take the text. */}
            <TabPanel idPrefix="rec" tab="broadcast" active={shown}>
              <ChannelFields value={form} onChange={setForm} idPrefix="rec" />
              <TextTicks
                member={member}
                busy={busy}
                setBusy={setBusy}
                onNumbers={onMember}
                // THE FIX IS ON ANOTHER TAB, so the link that names it switches
                // to that tab AND takes focus with it. Leaving focus behind
                // means a keyboard user has to walk back through the panel to
                // reach the box the sentence is about, and a screen reader is
                // told nothing at all — the control reads as broken.
                //
                // AND IT NAMES THE SUB-TAB NOW. The box that corrects a number
                // is behind Numbers; landing on Personal information's first
                // sub-tab would put the identity grid on screen and leave the
                // hunt exactly where the sentence promised to end it.
                onFix={() => jump('personal', 'numbers')}
              />
            </TabPanel>
          </fieldset>

          {/* ONE TAB, TWO KEYS, WHICH IS WHY IT IS OUT HERE. The customer put
              certifications and documents together — "4 should be for
              certifications and documents. Could share the same tab" — and they
              are gated separately: a certification rides on the ungated roster
              row, while a scan needs `settings.member_documents`, because
              correcting a typo in a callsign and opening a photograph of
              somebody's passport are not the same privilege.

              So the panel sits outside the fieldset above and puts one of its
              own around the certifications. Inside that fieldset, a role
              holding the documents key and not the roster key would get the
              upload drawn and dead; inside nothing at all, the certification
              controls would lose the `busy` that serialises them. */}
          <TabPanel idPrefix="rec" tab="documents" active={shown}>
            <fieldset className="bare" disabled={locked}>
              <Certifications
                member={member}
                held={held}
                mayEdit={mayEdit}
                busy={busy}
                setBusy={setBusy}
                onHoldings={absorbHoldings}
              />
            </fieldset>

            {/* Drawn only when the key is held: not a disabled control, not an
                empty heading, and no way to learn from this screen that a
                member has documents on file without being allowed to see
                them. */}
            {mayFile && <Documents member={member} onCount={setDocuments} />}
          </TabPanel>
        </div>

        {/* THE REFUSAL SITS WITH THE BUTTON THAT CAUSED IT, which the
            registration already did and this dialog did not -- an asymmetry on
            a pair the customer asked to be identical, and the taller of the
            two is the one that had it wrong.

            `.sheet-recbody` is the only part that scrolls and `.modalrow` is
            pinned outside it. Personal information now carries the identity
            grid, eight address boxes, the numbers list with its add bar, the
            category radios and the area ticks, so it is taller than the dialog
            on any screen. Somebody who scrolls to the bottom of it and presses
            Save had "Callsign MT-9 is already in use. Callsigns go out on the
            air, so they have to be unique." painted above the fold, with
            nothing to scroll it into view -- and a Save button that un-spins
            with nothing visible reads as "nothing happened". */}
        <div className="modalrow">
          {error && (
            <div className="notice" role="alert" style={{ margin: 0 }}>{error}</div>
          )}
          {mayEdit && (
            <button
              className={member.active ? 'ghost danger' : 'ghost ok'}
              type="button"
              disabled={busy}
              onClick={retire}
            >
              {member.active ? 'Retire' : 'Put back on the roster'}
            </button>
          )}
          <span className="spacer" />
          <button className="ghost" type="button" onClick={leave}>
            {mayEdit ? 'Cancel' : 'Close'}
          </button>
          {/* A DISABLED BUTTON THAT DOES NOT EXPLAIN ITSELF IS A SCREEN
              SOMEBODY STARES AT, which is what the removed footnote said about
              itself. Its replacement was a `title` on the callsign input plus
              "the server's refusal arrives as a sentence" — and that refusal
              CANNOT arrive, because `identified` disables the button and the
              request is never sent. So the only explanation left was hover
              text, on a box inside a `<fieldset disabled>`, where browsers
              suppress tooltips.

              Said here instead, and ONLY while it is actually blocking — which
              is the difference between this and standing prose.

              AND IT NAMES ITS TAB, because the two boxes it is about can be a
              tab away — somebody clears a callsign, moves on to Broadcast, and
              presses Save from there. A sentence telling somebody to fill in a
              field they cannot see is an instruction with a hunt attached, so
              the tab is named and the name is clickable. THIS IS THE ONE
              REFUSAL WHOSE TAB THE SCREEN KNOWS — a refusal that came back from
              the SERVER is a sentence we did not author, and matching it to a
              tab would be a guess that puts a red mark on the wrong one. Those
              stay in `error`, above. */}
          {mayEdit && !identified && (
            <span className="footnote warn" style={{ margin: 0 }}>
              Give him a unit number or a name, on{' '}
              <button
                className="linky"
                type="button"
                // FOCUS GOES WITH IT. Switching the tab and leaving focus on a
                // link in the FOOTER -- after the whole panel in DOM order --
                // means a keyboard user has to Shift+Tab backwards through
                // Cancel and every field to reach the box the sentence is
                // about, and a screen reader is told nothing at all, so the
                // control reads as broken. The sentence exists to end a hunt;
                // this is what ends it.
                //
                // ONTO THE SUB-TAB, not merely onto the tab. Personal
                // information is three sub-tabs now, and the two boxes this
                // sentence is about are on one of them — landing on the panel
                // and leaving somebody to find which is the same hunt one level
                // down. `jump` sets both and focuses the sub-tab.
                onClick={() => jump('personal', 'identity')}>
                Personal information → Identity
              </button>.
            </span>
          )}
          {mayEdit && (
            <button className="primary compact" disabled={busy || !dirty || !identified}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

/** Registering somebody, with everything his record holds.
 *
 *  THE FORM WHERE A NEW MEMBER'S DETAILS ARE TYPED IS THE FORM THAT NEEDS THE
 *  FIELDS. The customer, having tested the record dialog: "You only put it
 *  when I open an existing member. I can edit all those information by adding
 *  a member, registering a new member. It also needs all the new updates." The
 *  record had grown a mailing address, a date of birth, an emergency contact,
 *  several numbers, certifications and identity documents while this stayed
 *  identity, channels, groups and one number — so registering somebody meant
 *  saving him, finding his row again, and typing the rest.
 *
 *  AND IT IS THE SAME FOUR TABS, DOCUMENTS INCLUDED, BY REQUEST. "When I want
 *  to register a member, it's missing the section of uploading files, the
 *  registration page, and the editing page from existing member should be the
 *  same. Identical." So this dialog draws every tab the record draws, in the
 *  same order, with the same headings.
 *
 *  WHAT `MemberIn` TAKES GOES IN THE CREATE BODY, AND NOTHING ELSE CAN. The
 *  eight address columns, the date of birth, the three emergency-contact
 *  fields, the joining date, the groups and a LIST of numbers are all on that
 *  model, so they are one POST. Certifications and identity documents are not,
 *  and cannot be: both hang off a member id that does not exist until it
 *  answers. Neither is faked with a staging area on the server — they are held
 *  in this component and sent through their own real routes the moment there is
 *  a member, one at a time, and anything that will not go on is named. See
 *  `NewDocuments` for why a `File` waits in the browser rather than anywhere
 *  else.
 */
function AddDialog({
  groups, onClose, onAdded,
}: {
  groups: MemberGroup[]
  /** The member the server made, and the certifications and documents that did
   *  NOT go on him. The second is never a reason to say the registration
   *  failed: he is on the roster and a broadcast reaches him. */
  onAdded: (made: Member, unattached: Unattached[]) => void
  onClose: () => void
}) {
  const [form, setForm] = useState({ ...BLANK })
  const [chosen, setChosen] = useState<string[]>([])
  const [joined, setJoined] = useState('')
  const [address, setAddress] = useState<Address>({ ...BLANK_ADDRESS })
  const [personal, setPersonal] = useState<Personal>({ ...NO_PERSONAL })
  const [numbers, setNumbers] = useState<NewNumber[]>([])
  const [number, setNumber] = useState<NewNumber>({ ...BLANK_NUMBER })
  const [certs, setCerts] = useState<NewCert[]>([])
  const [cert, setCert] = useState<NewCert>({ ...BLANK_CERT })
  /** The scans chosen for a member who does not exist yet, and the add bar
   *  underneath them. Uploaded after the create — see `NewDocuments`. */
  const [docs, setDocs] = useState<NewDocument[]>([])
  const [doc, setDoc] = useState<NewDocumentDraft>({ ...BLANK_DOCUMENT })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** WHETHER THIS ROLE MAY FILE A SCAN AT ALL — its own key, exactly as on the
   *  record. Without it the Documents block is not drawn: an upload it may not
   *  perform is a control drawn and then refused, and here it would be refused
   *  after the member had already been created. */
  const mayFile = can('settings.member_documents')
  /** WHICH TAB IS ON SCREEN, AND NOTHING ELSE.
   *
   *  EVERY FIELD'S STATE LIVES ABOVE THIS LINE — `form`, `chosen`, `joined`,
   *  `address`, `personal`, `numbers`, `number`, `certs`, `cert`, `docs`,
   *  `doc` — so a panel going out of view cannot reach any of it. Somebody
   *  types a mailing address, jumps to Broadcast to untick a number, comes
   *  back, and the address is exactly where it was, because being drawn was
   *  never what kept it. That matters more here than on the record: none of
   *  this is stored anywhere until Save, so a tab switch that cleared a field
   *  would destroy typing with no route back — and for `docs` it would throw
   *  away a chosen file with no way to tell it had gone. `TabPanel` keeps the
   *  panels mounted for the state its children hold themselves.
   *
   *  AND `snapshot` IS UNTOUCHED BY IT. The guard compares the WHOLE form, not
   *  the visible tab — a registration with an address typed on a tab you are
   *  not looking at still prompts before Escape throws it away. */
  const [tab, setTab] = useState<TabKey>(ADD_OPENS_ON)
  /** WHICH SUB-TAB OF PERSONAL INFORMATION IS ON SCREEN — the record's split,
   *  because the customer asked for these two dialogs to be identical and he
   *  reported the scrolling on both. View state like `tab`, out of `snapshot`
   *  for the same reason, and the fields it switches between all live above it
   *  so switching cannot reach them. */
  const [sub, setSub] = useState<PersonalKey>('identity')

  /** THE CONTROL A JUMP LANDS ON — the record's `landOn`, and here for exactly
   *  the same reason: a sub-tab's button is inside a panel that is still
   *  `display: none` at the moment the handler runs, and `focus()` on such an
   *  element does nothing at all. Focusing after the render is what turns the
   *  footer's link back into a control rather than a word that moves the
   *  screen and abandons the keyboard. */
  const [landOn, setLandOn] = useState<string | null>(null)
  useEffect(() => {
    if (!landOn) return
    document.getElementById(landOn)?.focus()
    setLandOn(null)
  }, [landOn])

  //: GO TO A TAB AND TAKE THE KEYBOARD WITH IT. `within` names a sub-tab of
  //: Personal information; without it the main tab's own button takes focus.
  const jump = (key: TabKey, within?: PersonalKey) => {
    setTab(key)
    if (within) setSub(within)
    setLandOn(within ? `add-personal-tab-${within}` : `add-tab-${key}`)
  }

  /** The scrolling half of the dialog, so a tab switch can start at the top of
   *  the panel it just opened. */
  const body = useRef<HTMLDivElement>(null)

  /** A NEW TAB STARTS AT ITS OWN TOP — the record's rule, and the same one
   *  scroll container behind it. The sub-tabs are in that container too, so
   *  they are in the rule. Only the scroll moves; nothing anybody typed is
   *  touched, which on this form is the whole registration. */
  useEffect(() => {
    if (body.current) body.current.scrollTop = 0
  }, [tab, sub])

  /** EVERYTHING THIS FORM HOLDS, as one comparable string — the record
   *  dialog's `snapshotOf`, and here for a sharper reason than there: this is
   *  where the most typing is lost, because none of it is stored anywhere
   *  until Save.
   *
   *  THE ADDRESS, THE DATE OF BIRTH, THE EMERGENCY CONTACT, THE NUMBERS, THE
   *  CERTIFICATIONS AND THE DOCUMENTS ARE ALL IN IT. The guard only asks about
   *  what it can see: the old check compared `form` against `BLANK` and looked
   *  at one number box, so a full registration — an address, a birthday, an
   *  emergency contact, four numbers and three cards — went silently on Escape
   *  or a mis-click on the backdrop.
   *
   *  THE HALF-TYPED ROWS COUNT TOO. `number`, `cert` and `doc` are the add
   *  bars: a number typed and not yet added, or a scan picked and not yet
   *  added, is work somebody did, and losing it without being asked is the same
   *  failure in miniature.
   *
   *  A `File` DOES NOT SURVIVE `JSON.stringify`, which is why the documents are
   *  spelled out rather than passed in whole. It has no enumerable own
   *  properties, so every file serialises to `{}` — adding one would still be
   *  noticed (the array grows) but swapping the chosen scan for a different one
   *  would leave this string unchanged, and the guard would then say nothing
   *  about a file somebody had picked. Naming the four things that identify one
   *  is what makes the check honest.
   *
   *  SORTED BEFORE COMPARING, for the groups: ticks land in whatever order the
   *  boxes were touched, so an unsorted comparison would call a member dirty
   *  for having been unticked and re-ticked. */
  const snapshot = JSON.stringify({
    form, joined, address, personal, numbers, number, certs, cert,
    chosen: [...chosen].sort(),
    docs: docs.map((d) => (
      { name: d.file.name, size: d.file.size, kind: d.kind, note: d.note })),
    doc: {
      name: doc.file?.name ?? '', size: doc.file?.size ?? 0,
      kind: doc.kind, note: doc.note,
    },
  })
  const initial = useRef(snapshot)
  const dirty = snapshot !== initial.current

  // THE EXITS CLOSE WHILE THE WRITE IS RUNNING, and this is not politeness.
  //
  // `add()` is a create POST followed by ONE MORE round trip per chosen
  // certification, and the guard's sentence -- "nothing has been saved yet" --
  // becomes FALSE the instant the create returns. Every exit was live for that
  // whole window: the header X and the footer Cancel sit outside
  // `<fieldset disabled={busy}>`, the backdrop handlers are on the div, and
  // `useDialogGuard` listens for Escape on `window`.
  //
  // Worse, `add()` is detached: `onClose` only unmounts the dialog, so the
  // loop carried on and `onAdded` still fired. The operator was told nothing
  // had been saved, agreed to discard it, and was then shown a green banner
  // saying the member was registered.
  //
  // AND THE RETRY IS WHAT COSTS. A member entered by NAME with no unit number
  // is supported deliberately and has no unique key at all, so registering him
  // again silently creates a SECOND roster row for one volunteer -- two
  // broadcast deliveries, two sets of numbers to keep in step, and
  // `incident_responders` is ON DELETE RESTRICT so the duplicate cannot be
  // deleted once he has attended a call. A callsign at least answers 409.
  const { leave, backdrop } = useDialogGuard(
    () => {
      if (busy) return 'Still saving. Wait a moment before closing this.'
      return dirty ? 'Discard this member? Nothing has been saved yet.' : null
    },
    () => { if (!busy) onClose() },
  )

  // THE SAME MECHANISM THE RECORD DIALOG USES, and it was the one piece of
  // `sheet.tsx` this dialog never picked up. Without it, closing the
  // registration drops focus to the document body -- so somebody working
  // through a roster on a keyboard tabs in from the top of the page for every
  // single member.
  useReturnFocus()

  const identified = !!(form.display_name.trim() || form.callsign.trim())

  /** The strip. THE SAME FOUR THE RECORD DRAWS, in the same order, with the
   *  same labels — which is the whole of what "identical" means here, and it is
   *  the customer's word.
   *
   *  THERE IS A DOCUMENTS BLOCK NOW, AND THAT IS THE CHANGE. It used to be five
   *  tabs of six, because a scan is filed against a member id and there is none
   *  until the create returns. That reasoning was sound about the SERVER and
   *  wrong about the form: the file waits in the browser and goes up the moment
   *  there is an id, so the control is not refused, it is deferred. See
   *  `NewDocuments`.
   *
   *  ADDRESS AND EMERGENCY CONTACT ARE ALWAYS HERE, unlike the record, where
   *  they are drawn only for a role holding `settings.members`. The difference
   *  is what that check is FOR: there, their values arrive on the gated record
   *  read and an empty box for a role that may not read one says "nothing on
   *  file", which is a wrong fact rather than a refusal. Here there is nothing
   *  to read back — the boxes start empty for everybody, and only a role that
   *  may write is offered this dialog at all.
   *
   *  NO AMBER ON BROADCAST EITHER, and that is not an omission. The record's
   *  amber reports `readable`, which is `app/phones.py`'s verdict on a number
   *  it has already normalised; nothing has read these yet, and a browser
   *  deciding for itself would be the phone-book bug. It appears on his record
   *  the moment he is saved, which is why a registration that comes back
   *  holding one opens that record. */
  const tabs: TabSpec[] = [
    {
      key: 'personal',
      label: 'Personal information',
      // NOT ON AN UNTOUCHED FORM, which is the same rule the footer sentence
      // follows and for the same reason: a registration opens with both
      // identity boxes empty, so an unguarded alarm would be on the tab from
      // the moment the dialog opened and would sit there while somebody typed
      // an address. That is the standing prose the customer asked to be rid of,
      // wearing a badge. `dirty` is the moment it becomes a refusal rather
      // than an instruction.
      ...(dirty && !identified ? {
        badge: '!',
        alarm: true,
        badgeTitle: 'A member needs a unit number or a name.',
      } : {}),
    },
    { key: 'emergency', label: 'Emergency contact' },
    // NO BADGE OF ITS OWN, matching the record. The one thing worth flagging
    // on this tab is a number nothing can text, and that is the server's
    // verdict on a number it has normalised — see above. A plain count of
    // numbers typed would be a mark that differs between the two dialogs for
    // the same tab, which is how the two strips start to drift.
    { key: 'broadcast', label: 'Broadcast' },
    {
      // THE COUNT IS OF WHAT WILL BE FILED, which is the same thing the
      // record's counts once the member exists. The documents are only counted
      // when they can be chosen at all.
      key: 'documents',
      label: 'Certifications and documents',
      ...(certs.length + (mayFile ? docs.length : 0) ? {
        badge: String(certs.length + (mayFile ? docs.length : 0)),
        badgeTitle: `${certs.length} certification${certs.length === 1 ? '' : 's'}`
          + (mayFile
            ? `, ${docs.length} document${docs.length === 1 ? '' : 's'}`
            : ''),
      } : {}),
    },
  ]

  /** THE RECORD'S THREE SUB-TABS, WITH ADDRESS ALWAYS DRAWN — the one
   *  difference, and the one the record already has for the panel itself.
   *  There, the mailing address arrives on a gated read and an empty box would
   *  read as "nothing on file"; here there is nothing to read back, the boxes
   *  start empty for everybody, and only a role that may write is offered this
   *  dialog at all. */
  const subTabs: TabSpec<PersonalKey>[] = [
    {
      key: 'identity',
      label: 'Identity',
      // THE MAIN TAB'S BADGE, ON THE SUB-TAB THAT HOLDS THE BOXES, and under
      // the same `dirty` guard: on an untouched form it would be an alarm from
      // the moment the dialog opened, which is standing prose wearing a badge.
      ...(dirty && !identified ? {
        badge: '!',
        alarm: true,
        badgeTitle: 'A member needs a unit number or a name.',
      } : {}),
    },
    { key: 'address', label: 'Address' },
    { key: 'numbers', label: 'Numbers' },
  ]

  async function add(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)

    let made: Member
    try {
      made = await api.memberCreate({
        display_name: form.display_name.trim() || null,
        callsign: form.callsign.trim() || null,
        email: form.email.trim() || null,
        radio_imei: form.radio_imei.trim() || null,
        vehicle_plate: form.vehicle_plate.trim() || null,
        has_radio: form.has_radio,
        notify_email: form.notify_email,
        notify_card: form.notify_card,
        group_ids: chosen,
        joined_on: joined || null,
        // EVERY NUMBER HE ANSWERS ON, in one body. `MemberIn.phones` is a list
        // and `create_member` walks it through `members.add_number`, so this
        // is the same path the record's Add button takes.
        //
        // `notify: null` RATHER THAN `true`, and the difference is a whole
        // registration. The server reads "not stated" as "text it if it can be
        // read" and reads an explicit `true` on a number it cannot read as a
        // refusal — which, raised inside the create transaction, rolls back the
        // member as well. Unticked is sent as an explicit `false`, because that
        // one is a decision.
        phones: numbers.map((n) => ({
          phone: n.phone,
          label: n.label || null,
          notify: n.notify ? null : false,
          is_primary: n.primary,
        })),
        // ALL EIGHT PARTS AND ALL FOUR PERSONAL FIELDS, WHOLESALE, and that is
        // right HERE and wrong on the record. `update_member` reads its body
        // with `exclude_unset`, so an untouched field must not be sent at all;
        // `create_member` writes every column of a row that does not exist yet,
        // and a part left out is a part written NULL either way.
        ...addressBody(address),
        ...personalBody(personal),
      })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that member.')
      setBusy(false)
      return
    }

    // THE MEMBER EXISTS FROM HERE ON, AND NOTHING BELOW MAY READ AS THOUGH HE
    // DOES NOT. A certification and a scan each hang off a member id, so
    // neither could have gone in the body above; they are attached now, through
    // the real routes, one at a time — the certification route answers with his
    // whole list, so two in flight at once would be two answers about the same
    // member arriving in either order.
    //
    // A FAILURE HERE IS NOT A FAILED REGISTRATION. It is named, by name, and
    // the record opens on him so the card or the scan can be added where the
    // retry lives. Showing "Could not add that member" would be confidently
    // wrong about the one thing the operator needs to know, and would have him
    // register the same person twice.
    //
    // THE SERVER'S OWN SENTENCE IS CARRIED OUT WITH THE NAME, because these
    // refusals are the ones this codebase writes as sentences and they explain
    // themselves exactly once. "That certification would expire before it was
    // obtained. Check the two dates are the right way round." is the whole
    // answer; a bare `catch` reduced it to a name and an instruction to try
    // again below -- so the operator retypes the same two reversed dates into
    // the record and has to earn the refusal a second time, having meanwhile
    // lost the dates the form was holding. The documents need it just as much:
    // "That file is not one this can store" and "That exact file is already on
    // this member" are answered by different actions, and neither of them is
    // picking the same file again.
    //
    // And when the failure is not per-row at all -- the session gone, the
    // server down -- every card lands in this list, and "add them below" is
    // simply the wrong instruction. The message says which it was.
    const unattached: Unattached[] = []
    for (const c of certs) {
      try {
        await api.memberCertificationAdd(made.id, {
          certification_id: c.certification_id,
          obtained_on: c.obtained_on || null,
          expires_on: c.expires_on || null,
          note: c.note || null,
        })
      } catch (e) {
        unattached.push({
          what: 'certification',
          name: c.name,
          why: e instanceof ApiError ? e.message : 'The server could not be reached.',
        })
      }
    }

    // THE FILES HELD IN THE BROWSER, NOW THAT THERE IS SOMETHING TO FILE THEM
    // AGAINST. This is the whole reason a `File` waits in component state
    // rather than being staged anywhere on the server — see `NewDocuments`.
    // One at a time, like the cards above: the upload is encrypted and hashed
    // on the server and a member's scans are a handful, so nothing here is
    // worth racing.
    for (const d of docs) {
      try {
        await api.memberDocumentUpload(made.id, d.file, d.kind, d.note || null)
      } catch (e) {
        unattached.push({
          what: 'document',
          name: d.file.name,
          why: e instanceof ApiError ? e.message : 'The server could not be reached.',
        })
      }
    }

    onAdded(made, unattached)
  }

  return (
    <div className="modalback" {...backdrop}>
      {/* `memrec`, like the record — the same four tab labels need the same
          width, and a registration that is narrower than the record it becomes
          is the two dialogs disagreeing about something the customer asked to
          be identical. */}
      <form
        className="modal wide sheet-rec memrec"
        role="dialog"
        aria-modal="true"
        aria-labelledby="addmem"
        onSubmit={add}
      >
        <header className="sheet-rechead">
          <h3 id="addmem">Register a member</h3>
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>

        {/* BETWEEN THE HEAD AND THE BODY, so it never scrolls away, and
            OUTSIDE the fieldset below so `busy` cannot make it unclickable.
            The same placement the record uses — one mechanism, one shape. */}
        <TabStrip
          idPrefix="add" label="Sections"
          tabs={tabs} active={tab} onPick={setTab} />

        <div className="sheet-recbody" ref={body}>
          {/* THE RECORD'S TABS, ALL OF THEM, AND ITS SUB-TABS TOO. Somebody who
              registers a member and then opens him finds the same four tabs
              with the same names in the same order, the same three sub-tabs
              behind the first, and the same blocks behind those — which is what
              the customer asked for in the word "identical", and the reason his
              second fault ("we should not need to scroll down") is fixed on
              both dialogs rather than on the one he happened to be looking at.

              THE SUB-STRIP IS OUTSIDE THE FIELDSET, the record's rule: a
              `<fieldset disabled>` disables every button under it, so a strip
              inside one would go dead for the length of the create — which on
              this form is the moment somebody most wants to check what they
              typed on another sub-tab. */}
          <TabPanel idPrefix="add" tab="personal" active={tab}>
            <TabStrip
              tone="sub"
              idPrefix="add-personal"
              label="Personal information"
              tabs={subTabs}
              active={sub}
              onPick={setSub}
            />

            <fieldset className="bare" disabled={busy}>
              <TabPanel tone="sub" idPrefix="add-personal" tab="identity" active={sub}>
                <IdentityFields
                  value={form}
                  onChange={setForm}
                  idPrefix="add"
                  joined={joined}
                  onJoined={setJoined}
                  birth={personal.date_of_birth}
                  onBirth={(v) => setPersonal({ ...personal, date_of_birth: v })}
                />

                {/* TWO BLOCKS AND NOT ONE ROW OF TICKS — the record's rule, and
                    the same two components, so the two dialogs cannot come to
                    disagree about what a category is. On Identity for the
                    record's reason: they are what he IS, not a fourth section
                    of their own. */}
                {/* SIDE BY SIDE, WHICH SAYS "TWO SEPARATE THINGS" BETTER
                    THAN STACKING THEM DID. The customer's correction was
                    exactly that -- "It's two separate things, categories and
                    areas. I think you're looking at it as it's one big thing"
                    -- and two columns make the split structural rather than a
                    matter of reading two headings in a row. It also costs one
                    row instead of two, which is what lets Identity fit the
                    fixed box without scrolling. */}
                <div className="sidebyside">
                  <div>
                    <CategoryChoice
                      groups={groups} chosen={chosen} onChange={setChosen}
                      idPrefix="add" />
                  </div>
                  <div>
                    <AreaTicks
                      groups={groups} chosen={chosen} onChange={setChosen}
                      idPrefix="add" />
                  </div>
                </div>
              </TabPanel>

              {/* NOT WRAPPED IN A PERMISSION CHECK, unlike the record's copy of
                  this block and the emergency contact, and the difference is
                  what the check is FOR. There, they are drawn only for a role
                  holding `settings.members` because their values arrive on the
                  gated record read — an empty box for a role that may not read
                  one says "nothing on file", which is a wrong fact rather than
                  a refusal. Here there is nothing to read back: the boxes start
                  empty for everybody, and only a role that may write is offered
                  this dialog at all (the toolbar's + Add member is behind the
                  same key). So this sub-tab is always drawn, where the record's
                  is not.

                  IT IS NOT A `places` ROW AND IS NEVER GEOCODED — migration 044
                  has the whole argument, and `AddressFields` is reused rather
                  than copied so a member's address and a caller's cannot come to
                  disagree about what a street is.

                  THE HEADING SURVIVES THE SUB-TAB, as it does on the record:
                  "Mailing address" is the only thing on screen that says these
                  eight columns are where a letter goes rather than where
                  anybody is sent. */}
              <TabPanel tone="sub" idPrefix="add-personal" tab="address" active={sub}>
                <h4 className="sheet-recsub">Mailing address</h4>
                <AddressFields showCountry value={address} onChange={setAddress} />
              </TabPanel>

              <TabPanel tone="sub" idPrefix="add-personal" tab="numbers" active={sub}>
                <NewNumbers
                  rows={numbers}
                  onRows={setNumbers}
                  draft={number}
                  onDraft={setNumber}
                />
              </TabPanel>
            </fieldset>
          </TabPanel>

          <fieldset className="bare" disabled={busy}>
            {/* WHO TO RING IF SOMETHING HAPPENS TO HIM. Its own tab rather
                than three more boxes on the identity form, because it is about
                somebody else: she is not a member of this organization and
                never agreed to be in its systems.

                NOTHING HERE IS A CHANNEL AND NOTHING MAY SUGGEST IT IS. There
                is no text tick beside this number and there is not going to be
                one — an emergency contact in `member_phones` would be texted
                the nature, location and priority of every call the organization
                takes. `_reachable` on the server cannot be satisfied by
                anything in this section either, so a member whose only contact
                is his mother is refused as unreachable. Which is also why this
                is not part of Broadcast.

                AND THERE IS NO "could not be read" NOTICE HERE, unlike the
                record's copy. That notice reports
                `emergency_contact_readable`, which is the server's verdict on
                a number it has already normalised — nothing has read this one
                yet, and a browser deciding for itself would be the phone-book
                bug. It shows on his record the moment he is saved.

                NO HEADING OF ITS OWN: the tab is the heading, exactly as on the
                record. */}
            <TabPanel idPrefix="add" tab="emergency" active={tab}>
              <div className="grid2">
                <div className="field">
                  <label htmlFor="add-ecname">Name</label>
                  <input
                    id="add-ecname"
                    value={personal.emergency_contact_name}
                    maxLength={200}
                    onChange={(e) => setPersonal(
                      { ...personal, emergency_contact_name: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="add-ecrel">Relationship</label>
                  <input
                    id="add-ecrel"
                    value={personal.emergency_contact_relationship}
                    maxLength={80}
                    placeholder="Wife, son, neighbour"
                    onChange={(e) => setPersonal({
                      ...personal,
                      emergency_contact_relationship: e.target.value,
                    })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="add-ecphone">Number</label>
                  {/* BOUNDED AT THE SERVER'S OWN 40, like the member's own
                      numbers: a number is a number whoever it belongs to. */}
                  <PhoneInput
                    id="add-ecphone"
                    value={personal.emergency_contact_phone}
                    maxLength={40}
                    onChange={(e) => setPersonal(
                      { ...personal, emergency_contact_phone: e.target.value })}
                  />
                </div>
              </div>
            </TabPanel>

            {/* BROADCAST — the ticks, the email address, and which of the
                numbers typed on Personal information take the text. */}
            <TabPanel idPrefix="add" tab="broadcast" active={tab}>
              <ChannelFields value={form} onChange={setForm} idPrefix="add" />
              <NewTextTicks rows={numbers} onRows={setNumbers} />
            </TabPanel>

            {/* ONE TAB FOR THE TWO, as on the record. Both are attached AFTER
                the create, through their own routes, because both hang off a
                member id — so this panel is the one place on the form where
                what is typed does not go into the create body. */}
            <TabPanel idPrefix="add" tab="documents" active={tab}>
              <NewCertifications
                rows={certs}
                onRows={setCerts}
                draft={cert}
                onDraft={setCert}
              />

              {/* THE BLOCK THAT WAS MISSING, and the customer's own words for
                  what was wrong: "When I want to register a member, it's
                  missing the section of uploading files."

                  BEHIND ITS OWN KEY, like the record's. A role that may build a
                  roster and may not open a passport scan is a real role — the
                  two are separate leaves on purpose — and a file picker it
                  cannot use would be a control drawn and then refused after the
                  member had already been created. */}
              {mayFile && (
                <NewDocuments
                  rows={docs}
                  onRows={setDocs}
                  draft={doc}
                  onDraft={setDoc}
                />
              )}
            </TabPanel>
          </fieldset>
        </div>

        {/* THE REFUSAL SITS WITH THE BUTTON THAT CAUSED IT, not at the top of
            the body. `.sheet-recbody` is the only part that scrolls and
            `.modalrow` is pinned outside it — and this form now carries the
            identity boxes, eight address boxes, a numbers list, the category
            and the areas, the channels, a certifications list, the documents
            and three boxes for the emergency contact, so it is taller than the
            dialog on any screen. Somebody
            pressing Add is at the BOTTOM of that scroll, and every refusal
            rendered above the fold with nothing to scroll it into view.

            What that hid is the likeliest refusal this customer will meet:
            `BLANK` has `has_radio: false`, so registering a unit-only row —
            fifteen of his twenty-four look like that — is refused by
            `_reachable`, and all he saw was the button stop spinning. So did
            "Callsign MT-9 is already in use", which is the ONE sentence that
            says his previous attempt actually created the member. A button
            that un-spins with nothing on screen reads as "nothing happened",
            and the next move is to press it again. */}
        <div className="modalrow">
          {error && (
            <div className="notice" role="alert" style={{ margin: 0 }}>{error}</div>
          )}
          <button className="ghost" type="button" onClick={leave}>Cancel</button>
          {/* A DISABLED BUTTON CANNOT DELIVER THE SERVER'S REFUSAL: `identified`
              stops the request being sent at all, so "A member needs a unit
              number or a name" never arrives. Said here instead, the way the
              record's Save row says it.

              NOT ON AN UNTOUCHED FORM, which is the difference between this and
              the record. There, a record always opens identified, so the
              sentence can only appear once somebody has cleared both boxes. A
              registration form opens with both empty — so, unguarded, this
              would be on screen from the moment the dialog opened and would sit
              there while somebody typed an address, which is the standing prose
              the customer asked to be rid of. `dirty` is the moment it starts
              being a refusal rather than an instruction.

              AND IT NAMES ITS TAB, like the record's does. Somebody who has
              got as far as pressing Add has usually walked away from the tab
              those two boxes are on, and a sentence about a field that is not
              on screen is an instruction with a hunt attached. */}
          {dirty && !identified && (
            <span className="footnote warn" style={{ margin: 0 }}>
              Give him a unit number or a name, on{' '}
              <button
                className="linky"
                type="button"
                // FOCUS GOES WITH IT. Switching the tab and leaving focus on a
                // link in the FOOTER -- after the whole panel in DOM order --
                // means a keyboard user has to Shift+Tab backwards through
                // Cancel and every field to reach the box the sentence is
                // about, and a screen reader is told nothing at all, so the
                // control reads as broken. The sentence exists to end a hunt;
                // this is what ends it.
                //
                // ONTO THE SUB-TAB, like the record's: Personal information is
                // three sub-tabs and the two boxes are on Identity, so landing
                // on the panel alone would leave the hunt one level down.
                onClick={() => jump('personal', 'identity')}>
                Personal information → Identity
              </button>.
            </span>
          )}
          <button className="primary compact" disabled={busy || !identified}>
            {busy ? 'Saving…' : 'Add to the roster'}
          </button>
        </div>
      </form>
    </div>
  )
}
